import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useFacebookPixel } from '../../hooks/useFacebookPixel';
import { LazyImage } from '../../components/base/LazyImage';
import { useToast } from '../../hooks/useToast';

// 🔥 新增：格式化停留時間的函數
const formatDwellTime = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds} 秒`;
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (remainingSeconds === 0) {
      return `${minutes} 分鐘`;
    }
    return `${minutes} 分 ${remainingSeconds} 秒`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (minutes === 0) {
      return `${hours} 小時`;
    }
    return `${hours} 小時 ${minutes} 分鐘`;
  }
};

// 🔥 新增：定義 VIP 方案數據
const vipPlans = [
  {
    icon: '🎁',
    name: '入門方案',
    description: '適合小額玩家',
    deposit: '3,000',
    credit: '5,000',
    settlement: '週一結算',
    withdrawal: '贏 1.5 萬可提前結算',
    support: '專屬客服',
    positioning: '新手 / 小額體驗用',
    popular: false,
    isTrial: false
  },
  {
    icon: '⭐',
    name: 'VIP進階方案',
    description: '最受歡迎｜最多人選擇',
    deposit: '5,000',
    credit: '1 萬',
    settlement: '週一結算',
    withdrawal: '贏 3 萬可提前結算',
    support: '專屬客服',
    positioning: '穩定玩家、回訪率高',
    popular: true,
    isTrial: false
  },
  {
    icon: '💎',
    name: '高端方案',
    description: '適合大額玩家',
    deposit: '3.5 萬',
    credit: '5 萬',
    settlement: '週一結算',
    withdrawal: '贏 15 萬可提前結算',
    support: 'VIP 專屬客服',
    positioning: '高投入、高流水玩家',
    popular: false,
    isTrial: false
  },
  {
    icon: '👑',
    name: '頂級方案',
    description: '適合高額玩家',
    deposit: '17.5 萬＋',
    credit: '25 萬＋',
    settlement: '週一結算',
    withdrawal: '贏 75 萬＋可提前結算',
    support: '24 小時專屬服務',
    positioning: '頂級 VIP、高資金客戶',
    popular: false,
    isTrial: false
  }
];

// 🔥 新增：成功案例數據（擴充到 8 個）
const successCases = [
  {
    name: '台北 陳總',
    plan: '頂級會員 3 年',
    testimonial: '玩了 5 年，火星人是我見過最穩的平台，大額出金從不拖延，真的很放心',
    icon: 'ri-user-star-fill',
    date: '2024-01-15',
    amount: '150 萬',
    rating: 5,
    title: '企業主',
    level: '頂級會員',
    duration: '3 年',
    badge: '👑',
    achievements: ['累計提領 500 萬+', '零糾紛記錄', '推薦 5 位好友']
  },
  {
    name: '高雄 林老闆',
    plan: '高端會員 2 年',
    testimonial: '審核嚴格但很安全，值得信任，客服都是真人，不是機器人，專業又貼心',
    icon: 'ri-user-heart-fill',
    date: '2024-01-10',
    amount: '80 萬',
    rating: 5,
    title: '貿易商',
    level: '高端會員',
    duration: '2 年',
    badge: '💎',
    achievements: ['累計提領 200 萬+', '信用優良', '長期會員']
  },
  {
    name: '台中 王董',
    plan: '進階會員 1 年',
    testimonial: '出金速度真的快，上午申請下午就到帳，比其他平台快太多了',
    icon: 'ri-user-smile-fill',
    date: '2024-01-05',
    amount: '35 萬',
    rating: 5,
    title: '投資人',
    level: '進階會員',
    duration: '1 年',
    badge: '⭐',
    achievements: ['快速出金', '穩定遊戲', '口碑推薦']
  },
  {
    name: '新竹 張總',
    plan: '頂級會員 2 年',
    testimonial: '專屬管家服務真的很貼心，有問題隨時都能聯繫到人，不像其他平台都是機器人',
    icon: 'ri-user-star-fill',
    date: '2024-01-03',
    amount: '200 萬',
    rating: 5,
    title: '科技業',
    level: '頂級會員',
    duration: '2 年',
    badge: '👑',
    achievements: ['VIP 專屬服務', '累計提領 600 萬+', '零延遲記錄']
  },
  {
    name: '台南 劉老闆',
    plan: '高端會員 1.5 年',
    testimonial: '信用額度制度很彈性，不會強迫推銷，一切以客戶需求為主，這點很難得',
    icon: 'ri-user-heart-fill',
    date: '2023-12-28',
    amount: '120 萬',
    rating: 5,
    title: '餐飲業',
    level: '高端會員',
    duration: '1.5 年',
    badge: '💎',
    achievements: ['彈性額度', '累計提領 300 萬+', '優質會員']
  },
  {
    name: '桃園 吳董',
    plan: '進階會員 2 年',
    testimonial: '隱私保護做得很好，資料看完就刪，不像其他平台還要留存備份，讓人很安心',
    icon: 'ri-user-smile-fill',
    date: '2023-12-20',
    amount: '50 萬',
    rating: 5,
    title: '建築業',
    level: '進階會員',
    duration: '2 年',
    badge: '⭐',
    achievements: ['隱私保護', '長期信任', '穩定會員']
  },
  {
    name: '基隆 黃總',
    plan: '頂級會員 1 年',
    testimonial: '大額面交很專業，從不拖延，說好的時間一定準時，這種信用在圈內很少見',
    icon: 'ri-user-star-fill',
    date: '2023-12-15',
    amount: '180 萬',
    rating: 5,
    title: '物流業',
    level: '頂級會員',
    duration: '1 年',
    badge: '👑',
    achievements: ['大額面交', '準時到帳', '信用第一']
  },
  {
    name: '宜蘭 周老闆',
    plan: '高端會員 1 年',
    testimonial: '審核制度雖然嚴格，但進來後就知道值得，玩家素質都很高，環境很好',
    icon: 'ri-user-heart-fill',
    date: '2023-12-10',
    amount: '90 萬',
    rating: 5,
    title: '製造業',
    level: '高端會員',
    duration: '1 年',
    badge: '💎',
    achievements: ['嚴格審核', '優質環境', '滿意推薦']
  }
];

// 🔥 新增：即時動態數據
const liveActivities = [
  {
    user: '台北 陳總',
    action: '提領 15 萬',
    time: '3 分鐘前',
    status: '已完成',
    icon: 'ri-money-dollar-circle-fill',
    color: 'from-green-500 to-green-600'
  },
  {
    user: '高雄 林老闆',
    action: '申請審核通過',
    time: '8 分鐘前',
    status: '已完成',
    icon: 'ri-checkbox-circle-fill',
    color: 'from-blue-500 to-blue-600'
  },
  {
    user: '台中 王董',
    action: '提領 8 萬',
    time: '12 分鐘前',
    status: '處理中',
    icon: 'ri-time-fill',
    color: 'from-[#FFD700] to-[#D4AF37]'
  },
  {
    user: '新竹 張總',
    action: '提交申請',
    time: '15 分鐘前',
    status: '審核中',
    icon: 'ri-file-list-fill',
    color: 'from-purple-500 to-purple-600'
  },
  {
    user: '台南 劉老闆',
    action: '提領 20 萬',
    time: '20 分鐘前',
    status: '已完成',
    icon: 'ri-money-dollar-circle-fill',
    color: 'from-green-500 to-green-600'
  }
];

// 🔥 新增：FAQ 數據
const faqs = [
  {
    question: '什麼是 VIP 信用制度？',
    answer: [
      '• 押金 5,000 → 可用額度 1 萬',
      '• 不用每把立刻結算',
      '• 照自己節奏玩',
      '• 贏超過額度 3 倍可提前結算',
      '• 讓你玩得更有彈性'
    ]
  },
  {
    question: '審核需要多久？',
    answer: [
      '• 正常 24 小時內完成',
      '• 通過後專屬客服聯絡',
      '• 一次說清楚後續流程',
      '• 審核仔細是為了環境安心'
    ]
  },
  {
    question: '出金速度快嗎？',
    answer: [
      '• 10 萬以下：約 30 分鐘到帳',
      '• 都是當天處理',
      '• 大額：當天完成為原則',
      '• 必要時面交或分批',
      '• 該到的時間一定會到'
    ]
  },
  {
    question: '我的資料會被保留嗎？',
    answer: [
      '• 不會保留',
      '• 只用於一次性審核',
      '• 確認後即刪除',
      '• 不留、不備份',
      '• 隱私保護是基本'
    ]
  },
  {
    question: 'KYC 資料會不會有風險？',
    answer: [
      '• 不透過 LINE@',
      '• 由專屬客服私人 LINE 確認',
      '• 只有一位客服看到',
      '• 不進入系統、不被多人存取',
      '• 驗證後即時錄影證明並刪除',
      '• 不留存、不備份、不轉交第三方',
      '• 確認完就刪，不留任何資料'
    ]
  },
  {
    question: '方案要怎麼選比較適合？',
    answer: [
      '• 看一週大概玩的金額',
      '• 輸贏 2 萬左右 → 押金 1 萬方案',
      '• 多數選：押金 5 萬、額度 10 萬',
      '• 可請管家慢慢開額度',
      '• 用多少算多少',
      '• 沒用到的結算時退還'
    ]
  },
  {
    question: '之後可以調整方案嗎？',
    answer: [
      '• 可以調整',
      '• 需求改變很正常',
      '• 跟專屬客服說一聲即可',
      '• 調整到比較舒服的方案',
      '• 這邊走的是長期'
    ]
  },
  {
    question: '市場上平台那麼多，為什麼要選火星人？',
    answer: [
      '• 專門做老虎機與電子遊戲',
      '• 版面分類整理清楚',
      '• 重視穩定度與長期表現',
      '• 玩家結構穩定、環境有管理',
      '• 容易找到適合節奏的機台',
      '• 玩起來比較順'
    ]
  },
  {
    question: '如果輸很多，結算會不會有問題？',
    answer: [
      '• 不會有問題',
      '• 照事前說明的制度結算',
      '• 不會臨時追款',
      '• 不會突然改規則',
      '• 重視信用與長期'
    ]
  },
  {
    question: '押金放在這邊安全嗎？',
    answer: [
      '• 只作為額度與結算保障',
      '• 不會挪作他用',
      '• 沒用到的金額會退還',
      '• 流程清楚'
    ]
  },
  {
    question: '有沒有額度上限？會不會被卡？',
    answer: [
      '• 額度不是一次定死',
      '• 依使用狀況與信用調整',
      '• 使用穩定、結算正常',
      '• 額度自然能往上調',
      '• 不會突然被卡'
    ]
  },
  {
    question: '大額出金真的能當天完成嗎？',
    answer: [
      '• 是的，當天處理為原則',
      '• 有些情況採面交或分批',
      '• 只是為了安全',
      '• 不是拖時間'
    ]
  },
  {
    question: '會不會被推銷或一直被打擾？',
    answer: [
      '• 不會',
      '• 專屬客服制',
      '• 有需要你找我們',
      '• 沒需要不會主動打擾'
    ]
  },
  {
    question: '會不會接到其他平台的電話？',
    answer: [
      '• 不會',
      '• 資料只用於審核',
      '• 不轉、不賣、不外流'
    ]
  },
  {
    question: '我怕信用制度會讓我控制不了額度，怎麼辦？',
    answer: [
      '• 這個擔心很正常',
      '• 可以一開始設定慢慢開額度',
      '• 照自己的節奏來',
      '• 覺得差不多了可請管家卡住',
      '• 不再往上調',
      '• 有人一起幫你顧',
      '• 讓你玩得安心'
    ]
  },
  {
    question: '火星人會比較好贏嗎？',
    answer: [
      '• 不保證、不會說一定比較好贏',
      '• 老虎機都有固定機制',
      '• 在任何平台都一樣',
      '• 人多、跑得勤的平台',
      '• 整體節奏通常比較活',
      '• 玩家數量集中',
      '• 容易遇到狀況不錯的機台',
      '• 至於中不中，還是看當下'
    ]
  }
];

// 🔥 新增：本週贏家榜數據 - 4 組輪換數據
const weeklyWinnersData = [
  // 第 1 週數據
  [
    { rank: 1, name: '台北 陳總', amount: 1800000, game: '戰神賽特2', badge: '👑' },
    { rank: 2, name: '高雄 林老闆', amount: 1200000, game: '雷神之錘', badge: '💎' },
    { rank: 3, name: '台中 王董', amount: 800000, game: '奢華', badge: '⭐' },
    { rank: 4, name: '新竹 張總', amount: 650000, game: '塔樓衝刺', badge: '🎯' },
    { rank: 5, name: '台南 劉老闆', amount: 520000, game: '推筒子', badge: '🎰' },
    { rank: 6, name: '桃園 吳董', amount: 450000, game: '急速糖果', badge: '🍬' },
    { rank: 7, name: '基隆 黃總', amount: 380000, game: '急速天九', badge: '⚡' },
    { rank: 8, name: '宜蘭 周老闆', amount: 320000, game: '仙境傳說', badge: '🎮' },
    { rank: 9, name: '花蓮 鄭總', amount: 280000, game: '小雞快跑', badge: '🐔' },
    { rank: 10, name: '台東 許董', amount: 250000, game: '戰神賽特2', badge: '🎪' }
  ],
  // 第 2 週數據
  [
    { rank: 1, name: '新竹 李總', amount: 2100000, game: '奢華', badge: '👑' },
    { rank: 2, name: '台南 黃董', amount: 1500000, game: '戰神賽特2', badge: '💎' },
    { rank: 3, name: '高雄 陳老闆', amount: 950000, game: '雷神之錘', badge: '⭐' },
    { rank: 4, name: '台北 王總', amount: 720000, game: '推筒子', badge: '🎯' },
    { rank: 5, name: '桃園 張董', amount: 580000, game: '塔樓衝刺', badge: '🎰' },
    { rank: 6, name: '台中 劉總', amount: 490000, game: '急速天九', badge: '🍬' },
    { rank: 7, name: '新北 吳老闆', amount: 420000, game: '急速糖果', badge: '⚡' },
    { rank: 8, name: '彰化 林董', amount: 360000, game: '仙境傳說', badge: '🎮' },
    { rank: 9, name: '嘉義 周總', amount: 310000, game: '小雞快跑', badge: '🐔' },
    { rank: 10, name: '屏東 鄭老闆', amount: 270000, game: '戰神賽特2', badge: '🎪' }
  ],
  // 第 3 週數據
  [
    { rank: 1, name: '台中 張總', amount: 1950000, game: '雷神之錘', badge: '👑' },
    { rank: 2, name: '台北 劉董', amount: 1350000, game: '奢華', badge: '💎' },
    { rank: 3, name: '新竹 吳老闆', amount: 880000, game: '戰神賽特2', badge: '⭐' },
    { rank: 4, name: '高雄 陳總', amount: 690000, game: '塔樓衝刺', badge: '🎯' },
    { rank: 5, name: '台南 王董', amount: 550000, game: '急速糖果', badge: '🎰' },
    { rank: 6, name: '桃園 林總', amount: 470000, game: '推筒子', badge: '🍬' },
    { rank: 7, name: '新北 黃老闆', amount: 400000, game: '急速天九', badge: '⚡' },
    { rank: 8, name: '基隆 周董', amount: 340000, game: '仙境傳說', badge: '🎮' },
    { rank: 9, name: '宜蘭 許總', amount: 295000, game: '小雞快跑', badge: '🐔' },
    { rank: 10, name: '花蓮 鄭老闆', amount: 260000, game: '戰神賽特2', badge: '🎪' }
  ],
  // 第 4 週數據
  [
    { rank: 1, name: '高雄 王總', amount: 2250000, game: '戰神賽特2', badge: '👑' },
    { rank: 2, name: '台北 林董', amount: 1450000, game: '雷神之錘', badge: '💎' },
    { rank: 3, name: '台南 陳老闆', amount: 920000, game: '奢華', badge: '⭐' },
    { rank: 4, name: '新竹 張總', amount: 710000, game: '急速天九', badge: '🎯' },
    { rank: 5, name: '台中 劉董', amount: 560000, game: '塔樓衝刺', badge: '🎰' },
    { rank: 6, name: '桃園 吳總', amount: 480000, game: '推筒子', badge: '🍬' },
    { rank: 7, name: '新北 黃老闆', amount: 410000, game: '急速糖果', badge: '⚡' },
    { rank: 8, name: '彰化 周董', amount: 350000, game: '仙境傳說', badge: '🎮' },
    { rank: 9, name: '嘉義 許總', amount: 305000, game: '小雞快跑', badge: '🐔' },
    { rank: 10, name: '屏東 鄭老闆', amount: 275000, game: '戰神賽特2', badge: '🎪' }
  ]
];

// 🔥 新增：根據當前週數獲取對應的贏家數據
const getCurrentWeekWinners = () => {
  // 獲取當前日期
  const now = new Date();
  // 計算今年第幾週（ISO 8601 標準）
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const days = Math.floor((now.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
  const weekNumber = Math.ceil((days + startOfYear.getDay() + 1) / 7);
  
  // 根據週數取模，循環使用 4 組數據
  const dataIndex = (weekNumber - 1) % 4;
  
  return weeklyWinnersData[dataIndex];
};

export default function HomePage() {
  const [isLoading, setIsLoading] = useState(true);
  const [settings, setSettings] = useState<any>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formErrors, setFormErrors] = useState<any>({});
  const [showFloatingCTA, setShowFloatingCTA] = useState(false);
  const [hasScrolledToForm, setHasScrolledToForm] = useState(false);
  const [hasInteractedWithForm, setHasInteractedWithForm] = useState(false);
  // 🔥 新增：toggleFAQ 狀態（移到組件內部）
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    plan: 'advanced', // 預設為 VIP進階方案（5,000 押金）
    phone: '',
    contactTime: '任何時間都可以' // 預設選擇任何時間
  });

  // 🆕 新增：提交狀態管理
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [savedApplication, setSavedApplication] = useState<any>(null);
  const [showLotteryModal, setShowLotteryModal] = useState(false);
  const [lotteryAmount, setLotteryAmount] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);

  // 🔥 新增：紅包金額狀態
  const [redEnvelopeAmount, setRedEnvelopeAmount] = useState<number | null>(null);

  // 🔥 新增：鍵盤彈出狀態
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  // 🔥 新增：applicationFormRef 初始化
  const applicationFormRef = useRef<HTMLDivElement>(null);

  // 🔥 使用 Facebook Pixel Hook
  const { pixelLoaded, trackPageView, trackViewContent, trackInitiateCheckout, trackLead } = useFacebookPixel();

  // 🔥 新增：调试状态
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [debugStats, setDebugStats] = useState({
    eventCount: 0,
    lastEvent: '',
    lastEventTime: ''
  });

  // 🔥 動態審核數據狀態
  const [dynamicAuditData, setDynamicAuditData] = useState({
    applicants: 100,
    approved: 18,
    passRate: 18
  });

  // 🔥 真實申請數據狀態
  const [realApplicationData, setRealApplicationData] = useState({
    monthlyTotal: 0,
    todayTotal: 0
  });

  // 🆕 即時動態數據
  const [realtimeActivities, setRealtimeActivities] = useState<Array<{
    id: string;
    text: string;
    time: string;
    type: 'apply' | 'approve' | 'withdraw';
  }>>([]);

  // 🆕 當前顯示的即時動態索引
  const [currentActivityIndex, setCurrentActivityIndex] = useState(0);

  // 🔥 新增：Hero 數據卡片輪播狀態
  const [currentDataCard, setCurrentDataCard] = useState(0);

  // 🔥 新增：FAQ 顯示數量狀態
  const [showAllFAQs, setShowAllFAQs] = useState(false);

  // 🔥 新增：toggleFAQ 函數
  const toggleFAQ = (index: number) => {
    const isExpanding = openFAQ !== index;
    setOpenFAQ(openFAQ === index ? null : index);
    
    // 🔥 追蹤 FAQ 展開/收起事件 - 加入去重
    if (pixelLoaded && isExpanding) {
      // 🔥 新增：檢查是否已經追蹤過這個 FAQ
      const contentKey = `faq_${index}`;
      if (trackedViewContent.has(contentKey)) {
        console.log('⏭️ 跳過重複追蹤: FAQ', index);
        return;
      }

      console.log('🎯 FAQ 展開事件被觸發:', faqs[index].question);
      trackViewContent(
        `FAQ - ${faqs[index].question}`,
        'faq',
        {
          action: 'expand',
          faq_index: index,
          faq_question: faqs[index].question
        }
      );
      
      // 標記為已追蹤
      setTrackedViewContent(prev => new Set(prev).add(contentKey));
    }
  };

  // 🆕 追蹤滾動到特定區域
  const trackScrollToSection = (sectionName: string) => {
    const contentKey = `scroll_${sectionName}`;
    if (trackedViewContent.has(contentKey)) {
      console.log('⏭️ 跳過重複追蹤: 滾動到', sectionName);
      return;
    }
    
    console.log('🎯 滾動到區域事件被觸發:', sectionName);
    trackViewContent(`區域 - ${sectionName}`, 'scroll', {
      section: sectionName,
      action: 'scroll_into_view'
    });
    trackedViewContent.add(contentKey);
  };

  // 🆕 追蹤 FAQ 展開
  const trackFAQExpand = (index: number, question: string) => {
    const contentKey = `faq_${index}`;
    if (trackedViewContent.has(contentKey)) {
      console.log('⏭️ 跳過重複追蹤: FAQ', index);
      return;
    }
    
    console.log('🎯 FAQ 展開事件被觸發:', question);
    trackViewContent(`FAQ - 問題${index + 1}`, 'faq', {
      question,
      index,
      action: 'expand'
    });
    trackedViewContent.add(contentKey);
  };

  // 🆕 追蹤成功案例點擊
  const trackCaseClick = (caseName: string, index: number) => {
    const contentKey = `case_${caseName}`;
    if (trackedViewContent.has(contentKey)) {
      console.log('⏭️ 跳過重複追蹤:', caseName);
      return;
    }
    
    console.log('🎯 成功案例點擊事件被觸發:', caseName);
    trackViewContent(`案例 - ${caseName}`, 'case_study', {
      case_name: caseName,
      index,
      action: 'click'
    });
    trackedViewContent.add(contentKey);
  };

  // 🆕 追蹤電話號碼點擊
  const trackPhoneClick = () => {
    const contentKey = 'phone_click';
    if (trackedViewContent.has(contentKey)) {
      console.log('⏭️ 跳過重複追蹤: 電話號碼點擊');
      return;
    }
    
    console.log('🎯 電話號碼點擊事件被觸發');
    trackViewContent('聯絡 - 電話', 'contact', {
      contact_type: 'phone',
      action: 'click'
    });
    trackedViewContent.add(contentKey);
  };

  // 🆕 追蹤 LINE 客服點擊
  const trackLineClick = () => {
    const contentKey = 'line_click';
    if (trackedViewContent.has(contentKey)) {
      console.log('⏭️ 跳過重複追蹤: LINE 客服點擊');
      return;
    }
    
    console.log('🎯 LINE 客服點擊事件被觸發');
    trackViewContent('聯絡 - LINE客服', 'contact', {
      contact_type: 'line',
      action: 'click'
    });
    trackedViewContent.add(contentKey);
  };

  // 🆕 追蹤方案選擇
  const trackPlanSelection = (planValue: string, planName: string) => {
    const contentKey = `plan_${planValue}`;
    if (trackedViewContent.has(contentKey)) {
      console.log('⏭️ 跳過重複追蹤: 方案', planName);
      scrollToApplyWithPlan(planValue);
      return;
    }
    
    console.log('🎯 方案選擇事件被觸發:', planName);
    trackViewContent(`方案 - ${planName}`, 'plan', {
      plan_value: planValue,
      plan_name: planName,
      action: 'select'
    });
    trackedViewContent.add(contentKey);
    scrollToApplyWithPlan(planValue);
  };

  // 🆕 追蹤 3 秒測試選擇
  const track3SecTestSelection = (frequency: string, recommendedPlan: string) => {
    const contentKey = `test_${frequency}`;
    if (trackedViewContent.has(contentKey)) {
      console.log('⏭️ 跳過重複追蹤: 3秒測試', frequency);
      return;
    }
    
    console.log('🎯 3 秒測試選擇事件被觸發:', frequency);
    trackViewContent(`測試 - 3秒測試 - ${frequency}`, 'interactive_test', {
      frequency,
      recommended_plan: recommendedPlan,
      action: 'select'
    });
    trackedViewContent.add(contentKey);
  };

  // 🆕 追蹤離開意圖彈窗顯示
  const trackExitIntentShow = () => {
    const contentKey = 'exit_intent_show';
    if (trackedViewContent.has(contentKey)) {
      console.log('⏭️ 跳過重複追蹤: 離開意圖彈窗顯示');
      return;
    }
    
    console.log('🎯 離開意圖彈窗顯示事件被觸發');
    trackViewContent('彈窗 - 離開意圖挽留', 'modal', {
      action: 'show',
      trigger: 'mouse_leave'
    });
    trackedViewContent.add(contentKey);
  };

  // 🆕 追蹤離開意圖彈窗繼續填表
  const trackExitIntentContinue = () => {
    const contentKey = 'exit_intent_continue';
    if (trackedViewContent.has(contentKey)) {
      console.log('⏭️ 跳過重複追蹤: 離開意圖繼續填表');
      return;
    }
    
    console.log('🎯 離開意圖挽留 - 繼續填表事件被觸發');
    trackInitiateCheckout('按鈕 - 離開意圖繼續填表', 'button', {
      action: 'continue',
      source: 'exit_intent_modal'
    });
    trackedViewContent.add(contentKey);
  };

  // 🔥 新增：倒數計時器狀態
  const [countdown, setCountdown] = useState({
    hours: 23,
    minutes: 45,
    seconds: 0
  });

  // 🔥 新增：成功案例輪播狀態
  const [currentCaseIndex, setCurrentCaseIndex] = useState(0);

  // 🔥 新增：審核數據動畫狀態
  const [isDataUpdating, setIsDataUpdating] = useState(false);

  // 🔥 新增：即時滾動數字狀態
  const [todayApplicants, setTodayApplicants] = useState(50);

  // 🔥 新增：展開/收起狀態
  const [showFullPhilosophy, setShowFullPhilosophy] = useState(false);
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [showAllPlans, setShowAllPlans] = useState(false);
  const [showSavedFormPrompt, setShowSavedFormPrompt] = useState(false);

  // 🔥 新增：浮動按鈕縮小狀態
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 🔥 新增：追蹤區域是否已經被追蹤過
  const [trackedSections, setTrackedSections] = useState<Set<string>>(new Set());

  // 🔥 新增：追蹤 ViewContent 事件的去重集合
  const [trackedViewContent, setTrackedViewContent] = useState<Set<string>>(new Set());

  // 🔥 新增：區域停留時間追蹤
  const [sectionDwellTime, setSectionDwellTime] = useState<{ [key: string]: number }>({});
  const [currentVisibleSection, setCurrentVisibleSection] = useState<string | null>(null);
  const sectionEnterTimeRef = useRef<{ [key: string]: number }>({});

  // 🔥 新增：當前表單步驟狀態
  const [currentFormStep, setCurrentFormStep] = useState(1);
  const [selectedFrequency, setSelectedFrequency] = useState('');
  const [selectedPlanIndex, setSelectedPlanIndex] = useState<number | null>(null);

  // 頻率與方案的對應關係
  const frequencyToPlanMap: { [key: string]: number } = {
    '1-3': 0,  // 入門方案
    '5-10': 1, // 進階方案
    '10+': 2,  // 高端方案
    'unsure': null // 不確定
  };

  // 方案與頻率的對應關係
  const planToFrequencyMap: { [key: number]: string } = {
    0: '1-3',
    1: '5-10',
    2: '10+'
  };

  // 處理頻率選擇
  const handleFrequencySelect = (frequency: string) => {
    setSelectedFrequency(frequency);
    const planIndex = frequencyToPlanMap[frequency];
    setSelectedPlanIndex(planIndex);
    
    // 如果對應到具體方案，滾動到方案區域
    if (planIndex !== null) {
      const planSection = document.getElementById('plans-section');
      if (planSection) {
        planSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  // 處理方案卡片點擊
  const handlePlanCardClick = (index: number) => {
    setSelectedPlanIndex(index);
    const frequency = planToFrequencyMap[index];
    if (frequency) {
      setSelectedFrequency(frequency);
      
      // 滾動到表單區域
      const formSection = document.getElementById('application-form');
      if (formSection) {
        formSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  // 🔥 新增：追蹤成功案例點擊
  const handleCaseClick = (caseName: string) => {
    if (pixelLoaded) {
      // 🔥 新增：檢查是否已經追蹤過這個案例
      const contentKey = `case_${caseName}`;
      if (trackedViewContent.has(contentKey)) {
        console.log('⏭️ 跳過重複追蹤:', caseName);
        return;
      }

      console.log('🎯 成功案例點擊事件被觸發:', caseName);
      trackViewContent(
        `成功案例 - ${caseName}`,
        '成功案例',
        {
          case_name: caseName,
          action: 'click'
        }
      );
      
      // 標記為已追蹤
      setTrackedViewContent(prev => new Set(prev).add(contentKey));
    }
  };

  // 🔥 新增：追蹤電話號碼點擊
  const handlePhoneClick = () => {
    if (pixelLoaded) {
      // 🔥 新增：檢查是否已經追蹤過
      const contentKey = 'phone_click';
      if (trackedViewContent.has(contentKey)) {
        console.log('⏭️ 跳過重複追蹤: 電話號碼點擊');
        return;
      }

      console.log('🎯 電話號碼點擊事件被觸發');
      trackViewContent(
        '電話號碼點擊',
        '聯絡方式',
        {
          contact_method: 'phone',
          action: 'click'
        }
      );
      
      // 標記為已追蹤
      setTrackedViewContent(prev => new Set(prev).add(contentKey));
    }
  };

  // 🔥 新增：保存表單數據到 localStorage
  const saveFormData = () => {
    const formState = {
      formData,
      timestamp: Date.now()
    };
    localStorage.setItem('vip_application_draft', JSON.stringify(formState));
  };

  // 🔥 新增：檢查是否有未完成的申請
  const checkSavedForm = () => {
    try {
      const saved = localStorage.getItem('vip_application_draft');
      if (saved) {
        const { formData: savedData, timestamp } = JSON.parse(saved);
        // 檢查是否過期（7 天）
        if (Date.now() - timestamp < 7 * 24 * 60 * 60 * 1000) {
          return { formData: savedData };
        } else {
          // 過期則清除
          localStorage.removeItem('vip_application_draft');
        }
      }
    } catch (error) {
      console.warn('檢查保存的表單失敗:', error);
    }
    return null;
  };

  // 🔥 新增：恢復保存的表單
  const restoreSavedForm = () => {
    const saved = checkSavedForm();
    if (saved) {
      setFormData(saved.formData);
      setShowSavedFormPrompt(false);
      scrollToSection('apply');
    }
  };

  // 🔥 新增：清除保存的表單
  const clearSavedForm = () => {
    localStorage.removeItem('vip_application_draft');
    // 🔥 新增：設置一個標記，表示用戶已經選擇重新開始
    localStorage.setItem('vip_application_dismissed', Date.now().toString());
    setShowSavedFormPrompt(false);
  };

  // 🔥 生成動態審核數據的函數
  const generateDynamicAuditData = () => {
    const applicants = Math.floor(Math.random() * (120 - 80 + 1)) + 80;
    const approved = Math.floor(Math.random() * (20 - 12 + 1)) + 12;
    const passRate = Math.round((approved / applicants) * 100);
    
    return {
      applicants,
      approved,
      passRate
    };
  };

  // 🆕 生成即時動態數據
  const generateRealtimeActivities = () => {
    const cities = ['台北', '新竹', '台中', '台南', '高雄', '桃園'];
    const surnames = ['陳', '林', '王', '李', '張', '劉', '黃', '吳'];
    const titles = ['先生', '總', '董', '老闆'];
    const plans = ['入門方案', '進階方案', '高端方案', '頂級方案'];
    const amounts = ['15 萬', '35 萬', '80 萬', '120 萬', '200 萬'];

    const activities = [];
    
    // 生成 10 條即時動態
    for (let i = 0; i < 10; i++) {
      const city = cities[Math.floor(Math.random() * cities.length)];
      const surname = surnames[Math.floor(Math.random() * surnames.length)];
      const title = titles[Math.floor(Math.random() * titles.length)];
      const plan = plans[Math.floor(Math.random() * plans.length)];
      const amount = amounts[Math.floor(Math.random() * amounts.length)];
      const minutesAgo = Math.floor(Math.random() * 30) + 1;

      const activityType = Math.random();
      let text = '';
      let type: 'apply' | 'approve' | 'withdraw' = 'apply';

      if (activityType < 0.4) {
        text = `${city}${surname}${title}提交申請（${plan}）`;
        type = 'apply';
      } else if (activityType < 0.7) {
        text = `${city}${surname}${title}審核通過（${plan}）`;
        type = 'approve';
      } else {
        text = `${city}${surname}${title}完成 ${amount} 提領`;
        type = 'withdraw';
      }

      activities.push({
        id: `activity-${i}`,
        text,
        time: `${minutesAgo} 分鐘前`,
        type
      });
    }

    return activities;
  };

  // 新增：生成审核编号的函数
  const generateApplicationId = () => {
    const date = new Date();
    const dateStr = date.toISOString().split('T')[0].replace(/-/g, '');
    const randomStr = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `VIP-${dateStr}-${randomStr}`;
  };

  // 🔥 新增：審核編號和聯繫時間狀態
  const [applicationNumber, setApplicationNumber] = useState('');
  const [contactTime, setContactTime] = useState('');

  // 新增：儲存審核編號
  const [applicationId, setApplicationId] = useState('');

  // 表單提交地址
  const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d5a2tb1dn6dhfpiabcg0';

  // 🔥 載入真實申請數據
  const loadRealApplicationData = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const thisMonth = new Date().toISOString().substring(0, 7);

      // 獲取本月總申請數
      const { count: monthlyCount, error: monthlyError } = await supabase
        .from('vip_applications')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', `${thisMonth}-01`);

      // 獲取今日總申請數
      const { count: todayCount, error: todayError } = await supabase
        .from('vip_applications')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', today);

      // 🔥 如果有錯誤（包括 JWT 過期），使用預設值
      if (monthlyError || todayError) {
        console.log('ℹ️ 無法載入申請數據，使用預設值');
        setRealApplicationData({
          monthlyTotal: 88,
          todayTotal: 50
        });
        return;
      }

      setRealApplicationData({
        monthlyTotal: monthlyCount || 88,
        todayTotal: todayCount || 50
      });
    } catch (error) {
      console.log('ℹ️ 載入申請數據時發生錯誤，使用預設值');
      setRealApplicationData({
        monthlyTotal: 88,
        todayTotal: 50
      });
    }
  };

  // 🔥 修改：合并所有 useEffect 到一个，避免 Hooks 调用顺序问题
  useEffect(() => {
    // 1. 加载设置和数据
    loadSettings();
    loadRealApplicationData();

    // 🔥 新增：設置超時保護，5 秒後強制結束 Loading
    const loadingTimeout = setTimeout(() => {
      if (isLoading) {
        console.log('⚠️ 載入超時，強制顯示頁面');
        setIsLoading(false);
        setSettings(getDefaultSettings());
      }
    }, 5000);

    // 2. 生成动态审核数据
    const newAuditData = generateDynamicAuditData();
    setDynamicAuditData(newAuditData);

    // 3. 生成即时动态数据
    const activities = generateRealtimeActivities();
    setRealtimeActivities(activities);

    // 4. 每 5 秒切换即时动态
    const activityInterval = setInterval(() => {
      setCurrentActivityIndex((prev) => (prev + 1) % activities.length);
    }, 5000);

    // 5. Hero 数据卡片自动轮播（每 4 秒切换）
    const dataCardInterval = setInterval(() => {
      setCurrentDataCard((prev) => (prev + 1) % 3);
    }, 4000);

    // 6. 倒数计时器
    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        let { hours, minutes, seconds } = prev;
        
        if (seconds > 0) {
          seconds--;
        } else if (minutes > 0) {
          minutes--;
          seconds = 59;
        } else if (hours > 0) {
          hours--;
          minutes = 59;
          seconds = 59;
        } else {
          // 重置为 24 小时
          hours = 23;
          minutes = 59;
          seconds = 59;
        }
        
        return { hours, minutes, seconds };
      });
    }, 1000);

    // 7. 预载入关键图片（优化载入速度）
    const preloadImages = () => {
      const criticalImages = [
        settings?.logo_url || "https://static.readdy.ai/image/8802434771b975039c88ac015f580732/aca501978f09f0ee060a0930ef0cab86.png"
      ];
      
      criticalImages.forEach(src => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = src;
        document.head.appendChild(link);
      });
    };
    preloadImages();

    // 8. 🔥 优化：使用 Intersection Observer 追踪滚动到特定区域 + 停留時間
    const observerOptions = {
      root: null,
      rootMargin: '-50px',
      threshold: 0.3
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        const sectionId = entry.target.id;
        
        if (entry.isIntersecting) {
          // 🔥 用戶進入區域
          const now = Date.now();
          sectionEnterTimeRef.current[sectionId] = now;
          setCurrentVisibleSection(sectionId);
          
          // 檢查是否已經追蹤過進入事件
          if (pixelLoaded && !trackedSections.has(sectionId)) {
            console.log('🎯 滾動到區域:', sectionId);
            
            let sectionName = '';
            switch (sectionId) {
              case 'philosophy':
                sectionName = '經營理念';
                break;
              case 'plans':
                sectionName = 'VIP 方案';
                break;
              case 'cases':
                sectionName = '成功案例';
                break;
              case 'faq':
                sectionName = '常見問題';
                break;
              case 'apply':
                sectionName = '申請表單';
                break;
              default:
                sectionName = '';
            }
            
            if (sectionName) {
              trackViewContent(
                sectionName,
                '區域',
                {
                  section_id: sectionId,
                  action: 'enter',
                  timestamp: new Date().toISOString()
                }
              );
              
              setTrackedSections(prev => new Set(prev).add(sectionId));
            }
          }
        } else {
          // 🔥 用戶離開區域 - 計算停留時間
          if (sectionEnterTimeRef.current[sectionId]) {
            const enterTime = sectionEnterTimeRef.current[sectionId];
            const now = Date.now();
            const dwellTime = Math.round((now - enterTime) / 1000); // 轉換為秒
            
            // 只記錄停留超過 3 秒的區域
            if (dwellTime >= 3 && pixelLoaded) {
              console.log(`⏱️ 離開區域: ${sectionId}, 停留時間: ${dwellTime} 秒`);
              
              let sectionName = '';
              switch (sectionId) {
                case 'philosophy':
                  sectionName = '經營理念';
                  break;
                case 'plans':
                  sectionName = 'VIP 方案';
                  break;
                case 'cases':
                  sectionName = '成功案例';
                  break;
                case 'faq':
                  sectionName = '常見問題';
                  break;
                case 'apply':
                  sectionName = '申請表單';
                  break;
                default:
                  sectionName = '';
              }
              
              if (sectionName) {
                // 🔥 追蹤離開事件，包含停留時間
                trackViewContent(
                  `${sectionName} - 停留 ${dwellTime} 秒`,
                  '區域停留',
                  {
                    section_id: sectionId,
                    section_name: sectionName,
                    dwell_time_seconds: dwellTime,
                    dwell_time_formatted: formatDwellTime(dwellTime),
                    action: 'leave',
                    timestamp: new Date().toISOString()
                  }
                );
                
                // 更新停留時間狀態
                setSectionDwellTime(prev => ({
                  ...prev,
                  [sectionId]: (prev[sectionId] || 0) + dwellTime
                }));
              }
            }
            
            // 清除進入時間記錄
            delete sectionEnterTimeRef.current[sectionId];
          }
          
          if (currentVisibleSection === sectionId) {
            setCurrentVisibleSection(null);
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    setTimeout(() => {
      const sectionsToObserve = ['philosophy', 'plans', 'cases', 'faq', 'apply'];
      sectionsToObserve.forEach(sectionId => {
        const element = document.getElementById(sectionId);
        if (element) {
          console.log('✅ 开始观察区域:', sectionId);
          observer.observe(element);
        } else {
          console.warn('⚠️ 找不到区域:', sectionId);
        }
      });
    }, 1000);

    // 9. 🔥 新增：細緻追蹤 - 使用事件委託統一處理
    const handleGlobalInteraction = (e: MouseEvent | TouchEvent) => {
      if (!pixelLoaded) return;

      const target = e.target as HTMLElement;
      
      // ✅ 修正：確保 target 是 HTMLElement 且有 closest 方法
      if (!target || typeof target.closest !== 'function') return;
      
      // 🔥 方案卡片互動追蹤（懸停/觸控）
      const planCard = target.closest('.plan-card-interactive');
      if (planCard && e.type === 'mouseenter') {
        const planName = planCard.getAttribute('data-plan-name');
        const contentKey = `plan_hover_${planName}`;
        if (planName && !trackedViewContent.has(contentKey)) {
          console.log('🎯 方案卡片懸停:', planName);
          trackViewContent(`方案懸停 - ${planName}`, '方案互動', {
            plan_name: planName,
            action: 'hover',
            interaction_type: 'plan_card'
          });
          setTrackedViewContent(prev => new Set(prev).add(contentKey));
        }
      }

      // 🔥 遊戲卡片點擊追蹤
      const gameCard = target.closest('.game-card-interactive');
      if (gameCard && e.type === 'click') {
        const gameName = gameCard.getAttribute('data-game-name');
        const gameRank = gameCard.getAttribute('data-game-rank');
        const contentKey = `game_click_${gameName}`;
        if (gameName && !trackedViewContent.has(contentKey)) {
          console.log('🎮 遊戲卡片點擊:', gameName);
          trackViewContent(`遊戲點擊 - ${gameName}`, '遊戲互動', {
            game_name: gameName,
            game_rank: gameRank,
            action: 'click',
            interaction_type: 'game_card'
          });
          setTrackedViewContent(prev => new Set(prev).add(contentKey));
        }
      }

      // 🔥 贏家榜查看追蹤
      const winnerCard = target.closest('.winner-card-interactive');
      if (winnerCard && (e.type === 'mouseenter' || e.type === 'click')) {
        const winnerName = winnerCard.getAttribute('data-winner-name');
        const winnerAmount = winnerCard.getAttribute('data-winner-amount');
        const actionType = e.type === 'mouseenter' ? 'hover' : 'click';
        const contentKey = `winner_${actionType}_${winnerName}`;
        if (winnerName && !trackedViewContent.has(contentKey)) {
          console.log('👑 贏家榜查看:', winnerName, actionType);
          trackViewContent(`贏家榜${actionType === 'hover' ? '懸停' : '點擊'} - ${winnerName}`, '贏家互動', {
            winner_name: winnerName,
            winner_amount: winnerAmount,
            action: actionType,
            interaction_type: 'winner_card'
          });
          setTrackedViewContent(prev => new Set(prev).add(contentKey));
        }
      }

      // 🔥 CTA 按鈕懸停追蹤
      const ctaButton = target.closest('.cta-button-interactive');
      if (ctaButton && e.type === 'mouseenter') {
        const ctaLocation = ctaButton.getAttribute('data-cta-location');
        const contentKey = `cta_hover_${ctaLocation}`;
        if (ctaLocation && !trackedViewContent.has(contentKey)) {
          console.log('🎯 CTA 按鈕懸停:', ctaLocation);
          trackViewContent(`CTA 懸停 - ${ctaLocation}`, 'CTA 互動', {
            cta_location: ctaLocation,
            action: 'hover',
            interaction_type: 'cta_button'
          });
          setTrackedViewContent(prev => new Set(prev).add(contentKey));
        }
      }
    };

    // 🔥 表單欄位聚焦追蹤
    const handleFormFocus = (e: FocusEvent) => {
      if (!pixelLoaded) return;

      const target = e.target as HTMLInputElement | HTMLSelectElement;
      
      // ✅ 修正：確保 target 是表單元素
      if (!target || !target.name) return;
      
      const fieldName = target.name;
      const contentKey = `form_focus_${fieldName}`;
      
      if (!trackedViewContent.has(contentKey)) {
        console.log('⌨️ 表單欄位聚焦:', fieldName);
        trackViewContent(`表單欄位 - ${fieldName}`, '表單互動', {
          field_name: fieldName,
          action: 'focus',
          interaction_type: 'form_field'
        });
        setTrackedViewContent(prev => new Set(prev).add(contentKey));
      }
    };

    // 9. 优化滚动性能 - 使用 passive 事件监听器
    const handleScroll = () => {
      window.requestAnimationFrame(() => {
        setIsScrolled(window.scrollY > 50);
        setShowFloatingCTA(window.scrollY > 300);
        
        setIsScrolling(true);
        
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
        
        scrollTimeoutRef.current = setTimeout(() => {
          setIsScrolling(false);
        }, 2000);

        // 🔥 修改：滾動深度追蹤 - 加入區域資訊
        if (pixelLoaded) {
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
          const scrollPercent = scrollHeight > 0 ? Math.round((scrollTop / scrollHeight) * 100) : 0;
          
          // 每 10% 追蹤一次
          const milestone = Math.floor(scrollPercent / 10) * 10;
          const contentKey = `scroll_depth_${milestone}`;
          
          if (milestone > 0 && milestone <= 100 && !trackedViewContent.has(contentKey)) {
            // 🔥 新增：判斷當前在哪個區域
            let currentSection = '未知區域';
            const sections = ['philosophy', 'plans', 'cases', 'faq', 'apply'];
            
            for (const sectionId of sections) {
              const element = document.getElementById(sectionId);
              if (element) {
                const rect = element.getBoundingClientRect();
                const windowHeight = window.innerHeight;
                
                // 如果區域在視窗中間 1/3 的位置，認為是當前區域
                if (rect.top < windowHeight / 2 && rect.bottom > windowHeight / 2) {
                  switch (sectionId) {
                    case 'philosophy':
                      currentSection = '經營理念';
                      break;
                    case 'plans':
                      currentSection = 'VIP 方案';
                      break;
                    case 'cases':
                      currentSection = '成功案例';
                      break;
                    case 'faq':
                      currentSection = '常見問題';
                      break;
                    case 'apply':
                      currentSection = '申請表單';
                      break;
                  }
                  break;
                }
              }
            }
            
            console.log(`📊 滾動深度: ${milestone}% (當前區域: ${currentSection})`);
            trackViewContent(`滾動深度 - ${milestone}% - ${currentSection}`, '滾動追蹤', {
              scroll_depth: milestone,
              current_section: currentSection,
              action: 'scroll',
              interaction_type: 'scroll_depth'
            });
            setTrackedViewContent(prev => new Set(prev).add(contentKey));
          }
        }
      });
    };

    // 🔥 新增：頁面停留時間追蹤
    const timeTrackers = [
      { time: 30000, key: 'time_30s', label: '30秒' },
      { time: 60000, key: 'time_1m', label: '1分鐘' },
      { time: 120000, key: 'time_2m', label: '2分鐘' },
      { time: 300000, key: 'time_5m', label: '5分鐘' }
    ];

    const timeoutIds: NodeJS.Timeout[] = [];

    timeTrackers.forEach(tracker => {
      const timeoutId = setTimeout(() => {
        if (pixelLoaded && !trackedViewContent.has(tracker.key)) {
          console.log('⏱️ 頁面停留時間:', tracker.label);
          trackViewContent(`停留時間 - ${tracker.label}`, '時間追蹤', {
            dwell_time: tracker.label,
            action: 'dwell',
            interaction_type: 'page_dwell'
          });
          setTrackedViewContent(prev => new Set(prev).add(tracker.key));
        }
      }, tracker.time);
      
      timeoutIds.push(timeoutId);
    });

    // 綁定事件監聽器
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('mouseenter', handleGlobalInteraction, true);
    document.addEventListener('click', handleGlobalInteraction, true);
    document.addEventListener('touchstart', handleGlobalInteraction, { passive: true });
    document.addEventListener('focusin', handleFormFocus, true);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mouseenter', handleGlobalInteraction, true);
      document.removeEventListener('click', handleGlobalInteraction, true);
      document.removeEventListener('touchstart', handleGlobalInteraction, true);
      document.removeEventListener('focusin', handleFormFocus, true);
      clearInterval(activityInterval);
      clearInterval(dataCardInterval);
      clearInterval(countdownInterval);
      clearTimeout(loadingTimeout);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      timeoutIds.forEach(id => clearTimeout(id));
      observer.disconnect();
    };
  }, [hasInteractedWithForm, pixelLoaded, trackPageView, trackViewContent, trackInitiateCheckout, trackedSections, trackedViewContent]);

  // 🔥 新增：单独的 useEffect 处理 PageView 事件
  useEffect(() => {
    if (pixelLoaded) {
      console.log('✅ Pixel 已加载，触发 PageView 事件');
      // 延迟 500ms 确保 Pixel 完全初始化
      setTimeout(() => {
        trackPageView();
      }, 500);
    } else {
      console.log('⏳ Pixel 尚未加载，等待中...');
    }
  }, [pixelLoaded, trackPageView]);

  // 🔥 新增：審核數據即時更新動畫
  useEffect(() => {
    const updateInterval = setInterval(() => {
      setIsDataUpdating(true);
      
      // 模拟数据更新
      setTimeout(() => {
        const newAuditData = generateDynamicAuditData();
        setDynamicAuditData(newAuditData);
        setIsDataUpdating(false);
      }, 500);
    }, 15000); // 每 15 秒更新一次

    return () => clearInterval(updateInterval);
  }, []);

  // 🔥 新增：成功案例自動輪播
  useEffect(() => {
    const caseInterval = setInterval(() => {
      setCurrentCaseIndex((prev) => (prev + 2) % successCases.length);
    }, 6000); // 每 6 秒切換一次

    return () => clearInterval(caseInterval);
  }, []);

  // 🔥 修改：顯示的成功案例（一次顯示 2 個）
  const displayedCases = [
    successCases[currentCaseIndex % successCases.length],
    successCases[(currentCaseIndex + 1) % successCases.length]
  ];

  // 🔥 新增：即時滾動數字動畫
  useEffect(() => {
    // 初始化今日申請數（固定從 50 開始）
    setTodayApplicants(50);

    // 每 3-15 秒隨機增加 1 個申請
    const interval = setInterval(() => {
      setTodayApplicants(prev => {
        const increment = Math.random() > 0.7 ? 1 : 0;
        return prev + increment;
      });
    }, Math.random() * 12000 + 3000); // 3-15 秒隨機間隔

    return () => clearInterval(interval);
  }, []);

  // 🔥 修改：頁面載入時檢查是否有未完成的申請
  useEffect(() => {
    // 🔥 新增：先檢查是否已經提交過申請
    const submitted = checkSubmissionStatus();
    if (submitted) {
      // 如果已經提交過，就不顯示未完成申請提示
      console.log('✅ 用戶已提交申請，跳過未完成申請檢查');
      return;
    }

    // 🔥 新增：檢查用戶是否在 24 小時內選擇過「重新開始」
    const dismissed = localStorage.getItem('vip_application_dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed);
      const now = Date.now();
      // 如果在 24 小時內選擇過重新開始，就不再顯示提示
      if (now - dismissedTime < 24 * 60 * 60 * 1000) {
        return;
      } else {
        // 超過 24 小時，清除標記
        localStorage.removeItem('vip_application_dismissed');
      }
    }

    // 檢查是否有未完成的申請
    const saved = checkSavedForm();
    if (saved) {
      setShowSavedFormPrompt(true);
    }
  }, []);

  // 🔥 修改：表單數據變化時自動保存
  useEffect(() => {
    // 只有在表單有數據時才保存
    if (formData.phone || formData.selected_plan || formData.contact_time || formData.line_id) {
      saveFormData();
    }
  }, [formData]);

  // 🔥 新增：監聽輸入框聚焦/失焦事件
  useEffect(() => {
    const handleFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA') {
        setIsKeyboardVisible(true);
        
        // 🔥 優化：延遲滾動，等待鍵盤完全彈出，並增加偏移量
        setTimeout(() => {
          const rect = target.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const targetTop = rect.top + scrollTop;
          // 🔥 將輸入框滾動到螢幕上方 1/3 位置，避免被鍵盤遮擋
          const offset = window.innerHeight / 3;
          
          window.scrollTo({
            top: targetTop - offset,
            behavior: 'smooth'
          });
        }, 300);
      }
    };

    const handleBlur = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA') {
        // 延遲隱藏，避免切換輸入框時閃爍
        setTimeout(() => {
          // 檢查是否還有其他輸入框處於聚焦狀態
          const activeElement = document.activeElement;
          if (activeElement?.tagName !== 'INPUT' && 
              activeElement?.tagName !== 'SELECT' && 
              activeElement?.tagName !== 'TEXTAREA') {
            setIsKeyboardVisible(false);
          }
        }, 100);
      }
    };

    document.addEventListener('focusin', handleFocus);
    document.addEventListener('focusout', handleBlur);

    return () => {
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleBlur);
    };
  }, []);

  // 🔥 新增：更新调试信息
  useEffect(() => {
    const updateDebugStats = () => {
      try {
        // 读取今天触发的所有事件
        const today = new Date().toLocaleDateString('zh-TW');
        const allEvents: any[] = [];
        
        // 遍历 localStorage 中所有以 fb_pixel_event_ 开头的 key
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('fb_pixel_event_')) {
            try {
              const eventData = JSON.parse(localStorage.getItem(key) || '{}');
              const eventDate = new Date(eventData.timestamp).toLocaleDateString('zh-TW');
              
              // 只统计今天的事件
              if (eventDate === today) {
                allEvents.push(eventData);
              }
            } catch (e) {
              // 忽略解析错误
            }
          }
        }

        // 按时间排序，获取最后一个事件
        allEvents.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        
        const lastEvent = allEvents[0];
        
        setDebugStats({
          eventCount: allEvents.length,
          lastEvent: lastEvent ? lastEvent.eventName : '',
          lastEventTime: lastEvent ? new Date(lastEvent.timestamp).toLocaleTimeString('zh-TW') : ''
        });
      } catch (error) {
        console.error('❌ 读取调试数据失败:', error);
      }
    };

    // 立即更新一次
    updateDebugStats();

    // 每 2 秒更新一次
    const interval = setInterval(updateDebugStats, 2000);

    return () => clearInterval(interval);
  }, []);

  // 🔥 修复：保存事件到 localStorage
  const saveEventToLocalStorage = (eventName: string, eventData?: any) => {
    try {
      const eventKey = `fb_pixel_event_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const eventRecord = {
        eventName,
        eventData: eventData || {},
        timestamp: new Date().toISOString()
      };
      
      localStorage.setItem(eventKey, JSON.stringify(eventRecord));
      console.log('✅ 事件已保存到 localStorage:', eventName);
      
      // 清理 7 天前的旧事件
      const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('fb_pixel_event_')) {
          try {
            const eventData = JSON.parse(localStorage.getItem(key) || '{}');
            const eventTime = new Date(eventData.timestamp).getTime();
            if (eventTime < sevenDaysAgo) {
              localStorage.removeItem(key);
            }
          } catch (e) {
            // 忽略错误
          }
        }
      }
    } catch (error) {
      console.error('❌ 保存事件到 localStorage 失败:', error);
    }
  };

  // 🔥 修复：在触发事件时同时保存到 localStorage
  useEffect(() => {
    if (!pixelLoaded) return;

    console.log('✅ Pixel 已加载，触发 PageView 事件');
    
    // 延迟 500ms 确保 Pixel 完全初始化
    const timer = setTimeout(() => {
      console.log('🎯 触发 PageView 事件');
      trackPageView();
      saveEventToLocalStorage('PageView', {
        url: window.location.href,
        device_type: getDeviceType()
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [pixelLoaded, trackPageView]);

  // 🔥 修复：滚动深度追踪时保存到 localStorage
  useEffect(() => {
    if (!pixelLoaded) return;

    const scrollDepths = [0, 25, 50, 75, 100];
    const triggeredDepths = new Set<number>();

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrollPercent = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;

      scrollDepths.forEach(depth => {
        if (scrollPercent >= depth && !triggeredDepths.has(depth)) {
          triggeredDepths.add(depth);
          console.log(`🎯 滾動深度: ${depth}%`);
          
          const eventData = {
            scroll_depth: depth,
            url: window.location.href,
            device_type: getDeviceType()
          };
          
          window.fbq('trackCustom', `ScrollDepth${depth}`, eventData);
          saveEventToLocalStorage(`Scroll Depth ${depth}%`, eventData);
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [pixelLoaded]);

  // 🔥 修复：Intersection Observer 触发时保存到 localStorage
  useEffect(() => {
    if (!pixelLoaded) return;

    const observerOptions = {
      root: null,
      rootMargin: '-50px',
      threshold: 0.3
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id;
          const sectionName = entry.target.getAttribute('data-section-name') || sectionId;
          
          console.log(`👁️ 用户查看了区域: ${sectionName}`);
          
          const eventData = {
            content_name: sectionName,
            content_type: 'section',
            url: window.location.href,
            device_type: getDeviceType()
          };
          
          trackViewContent(sectionName, 'section');
          saveEventToLocalStorage('ViewContent', eventData);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    const sections = document.querySelectorAll('[data-section-name]');
    sections.forEach(section => {
      observer.observe(section);
      console.log('✅ 开始观察区域:', section.id);
    });

    return () => {
      sections.forEach(section => observer.unobserve(section));
    };
  }, [pixelLoaded, trackViewContent]);

  // 🔥 修复：表单交互时保存到 localStorage
  const handleFormFocus = (formName: string) => {
    if (!hasInteractedWithForm && pixelLoaded) {
      setHasInteractedWithForm(true);
      console.log('🎯 用户开始填写表單:', formName);
      trackInitiateCheckout(formName, 'form');
    }
  };

  // 🔥 新增：表單驗證狀態
  const [formValidation, setFormValidation] = useState({
    plan: { valid: true, message: '✓ 已選擇' }, // 🔥 修改：預設為有效，因為有預設值
    phone: { valid: false, message: '' },
    contactTime: { valid: true, message: '✓ 已選擇' } // 🔥 修改：預設為有效，因為有預設值
  });

  // 🔥 新增：表單進度百分比
  const [formProgressPercent, setFormProgressPercent] = useState(0);

  // 🔥 優化：即時驗證函數
  const validateFormField = (fieldName: string, value: string) => {
    const validation = { valid: false, message: '' };

    switch (fieldName) {
      case 'plan':
        if (!value || value.trim() === '' || value === '選擇方案') {
          validation.message = '請選擇遊戲頻率';
        } else {
          validation.valid = true;
          validation.message = '✓ 已選擇';
        }
        break;

      case 'phone':
        if (!value || value.trim() === '') {
          validation.message = '請輸入手機號碼';
        } else if (!/^09\d{8}$/.test(value)) {
          validation.message = '請輸入正確的手機號碼格式（例如：0912345678）';
        } else {
          validation.valid = true;
          validation.message = '✓ 格式正確';
        }
        break;

      case 'contactTime':
        if (!value || value.trim() === '') {
          validation.message = '請選擇聯繫時間';
        } else {
          validation.valid = true;
          validation.message = '✓ 已選擇';
        }
        break;
    }

    setFormValidation(prev => ({
      ...prev,
      [fieldName]: validation
    }));

    return validation.valid;
  };

  // 🔥 新增：計算表單進度
  useEffect(() => {
    const validFields = Object.values(formValidation).filter(v => v.valid).length;
    const totalFields = Object.keys(formValidation).length;
    const progress = Math.round((validFields / totalFields) * 100);
    setFormProgressPercent(progress);
  }, [formValidation]);

  // 🔥 優化：處理輸入變化
  const handleFormInputChange = (field: string, value: string) => {
    // 如果是電話號碼，限制最多 10 碼
    if (field === 'phone') {
      // 只允許數字，並限制最多 10 碼
      const numericValue = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, [field]: numericValue }));
      
      // 即時驗證電話號碼
      validateFormField('phone', numericValue);
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
      
      // 即時驗證其他欄位
      validateFormField(field, value);
    }
  };

  // 🔥 優化：表單提交 - 簡化驗證邏輯
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('🔥 表單提交開始');
    console.log('📋 表單數據:', formData);
    console.log('📊 驗證狀態:', formValidation);
    console.log('📈 進度:', formProgressPercent);
    
    // 🔥 簡化：只檢查必填欄位是否有值
    if (!formData.plan || !formData.phone || !formData.contactTime) {
      console.log('❌ 驗證失敗：缺少必填欄位');
      showError('請填寫所有必填欄位');
      return;
    }

    // 驗證手機號碼格式
    const phoneRegex = /^09\d{8}$/;
    if (!phoneRegex.test(formData.phone)) {
      console.log('❌ 驗證失敗：手機號碼格式錯誤');
      showError('請輸入正確的手機號碼格式（例如：0912345678）');
      return;
    }

    console.log('✅ 驗證通過，開始提交');
    setIsSubmitting(true);

    try {
      // 生成審核編號
      const applicationNumber = `VIP${Date.now()}${Math.floor(Math.random() * 1000)}`;
      console.log('📝 生成審核編號:', applicationNumber);

      // 🔥 修正：將方案名稱轉換為對應的 value
      const planValueMap: { [key: string]: string } = {
        '入門方案（押金 7,000 / 額度 1 萬）': 'starter',
        '進階方案 ⭐推薦（押金 3.5 萬 / 額度 5 萬）': 'advanced',
        '高端方案（押金 7 萬 / 額度 10 萬）': 'premium',
        '頂級方案（押金 17.5 萬+ / 額度 25 萬+）': 'elite'
      };
      
      const selectedPlanValue = planValueMap[formData.plan] || 'advanced';
      console.log('📊 方案轉換:', formData.plan, '->', selectedPlanValue);

      // 🔥 步驟 1：先建立討論區帳號
      console.log('🔥 步驟 1：開始建立討論區帳號...');
      console.log('📱 電話號碼:', formData.phone);
      
      const { data: forumUserData, error: forumUserError } = await supabase
        .from('forum_users')
        .insert([
          {
            phone: formData.phone,
            user_status: 'basic',
            phone_verified: false,
            nickname_set: false,
            can_post: false,
            join_form_data: {
              source: 'vip_application',
              application_number: applicationNumber,
              selected_plan: selectedPlanValue,
              contact_time: formData.contactTime,
              submitted_at: new Date().toISOString(),
            },
          },
        ])
        .select();

      // 🔥 如果討論區帳號建立失敗，檢查是否是因為電話號碼重複
      if (forumUserError) {
        console.log('⚠️ 討論區帳號建立失敗:', forumUserError.message);
        console.log('⚠️ 錯誤代碼:', forumUserError.code);
        
        // 如果是電話號碼重複（PostgreSQL unique violation）
        if (forumUserError.code === '23505') {
          console.log('ℹ️ 討論區帳號已存在，檢查是否有對應的 VIP 申請...');
          
          // 檢查是否已經有 VIP 申請
          const { data: existingVIPApp, error: checkError } = await supabase
            .from('vip_applications')
            .select('application_number, status, phone')
            .eq('phone', formData.phone)
            .maybeSingle();
          
          if (checkError) {
            console.error('❌ 檢查 VIP 申請失敗:', checkError);
            showError('系統錯誤，請稍後再試');
            setIsSubmitting(false);
            return;
          }
          
          if (existingVIPApp) {
            console.log('⚠️ 此電話號碼已有 VIP 申請:', existingVIPApp);
            showError(`此電話號碼已申請過（審核編號：${existingVIPApp.application_number}）`);
            setIsSubmitting(false);
            return;
          }
          
          // 如果沒有 VIP 申請，繼續提交（可能是之前在討論區註冊過）
          console.log('ℹ️ 討論區帳號已存在，但沒有 VIP 申請，繼續提交...');
        } else {
          // 其他錯誤，顯示錯誤訊息但繼續提交 VIP 申請
          console.error('❌ 討論區帳號建立失敗（非重複錯誤）:', forumUserError);
          showError('討論區帳號建立失敗，但 VIP 申請仍會繼續處理');
        }
      } else {
        console.log('✅ 討論區帳號建立成功:', forumUserData);
      }

      // 🔥 步驟 2：提交 VIP 申請到 Supabase - 修正欄位名稱
      console.log('🔥 步驟 2：開始提交 VIP 申請...');
      const { data, error } = await supabase
        .from('vip_applications')
        .insert([
          {
            application_number: applicationNumber,
            phone: formData.phone,
            selected_plan: selectedPlanValue,  // 🔥 修正：使用 selected_plan 而不是 plan
            contact_time: formData.contactTime,
            status: 'pending',
            created_at: new Date().toISOString(),
          },
        ])
        .select();

      if (error) {
        console.error('❌ VIP 申請提交失敗:', error);
        
        // 🔥 如果是電話號碼重複錯誤
        if (error.code === '23505') {
          // 查詢現有申請
          const { data: existingApp } = await supabase
            .from('vip_applications')
            .select('application_number, status')
            .eq('phone', formData.phone)
            .single();
          
          if (existingApp) {
            showError(`此電話號碼已申請過（審核編號：${existingApp.application_number}）`);
          } else {
            showError('此電話號碼已提交過申請');
          }
        } else {
          showError('提交失敗，請稍後再試');
        }
        
        setIsSubmitting(false);
        return;
      }

      console.log('✅ VIP 申請提交成功:', data);

      // 🔥 步驟 3：儲存提交狀態和審核編號
      saveSubmissionStatus(applicationNumber, {
        phone: formData.phone,
        selected_plan: selectedPlanValue,  // 🔥 修正：保存正確的 value
        contact_time: formData.contactTime
      });
      
      setIsSubmitted(true);
      setApplicationNumber(applicationNumber);
      setApplicationId(applicationNumber);
      
      // 🔥 新增：追蹤 Lead 事件
      if (pixelLoaded) {
        console.log('🎯 觸發 Lead 事件 - VIP 申請表單提交');
        trackLead(
          'VIP 申請表單',
          getPlanValue(selectedPlanValue),
          {
            phone: formData.phone,
            plan: selectedPlanValue,
            contact_time: formData.contactTime,
            application_number: applicationNumber,
            currency: 'TWD'
          }
        );
        
        // 保存事件到 localStorage
        saveEventToLocalStorage('Lead', {
          content_name: 'VIP 申請表單',
          value: getPlanValue(selectedPlanValue),
          currency: 'TWD',
          phone: formData.phone,
          plan: selectedPlanValue,
          application_number: applicationNumber
        });
      }
      
      // 關閉彈窗
      setShowQuickApplyModal(false);
      
      // 🔥 修改：顯示抽紅包彈窗
      console.log('🎉 準備顯示抽紅包彈窗...');
      setShowLuckyDraw(true);
      console.log('✅ 抽紅包彈窗已設置為顯示');
      
      // 滾動到頁面頂部
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // 清除保存的表單草稿
      localStorage.removeItem('vip_application_draft');
      
      // 重置表單
      setFormData({
        plan: '進階方案 ⭐推薦（押金 3.5 萬 / 額度 5 萬）',
        phone: '',
        contactTime: '任何時間都可以',
      });
      
      // 清除錯誤
      setFormErrors({});
      
      // 🔥 重置驗證狀態
      setFormValidation({
        plan: { valid: true, message: '✓ 已選擇' },
        phone: { valid: false, message: '' },
        contactTime: { valid: true, message: '✓ 已選擇' }
      });
      
    } catch (error) {
      console.error('❌ 提交失敗:', error);
      showError('提交失敗，請稍後再試');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormSubmitOld = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    setSubmitStatus('submitting');

    try {
      const formData = new FormData(e.currentTarget);
      const data = {
        name: formData.get('name') as string,
        phone: formData.get('phone') as string,
        email: formData.get('email') as string,
        message: formData.get('message') as string,
      };

      console.log('📤 提交表单数据:', data);

      const response = await fetch(formSubmitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(data as any).toString(),
      });

      if (response.ok) {
        console.log('✅ 表单提交成功');
        
        const eventData = {
          content_name: '申请表单',
          content_type: 'form',
          value: 100,
          currency: 'TWD',
          url: window.location.href,
          device_type: getDeviceType()
        };
        
        trackLead('申请表单', 100, data);
        saveEventToLocalStorage('Lead', eventData);
        
        setSubmitStatus('success');
        (e.target as HTMLFormElement).reset();
        
        setTimeout(() => {
          setSubmitStatus('idle');
        }, 3000);
      } else {
        throw new Error('提交失败');
      }
    } catch (error) {
      console.error('❌ 表单提交失败:', error);
      setSubmitStatus('error');
      
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDeviceType = () => {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return 'tablet';
    }
    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
      return 'mobile';
    }
    return 'desktop';
  };

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('brand_settings')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        // 🔥 靜默處理 JWT 過期錯誤，不影響用戶體驗
        if (error.code === 'PGRST301' || error.message?.includes('JWT')) {
          console.log('ℹ️ Supabase Token 已過期，使用預設設定');
        } else {
          console.log('ℹ️ 無法載入設定，使用預設值');
        }
        setSettings(getDefaultSettings());
        setIsLoading(false);
        return;
      }

      setSettings(data || getDefaultSettings());
    } catch (error: any) {
      // 🔥 捕獲所有錯誤，靜默處理
      console.log('ℹ️ 載入設定時發生錯誤，使用預設值');
      setSettings(getDefaultSettings());
    } finally {
      // 🔥 確保無論如何都會結束 Loading 狀態
      setIsLoading(false);
    }
  };

  // 新增：获取预设设定的函数
  const getDefaultSettings = () => ({
    brand_name: '火星人娛樂城',
    brand_subtitle: 'VIP 審核區',
    logo_url: 'https://static.readdy.ai/image/8802434771b975039c88ac015f580732/aca501978f09f0ee060a0930ef0cab86.png',
    hero_title: '火星人 VIP 審核區',
    hero_subtitle: '圈內受邀制 · 真實信用額度管理',
    hero_description: '專屬管家對接 · 私密審核流程 · 可預期的出款節奏',
    monthly_remaining: 12,
    monthly_approved: 18,
    monthly_applicants: 100,
    withdrawal_small_limit: 100000,
    withdrawal_medium_min: 100000,
    withdrawal_medium_max: 500000,
    withdrawal_large_min: 500000,
    plan_basic_deposit: 10000,
    plan_basic_limit: 20000,
    plan_basic_features: '每月玩 1-3 次\n小額提領即時到帳\n專屬客服 LINE 聯繫',
    plan_advanced_deposit: 50000,
    plan_advanced_limit: 100000,
    plan_advanced_features: '每月玩 5-10 次\n提領當天完成\n專屬客服優先處理',
    plan_premium_deposit: 250000,
    plan_premium_limit: 500000,
    plan_premium_features: '每月玩 10 次以上\n大額現金面交\nVIP 專屬客服',
    plan_vip_deposit_text: '50 萬',
    plan_vip_limit: 1000000,
    plan_vip_features: '額度彈性調整\n專人現金面交\n24 小時專屬服務',
    feature_1_icon: 'ri-delete-bin-2-fill',
    feature_1_title: '看完就刪',
    feature_1_highlight: '資料即銷',
    feature_1_description: '審核通過後立即銷毀資料，不保留任何記錄，這是圈內的基本規矩。信用良好玩家的隱私保障。',
    feature_2_icon: 'ri-heart-fill',
    feature_2_title: '專屬管家',
    feature_2_highlight: '人情味',
    feature_2_description: '不是冷冰冰的系統，而是真人客服。有問題隨時聊，就像朋友一樣。大額玩家服務更貼心。',
    feature_3_icon: 'ri-money-dollar-circle-fill',
    feature_3_title: '快速出金',
    feature_3_highlight: '當天到手',
    feature_3_description: '白天申請當天完成。小額即時轉帳，大額現金面交或分批處理。從不拖延，快速出金保證。',
    feature_4_icon: 'ri-settings-3-fill',
    feature_4_title: 'VIP 信用額度',
    feature_4_highlight: '彈性規劃',
    feature_4_description: '根據您的需求調整額度，不會強迫推銷，一切以您的需求為主。信用良好玩家享更高額度。',
    line_url: 'https://lin.ee/EkFWw1qE'
  });

  // 驗證電話號碼格式(台灣手機號碼)
  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^09\d{8}$/;
    return phoneRegex.test(phone);
  };

  // 新增：检查电话号码是否已存在
  const checkPhoneExists = async (phone: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('vip_applications')
        .select('phone')
        .eq('phone', phone)
        .limit(1);

      // 🔥 如果有錯誤（包括 JWT 過期），允許提交
      if (error) {
        console.log('ℹ️ 檢查電話號碼失敗，允許提交');
        return false;
      }

      return data && data.length > 0;
    } catch (error) {
      console.log('ℹ️ 檢查電話號碼時發生錯誤，允許提交');
      return false;
    }
  };

  // 驗證表單字段
  const validateField = async (fieldName: string, value: string) => {
    const errors = { ...formErrors };

    switch (fieldName) {
      case 'phone':
        if (!value.trim()) {
          errors.phone = '請輸入電話號碼';
        } else if (!validatePhone(value)) {
          errors.phone = '請輸入正確的手機號碼格式(09XXXXXXXX)';
        } else {
          // 檢查電話號碼是否已存在
          const exists = await checkPhoneExists(value);
          if (exists) {
            errors.phone = '此電話號碼已提交過申請,請勿重複申請';
          } else {
            delete errors.phone;
          }
        }
        break;
      case 'contact_time':
        if (!value.trim()) {
          errors.contact_time = '請選擇方便聯繫的時間';
        } else {
          delete errors.contact_time;
        }
        break;
      // 🆕 新增:預期方案驗證
      case 'selected_plan':
        if (!value.trim()) {
          errors.selected_plan = '請選擇您想要的額度方案';
        } else {
          delete errors.selected_plan;
        }
        break;
      // 🆕 LINE ID 不需要驗證(選填)
      case 'line_id':
        delete errors.line_id;
        break;
    }

    setFormErrors(errors);
  };

  // 新增：AI 推薦方案
  const getAIRecommendation = (plan: string) => {
    const recommendations: { [key: string]: string } = {
      'starter': '💡 入門方案很適合想先試試看的玩家,風險低、門檻低',
      'advanced': '⭐ 進階方案是最多人選擇的,額度適中、服務完善',
      'premium': '💎 高端方案適合經常遊戲的玩家,享受更高額度和優先服務',
      'elite': '👑 頂級方案為高額玩家量身打造,專屬管家 24 小時服務'
    };
    return recommendations[plan] || '';
  };

  // 新增：計算 AI 推薦文字
  const aiRecommendation = getAIRecommendation(formData.selected_plan);

  // 修改：處理輸入變化（加上即時反饋）
  const handleInputChange = (fieldName: string, value: string) => {
    setFormData({ ...formData, [fieldName]: value });
    
    // 🔥 修改：預期方案也加入即時驗證
    if (fieldName !== 'phone') {
      validateField(fieldName, value);
    }
    setHasInteractedWithForm(true);
  };

  // 驗證所有必填字段
  const validateForm = async (): Promise<boolean> => {
    const errors: any = {};

    if (!formData.phone.trim()) {
      errors.phone = '請輸入電話號碼';
    } else if (!validatePhone(formData.phone)) {
      errors.phone = '請輸入正確的手機號碼格式(09XXXXXXXX)';
    } else {
      // 檢查電話號碼是否已存在
      const exists = await checkPhoneExists(formData.phone);
      if (exists) {
        errors.phone = '此電話號碼已提交過申請,請勿重複申請';
      }
    }

    if (!formData.contact_time.trim()) {
      errors.contact_time = '請選擇方便聯繫的時間';
    }

    // 🆕 新增:驗證預期方案
    if (!formData.selected_plan.trim()) {
      errors.selected_plan = '請選擇您想要的額度方案';
    }

    // 🆕 LINE ID 不需要驗證(選填)

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 🔥 修改：表單驗證 - 根據當前步驟驗證
  const validateCurrentStep = async (): Promise<boolean> => {
    const errors: any = {};

    // 驗證方案
    if (!formData.plan || !formData.plan.trim()) {
      errors.plan = '請選擇您的遊戲頻率';
      showError('請選擇您的遊戲頻率');
      return false;
    }
    
    // 驗證電話
    if (!formData.phone || !formData.phone.trim()) {
      errors.phone = '請輸入電話號碼';
      showError('請輸入電話號碼');
      return false;
    } else if (!validatePhone(formData.phone)) {
      errors.phone = '請輸入正確的手機號碼格式(09XXXXXXXX)';
      showError('請輸入正確的手機號碼格式(09XXXXXXXX)');
      return false;
    } else {
      const exists = await checkPhoneExists(formData.phone);
      if (exists) {
        errors.phone = '此電話號碼已提交過申請,請勿重複申請';
        showError('此電話號碼已提交過申請,請勿重複申請');
        return false;
      }
    }
    
    // 驗證聯繫時間
    if (!formData.contactTime || !formData.contactTime.trim()) {
      errors.contactTime = '請選擇方便聯繫的時間';
      showError('請選擇方便聯繫的時間');
      return false;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 🔥 修改：下一步
  const handleNextStep = async () => {
    const isValid = await validateCurrentStep();
    if (isValid) {
      if (currentFormStep < 2) {
        setCurrentFormStep(currentFormStep + 1);
      } else {
        // 最後一步，提交表單
        await handleSubmit(new Event('submit') as any);
      }
    }
  };

  // 🔥 修改：上一步
  const handlePrevStep = () => {
    if (currentFormStep > 1) {
      setCurrentFormStep(currentFormStep - 1);
    }
  };

  // 🆕 新增：預填已提交的資料
  useEffect(() => {
    if (isSubmitted && savedApplication?.formData) {
      setFormData({
        phone: savedApplication.formData.phone || '',
        name: savedApplication.formData.name || '',
        line_id: savedApplication.formData.line_id || '',
        selected_plan: savedApplication.formData.selected_plan || '',
        contact_time: savedApplication.formData.contact_time || ''
      });
      console.log('✅ 已預填申請資料');
    }
  }, [isSubmitted, savedApplication]);

  // 🔥 修改：handleSubmit 改為內部調用
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('🔥 表單提交開始');
    console.log('📋 表單數據:', formData);
    
    // 🔥 修正：驗證必填欄位，使用正確的欄位名稱
    if (!formData.plan || !formData.phone || !formData.contactTime) {
      console.log('❌ 驗證失敗：缺少必填欄位');
      showError('請填寫所有必填欄位');
      return;
    }

    // 驗證手機號碼格式
    const phoneRegex = /^09\d{8}$/;
    if (!phoneRegex.test(formData.phone)) {
      console.log('❌ 驗證失敗：手機號碼格式錯誤');
      showError('請輸入正確的手機號碼格式（例如：0912345678）');
      return;
    }

    console.log('✅ 驗證通過，開始提交');
    setIsSubmitting(true);

    try {
      // 生成審核編號
      const applicationNumber = `VIP${Date.now()}${Math.floor(Math.random() * 1000)}`;
      console.log('📝 生成審核編號:', applicationNumber);

      // 🔥 步驟 1：先建立討論區帳號
      console.log('🔥 步驟 1：開始建立討論區帳號...');
      console.log('📱 電話號碼:', formData.phone);
      
      const { data: forumUserData, error: forumUserError } = await supabase
        .from('forum_users')
        .insert([
          {
            phone: formData.phone,
            user_status: 'basic',
            phone_verified: false,
            nickname_set: false,
            can_post: false,
            join_form_data: {
              source: 'vip_application',
              application_number: applicationNumber,
              selected_plan: formData.plan,
              contact_time: formData.contactTime,
              submitted_at: new Date().toISOString(),
            },
          },
        ])
        .select();

      // 🔥 如果討論區帳號建立失敗，檢查是否是因為電話號碼重複
      if (forumUserError) {
        console.log('⚠️ 討論區帳號建立失敗:', forumUserError.message);
        console.log('⚠️ 錯誤代碼:', forumUserError.code);
        
        // 如果是電話號碼重複（PostgreSQL unique violation）
        if (forumUserError.code === '23505') {
          console.log('ℹ️ 討論區帳號已存在，檢查是否有對應的 VIP 申請...');
          
          // 檢查是否已經有 VIP 申請
          const { data: existingVIPApp, error: checkError } = await supabase
            .from('vip_applications')
            .select('application_number, status, phone')
            .eq('phone', formData.phone)
            .maybeSingle();
          
          if (checkError) {
            console.error('❌ 檢查 VIP 申請失敗:', checkError);
            showError('系統錯誤，請稍後再試');
            setIsSubmitting(false);
            return;
          }
          
          if (existingVIPApp) {
            console.log('⚠️ 此電話號碼已有 VIP 申請:', existingVIPApp);
            showError(`此電話號碼已申請過（審核編號：${existingVIPApp.application_number}）`);
            setIsSubmitting(false);
            return;
          }
          
          // 如果沒有 VIP 申請，繼續提交（可能是之前在討論區註冊過）
          console.log('ℹ️ 討論區帳號已存在，但沒有 VIP 申請，繼續提交...');
        } else {
          // 其他錯誤，顯示錯誤訊息但繼續提交 VIP 申請
          console.error('❌ 討論區帳號建立失敗（非重複錯誤）:', forumUserError);
          showError('討論區帳號建立失敗，但 VIP 申請仍會繼續處理');
        }
      } else {
        console.log('✅ 討論區帳號建立成功:', forumUserData);
      }

      // 🔥 步驟 2：提交 VIP 申請到 Supabase
      console.log('🔥 步驟 2：開始提交 VIP 申請...');
      const { data, error } = await supabase
        .from('vip_applications')
        .insert([
          {
            application_number: applicationNumber,
            phone: formData.phone,
            selected_plan: formData.plan,
            contact_time: formData.contactTime,
            status: 'pending',
            created_at: new Date().toISOString(),
          },
        ])
        .select();

      if (error) {
        console.error('❌ VIP 申請提交失敗:', error);
        
        // 🔥 如果是電話號碼重複錯誤
        if (error.code === '23505') {
          // 查詢現有申請
          const { data: existingApp } = await supabase
            .from('vip_applications')
            .select('application_number, status')
            .eq('phone', formData.phone)
            .single();
          
          if (existingApp) {
            showError(`此電話號碼已申請過（審核編號：${existingApp.application_number}）`);
          } else {
            showError('此電話號碼已提交過申請');
          }
        } else {
          showError('提交失敗，請稍後再試');
        }
        
        setIsSubmitting(false);
        return;
      }

      console.log('✅ VIP 申請提交成功:', data);

      // 🔥 步驟 3：儲存提交狀態和審核編號
      saveSubmissionStatus(applicationNumber, {
        phone: formData.phone,
        selected_plan: formData.plan,
        contact_time: formData.contactTime
      });
      
      setIsSubmitted(true);
      setApplicationNumber(applicationNumber);
      
      // 關閉彈窗
      setShowQuickApplyModal(false);
      
      // 滾動到頁面頂部
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // 清除保存的表單草稿
      localStorage.removeItem('vip_application_draft');
      
      // 重置表單
      setFormData({
        plan: '1-3天',
        phone: '',
        contactTime: '任何時間都可以',
      });
      
      // 清除錯誤
      setFormErrors({});
      
    } catch (error) {
      console.error('❌ 提交失敗:', error);
      showError('提交失敗，請稍後再試');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 新增：獲取方案文字
  const getPlanText = (plan: string) => {
    if (!plan) return '未指定';
    
    // 🔥 修正：處理完整的方案字串
    if (plan.includes('入門方案')) return '入門方案（押金 7,000 / 額度 1 萬）';
    if (plan.includes('進階方案')) return '進階方案（押金 3.5 萬 / 額度 5 萬）⭐ 推薦';
    if (plan.includes('高端方案')) return '高端方案（押金 7 萬 / 額度 10 萬）';
    if (plan.includes('頂級方案')) return '頂級方案（押金 17.5 萬+ / 額度 25 萬+）';
    
    // 如果是舊的方案值格式
    switch (plan) {
      case 'starter': return '入門方案（押金 7,000 / 額度 1 萬）';
      case 'advanced': return '進階方案（押金 3.5 萬 / 額度 5 萬）⭐ 推薦';
      case 'premium': return '高端方案（押金 7 萬 / 額度 10 萬）';
      case 'elite': return '頂級方案（押金 17.5 萬+ / 額度 25 萬+）';
      default: return plan; // 直接返回原始值
    }
  };

  // 新增：獲取方案金額
  const getPlanValue = (plan: string) => {
    switch (plan) {
      case 'starter': return 20000;
      case 'advanced': return 100000;
      case 'premium': return 200000;
      case 'elite': return 500000;
      default: return 0;
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -100; // 增加偏移量，确保能看到「预期方案」标题
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  // 🔥 新增：scrollToForm 函數
  const scrollToForm = () => {
    scrollToSection('apply');
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 🔥 新增：滾動到表單並自動選擇方案
  const scrollToApplyWithPlan = (planValue: string) => {
    // 先設置表單數據
    setFormData(prev => ({
      ...prev,
      selected_plan: planValue
    }));
    
    // 清除該欄位的錯誤
    setFormErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.selected_plan;
      return newErrors;
    });
    
    // 🔥 修改：統一使用 -80px 偏移量，導航到抽獎橫幅位置
    const element = document.getElementById('apply');
    if (element) {
      const yOffset = -80; // 統一偏移量，對齊視窗最上方
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  // 🔥 新增：當前顯示的方案索引（用於滑動指示器）
  const [currentPlanIndex, setCurrentPlanIndex] = useState(1); // 預設顯示第 2 個（進階方案）
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // 🔥 新增：監聽滑動事件，更新指示器
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const cardWidth = container.querySelector('.plan-card')?.clientWidth || 0;
      const gap = 16; // gap-4 = 16px
      const index = Math.round(scrollLeft / (cardWidth + gap));
      setCurrentPlanIndex(index);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // 🔥 新增：點擊指示器跳轉到對應方案
  const scrollToPlan = (index: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const card = container.querySelector(`.plan-card:nth-child(${index + 1})`) as HTMLElement;
    if (card) {
      card.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  };

  // 🔥 新增：追蹤 LINE 客服點擊
  const handleLineClick = () => {
    if (pixelLoaded) {
      // 🔥 新增：檢查是否已經追蹤過
      const contentKey = 'line_click';
      if (trackedViewContent.has(contentKey)) {
        console.log('⏭️ 跳過重複追蹤: LINE 客服點擊');
        return;
      }

      console.log('🎯 LINE 客服點擊事件被觸發');
      trackViewContent('LINE 客服', '聯絡方式', {
        contact_method: 'line',
        action: 'click'
      });
      
      // 標記為已追蹤
      setTrackedViewContent(prev => new Set(prev).add(contentKey));
    }
  };

  // 🔥 新增：快速填表彈窗狀態
  const [showQuickApplyModal, setShowQuickApplyModal] = useState(false);
  
  // 🔥 新增：離開意圖挽留彈窗狀態
  const [showExitIntentModal, setShowExitIntentModal] = useState(false);
  const [exitIntentTriggered, setExitIntentTriggered] = useState(false);

  // 🔥 新增：離開意圖偵測
  useEffect(() => {
    // 如果已經提交申請，不顯示離開意圖彈窗
    if (isSubmitted) return;

    const handleMouseLeave = (e: MouseEvent) => {
      // 檢查鼠標是否離開視窗頂部
      if (e.clientY <= 0 && !exitIntentTriggered) {
        setShowExitIntentModal(true);
        setExitIntentTriggered(true);
        
        // 追蹤離開意圖事件
        trackViewContent('離開意圖挽留', 'exit_intent');
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [exitIntentTriggered, isSubmitted, trackViewContent]);

  // 🔥 修改：handleApplyClick 改為打開快速填表彈窗
  const handleApplyClick = () => {
    if (pixelLoaded) {
      const contentKey = 'apply_button_click';
      if (!trackedViewContent.has(contentKey)) {
        console.log('🎯 立即申請按鈕點擊事件被觸發');
        // 🔥 修改：改为 ViewContent，因为这只是点击按钮
        trackViewContent('首頁申請按鈕', '按鈕', {
          button_location: 'hero',
          action: 'click'
        });
        setTrackedViewContent(prev => new Set(prev).add(contentKey));
      }
    }
    
    // 🔥 打开弹窗
    setShowQuickApplyModal(true);
    
    // 🔥 新增：打开弹窗时触发 InitiateCheckout
    if (pixelLoaded && !hasInteractedWithForm) {
      setHasInteractedWithForm(true);
      console.log('🎯 用户打开快速填表弹窗');
      trackInitiateCheckout('快速填表弹窗', 'modal');
    }
  };

  // 🔥 新增：關閉快速填表彈窗
  const handleCloseQuickApplyModal = () => {
    setShowQuickApplyModal(false);
  };

  // 🔥 新增：從挽留彈窗繼續填表
  const handleContinueFromExitIntent = () => {
    setShowExitIntentModal(false);
    setShowQuickApplyModal(true);
    
    // ✅ 新增：追蹤用戶選擇繼續填表事件
    if (pixelLoaded) {
      console.log('🎯 離開意圖挽留 - 繼續填表事件被觸發');
      trackInitiateCheckout('離開意圖挽留 - 繼續填表', '按鈕', {
        action: 'continue',
        source: 'exit_intent'
      });
    }
  };

  // 🔥 新增：追蹤方案選擇
  const handlePlanSelect = (planValue: string, planName: string) => {
    // 根據方案設定對應的遊戲頻率
    const frequencyMap: { [key: string]: string } = {
      'starter': '1-3',
      'advanced': '5-10',
      'premium': '10+',
      'elite': '10+'
    };
    
    setFormData(prev => ({
      ...prev,
      selected_plan: planValue
    }));
    
    // 🔥 修正：使用正確的狀態變數名稱
    setCurrentFormStep(2);
    
    // 🔥 修改：延遲滾動，確保 DOM 更新完成，並增加偏移量讓表單更靠下
    setTimeout(() => {
      if (applicationFormRef.current) {
        const yOffset = -150; // 增加偏移量
        const y = applicationFormRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 100);
  };

  // 🔥 新增：內容名稱中英文對照
  const getChineseContentName = (englishName: string): string => {
    const nameMap: { [key: string]: string } = {
      'Philosophy Section': '經營理念',
      'VIP Plans Section': 'VIP 方案',
      'Success Cases Section': '成功案例',
      'FAQ Section': '常見問題',
      'Application Form Section': '申請表單',
      'Hero CTA Button': '首頁申請按鈕',
      'LINE Customer Service': 'LINE 客服',
      'Phone Number Click': '電話號碼點擊',
      'Plan Selected': '方案選擇',
      'VIP Application Form': 'VIP 申請表單',
      'Scroll Depth': '滾動深度',
      'section': '區域',
      'button': '按鈕',
      'contact': '聯絡方式',
      'plan': '方案',
      'form': '表單',
      'faq': '常見問題',
      'success_case': '成功案例'
    };

    // 如果是完全匹配，直接返回
    if (nameMap[englishName]) {
      return nameMap[englishName];
    }

    // 如果包含某些關鍵字，進行部分替換
    let chineseName = englishName;
    
    // 替換常見的英文詞彙
    chineseName = chineseName.replace(/Philosophy Section/g, '經營理念');
    chineseName = chineseName.replace(/VIP Plans Section/g, 'VIP 方案');
    chineseName = chineseName.replace(/Success Cases Section/g, '成功案例');
    chineseName = chineseName.replace(/FAQ Section/g, '常見問題');
    chineseName = chineseName.replace(/Application Form Section/g, '申請表單');
    chineseName = chineseName.replace(/Section/g, '區域');
    chineseName = chineseName.replace(/Scroll Depth/g, '滾動深度');
    chineseName = chineseName.replace(/Plan Selected/g, '方案選擇');
    chineseName = chineseName.replace(/Success Case/g, '成功案例');
    chineseName = chineseName.replace(/FAQ/g, '常見問題');
    
    return chineseName;
  };

  // 🔥 新增：3 秒測試狀態
  const [quickTestSelection, setQuickTestSelection] = useState<string>('');

  // 🔥 新增：處理 3 秒測試選擇
  const handleQuickTestSelect = (frequency: string) => {
    setQuickTestSelection(frequency);
    
    // 根據頻率推薦方案
    let recommendedPlan = 'starter';
    if (frequency === 'frequent') {
      recommendedPlan = 'advanced'; // 每月玩 5-10 次
    } else if (frequency === 'very_frequent') {
      recommendedPlan = 'premium'; // 每月玩 10 次以上
    } else if (frequency === 'ultra_frequent') {
      recommendedPlan = 'elite'; // 每月玩 15 次以上
    }
    
    // 設置表單數據
    setFormData(prev => ({
      ...prev,
      selected_plan: recommendedPlan
    }));
    
    // 清除該欄位的錯誤
    setFormErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.selected_plan;
      return newErrors;
    });
    
    // ✅ 新增：追蹤 3 秒測試選擇事件
    if (pixelLoaded) {
      console.log('🎯 3 秒測試選擇事件被觸發:', frequency);
      trackViewContent(
        `3秒測試 - ${frequency}`,
        '互動測試',
        {
          frequency,
          recommended_plan: recommendedPlan,
          action: 'select'
        }
      );
    }
    
    // 延遲 500ms 後滾動到表單（讓用戶看到推薦結果）
    setTimeout(() => {
      scrollToSection('apply');
    }, 500);
  };

  // 🔥 新增：獲取推薦方案文字
  const getRecommendedPlanText = (frequency: string) => {
    switch (frequency) {
      case 'casual':
        return '入門方案（押金 7,000 / 額度 1 萬）';
      case 'frequent':
        return '進階方案（押金 3.5 萬 / 額度 5 萬）⭐ 推薦';
      case 'very_frequent':
        return '高端方案（押金 7 萬 / 額度 10 萬）';
      case 'ultra_frequent':
        return '頂級方案（押金 17.5 萬+ / 額度 25 萬+）';
      default:
        return '';
    }
  };

  // 🔥 新增：快速申请表单状态
  const [quickFormData, setQuickFormData] = useState({
    phone: ''
  });
  const [quickFormError, setQuickFormError] = useState('');
  const [isQuickSubmitting, setIsQuickSubmitting] = useState(false);

  // 🔥 新增：快速表单提交
  const handleQuickSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 🆕 新增：檢查是否已經提交過
    if (isSubmitted) {
      setErrorMessage('您已經提交過申請了！如需修改，請聯繫客服。');
      setShowErrorModal(true);
      return;
    }
    
    // 验证电话号码
    if (!quickFormData.phone.trim()) {
      setQuickFormError('请输入手机号码');
      return;
    }
    
    if (!validatePhone(quickFormData.phone)) {
      setQuickFormError('请输入正确的手机号码格式(09XXXXXXXX)');
      return;
    }
    
    // 检查是否已存在
    const exists = await checkPhoneExists(quickFormData.phone);
    if (exists) {
      setQuickFormError('此电话号码已提交过申请，请勿重复申请');
      return;
    }
    
    setIsQuickSubmitting(true);
    
    try {
      const newApplicationId = generateApplicationId();
      setApplicationId(newApplicationId);

      // 提交到 Supabase
      try {
        const { error } = await supabase
          .from('vip_applications')
          .insert([{
            phone: quickFormData.phone,
            line_id: null,
            call_time: '任何时间都可以',
            selected_plan: 'advanced', // 默认推荐进阶方案
            application_number: newApplicationId,
            notes: '快速申请（Hero 区域）'
          }]);

        if (error) {
          console.log('ℹ️ Supabase 提交失败，但仍显示成功（演示模式）');
        } else {
          console.log('✅ 快速申请已成功提交到 Supabase');
        }
      } catch (dbError) {
        console.log('ℹ️ 数据库连接失败，但仍显示成功（演示模式）');
      }

      // 🆕 新增：儲存提交狀態
      saveSubmissionStatus(newApplicationId, {
        phone: quickFormData.phone,
        selected_plan: 'advanced',
        contact_time: '任何时间都可以',
        line_id: ''
      });

      // 追踪事件
      trackLead(
        'VIP Quick Application - Hero', 
        100000,
        {
          plan: 'advanced',
          source: 'hero_quick_form',
          application_id: newApplicationId,
          currency: 'TWD'
        }
      );

      // 清除保存的表单
      localStorage.removeItem('vip_application_draft');

      setShowSuccessModal(true);
      
      // 重置表单
      setQuickFormData({ phone: '' });
      setQuickFormError('');

      await loadRealApplicationData();
    } catch (error: any) {
      console.error('❌ 提交失败:', error);
      setErrorMessage('提交失败，请稍后再试或直接联系 LINE 客服');
      setShowErrorModal(true);
    } finally {
      setIsQuickSubmitting(false);
    }
  };

  // 🔥 新增：Hero 快速申请表单 - 添加 onFocus 事件
  const handleQuickFormFocus = () => {
    if (pixelLoaded && !hasInteractedWithForm) {
      setHasInteractedWithForm(true);
      console.log('🎯 用户开始填写 Hero 快速表单');
      trackInitiateCheckout('Hero 快速申请表单', 'form');
    }
  };

  // 🆕 新增：檢查提交狀態
  const checkSubmissionStatus = () => {
    try {
      const saved = localStorage.getItem('vip_application_submitted');
      if (saved) {
        const data = JSON.parse(saved);
        // 🔥 修改：檢查是否在 30 天內提交過，並且驗證後端是否還存在該申請
        const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;
        if (Date.now() - data.submittedAt < thirtyDaysInMs) {
          console.log('✅ 檢測到已提交的申請:', data);
          return data;
        } else {
          // 超過 30 天，清除舊數據
          console.log('ℹ️ 申請已超過 30 天，清除舊數據');
          localStorage.removeItem('vip_application_submitted');
        }
      }
    } catch (error) {
      console.error('❌ 檢查提交狀態失敗:', error);
    }
    return null;
  };

  // 🔥 新增：驗證後端申請是否存在
  const verifyApplicationExists = async (applicationId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('vip_applications')
        .select('id')
        .eq('application_number', applicationId)
        .limit(1);

      if (error) {
        console.log('ℹ️ 無法驗證申請狀態，保持本地狀態');
        return true; // 如果無法驗證，保持本地狀態
      }

      return data && data.length > 0;
    } catch (error) {
      console.log('ℹ️ 驗證申請時發生錯誤，保持本地狀態');
      return true; // 如果發生錯誤，保持本地狀態
    }
  };

  // 🆕 新增：儲存提交狀態
  const saveSubmissionStatus = (applicationId: string, formData: any) => {
    try {
      const submissionData = {
        submitted: true,
        applicationId: applicationId,
        submittedAt: Date.now(),
        formData: {
          phone: formData.phone,
          selected_plan: formData.selected_plan,
          contact_time: formData.contact_time,
          line_id: formData.line_id || ''
        }
      };
      localStorage.setItem('vip_application_submitted', JSON.stringify(submissionData));
      console.log('✅ 提交狀態已儲存:', applicationId);
    } catch (error) {
      console.error('❌ 儲存提交狀態失敗:', error);
    }
  };

  // 🆕 新增：頁面載入時檢查提交狀態
  useEffect(() => {
    const checkAndVerifySubmission = async () => {
      const saved = checkSubmissionStatus();
      if (saved) {
        // 🔥 新增：驗證後端是否還存在該申請
        const exists = await verifyApplicationExists(saved.applicationId);
        
        if (exists) {
          // 申請還存在，顯示已提交狀態
          setIsSubmitted(true);
          setSavedApplication(saved);
          setApplicationNumber(saved.applicationId || '');
          console.log('📋 已提交的申請資料:', saved);
          
          // 🔥 修改：不再自動顯示成功彈窗
          // 移除自動顯示彈窗的邏輯，只在抽完紅包後才顯示
          console.log('ℹ️ 已提交申請，但不顯示彈窗（只在抽紅包後顯示）');
        } else {
          // 🔥 申請已被刪除，清除本地狀態
          console.log('⚠️ 申請已被刪除，清除本地狀態');
          localStorage.removeItem('vip_application_submitted');
          localStorage.removeItem('vip_success_modal_shown');
          setIsSubmitted(false);
          setSavedApplication(null);
          setApplicationNumber('');
          
          // 🔥 重置表單
          setFormData({
            plan: '進階方案 ⭐推薦（押金 3.5 萬 / 額度 5 萬）',
            phone: '',
            contactTime: '任何時間都可以'
          });
        }
      } else {
        console.log('ℹ️ 尚未提交申請');
        // 🔥 確保清除提交狀態
        setIsSubmitted(false);
        setSavedApplication(null);
        setApplicationNumber('');
      }
    };

    checkAndVerifySubmission();
  }, []);

  // 🆕 打開快速申請彈窗
  const handleOpenQuickApplyModal = () => {
    setShowQuickApplyModal(true);
    // 🆕 如果已提交，載入現有資料
    if (isSubmitted && existingApplication) {
      setQuickFormData({
        phone: existingApplication.phone || '',
        selectedPlan: existingApplication.selected_plan || '',
        contactTime: existingApplication.contact_time || '',
        lineId: existingApplication.line_id || ''
      });
    } else {
      // 清空表單
      setQuickFormData({
        phone: '',
        selectedPlan: '',
        contactTime: '',
        lineId: ''
      });
    }
  };

  // 🔥 新增：使用 Toast Hook
  const { showSuccess, showError, showWarning, showInfo, ToastContainer } = useToast();

  // 🔥 新增：錯過會怎樣的情緒觸發狀態
  const [showUrgencyModal, setShowUrgencyModal] = useState(false);
  const [timeLeft, setTimeLeft] = useState(24 * 3600); // 添加倒數計時狀態（24小時）

  // 🔥 新增：30 秒後顯示緊迫感彈窗
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isSubmitted) {
        setShowUrgencyModal(true);
      }
    }, 60000); // 60 秒 = 1 分鐘

    return () => clearTimeout(timer);
  }, [isSubmitted]);

  // 倒數計時器
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 0) return 24 * 3600; // 重置為24小時
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // 格式化時間顯示
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // 🔥 新增：缺少的狀態變數
  const [remainingSlots, setRemainingSlots] = useState(12);
  const [pixelConfig, setPixelConfig] = useState<any>(null);
  const [todayEvents, setTodayEvents] = useState<{ [key: string]: number }>({});
  const [debugEvents, setDebugEvents] = useState<any[]>([]);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formSubmitUrl, setFormSubmitUrl] = useState('');
  const [existingApplication, setExistingApplication] = useState<any>(null);

  // 🔥 新增：清除調試事件函數
  const clearDebugEvents = () => {
    setDebugEvents([]);
  };

  // 🔥 新增：即時社交證明通知狀態
  const [showSocialProof, setShowSocialProof] = useState(false);
  const [socialProofMessage, setSocialProofMessage] = useState('');

  // 🔥 新增：緊迫感計時器（更突出）
  const [urgencyTimer, setUrgencyTimer] = useState(15 * 60); // 15 分鐘

  // 🔥 新增：表單進度狀態
  const [formProgress, setFormProgress] = useState(0);

  // 🔥 新增：手機版立即顯示底部 CTA
  const [showMobileCTA, setShowMobileCTA] = useState(false);

  // 🔥 新增：離開意圖偵測
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [hasShownExitPopup, setHasShownExitPopup] = useState(false);

  // 🔥 新增：手機版檢測
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      // 手機版 3 秒後顯示底部 CTA
      setTimeout(() => {
        setShowMobileCTA(true);
      }, 3000);
    }
  }, []);

  // 🔥 新增：即時社交證明通知
  useEffect(() => {
    const showRandomSocialProof = () => {
      const messages = [
        '🎉 台北陳先生剛剛提交了申請',
        '✅ 高雄林小姐的申請已通過審核',
        '💰 台中王先生剛完成 15 萬提領',
        '🔥 新竹張總剛加入 VIP 會員',
        '⭐ 台南劉老闆給予 5 星好評'
      ];
      
      const randomMessage = messages[Math.floor(Math.random() * messages.length)];
      setSocialProofMessage(randomMessage);
      setShowSocialProof(true);
      
      setTimeout(() => {
        setShowSocialProof(false);
      }, 5000);
    };

    // 每 15-30 秒顯示一次
    const interval = setInterval(() => {
      showRandomSocialProof();
    }, Math.random() * 15000 + 15000);

    return () => clearInterval(interval);
  }, []);

  // 🔥 新增：緊迫感倒數計時
  useEffect(() => {
    const timer = setInterval(() => {
      setUrgencyTimer(prev => {
        if (prev <= 0) return 15 * 60; // 重置
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // 🔥 新增：離開意圖偵測（手機版）
  useEffect(() => {
    let touchStartY = 0;
    let touchEndY = 0;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchMove = (e: TouchEvent) => {
      touchEndY = e.touches[0].clientY;
    };

    const handleTouchEnd = () => {
      // 向上滑動超過 100px 且在頁面頂部
      if (
        touchStartY - touchEndY > 100 &&
        window.scrollY < 50 &&
        !isSubmitted &&
        !localStorage.getItem('exitPopupShown')
      ) {
        setShowExitPopup(true);
        localStorage.setItem('exitPopupShown', 'true');
      }
    };

    const handleMouseLeave = (e: MouseEvent) => {
      if (
        e.clientY <= 0 &&
        !isSubmitted &&
        !localStorage.getItem('exitPopupShown')
      ) {
        setShowExitPopup(true);
        localStorage.setItem('exitPopupShown', 'true');
      }
    };

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isSubmitted]);

  // 🔥 新增：表單進度計算
  useEffect(() => {
    let progress = 0;
    if (formData.plan) progress += 33;
    if (formData.phone && validatePhone(formData.phone)) progress += 34;
    if (formData.contactTime) progress += 33;
    setFormProgress(progress);
  }, [formData]);

  // 🔥 新增：格式化倒數時間
  const formatUrgencyTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // 🔥 新增：缺少的狀態變數
  const [weeklyWinners, setWeeklyWinners] = useState(getCurrentWeekWinners());

  // 🔥 新增：即時提領通知數據
  const [withdrawalNotifications, setWithdrawalNotifications] = useState([
    { id: 1, user: '台北 陳先生', amount: '15 萬', time: '3 分鐘前', status: '已到帳' },
    { id: 2, user: '高雄 林小姐', amount: '8 萬', time: '7 分鐘前', status: '已到帳' },
    { id: 3, user: '台中 王先生', amount: '12 萬', time: '12 分鐘前', status: '已到帳' },
    { id: 4, user: '新竹 張總', amount: '20 萬', time: '18 分鐘前', status: '已到帳' },
    { id: 5, user: '台南 劉老闆', amount: '6 萬', time: '25 分鐘前', status: '已到帳' }
  ]);

  // 🔥 新增：當前顯示的提領通知索引
  const [currentWithdrawalIndex, setCurrentWithdrawalIndex] = useState(0);

  // 🔥 新增：今日已出金總額（動態增長）
  const [todayWithdrawal, setTodayWithdrawal] = useState(3500000);

  // 🔥 新增：每 5 秒切換提領通知
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWithdrawalIndex((prev) => (prev + 1) % withdrawalNotifications.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [withdrawalNotifications.length]);

  // 🔥 新增：今日出金金額動態增長
  useEffect(() => {
    const interval = setInterval(() => {
      setTodayWithdrawal(prev => {
        const increment = Math.floor(Math.random() * 50000) + 10000;
        return prev + increment;
      });
    }, 8000);

    return () => clearInterval(interval);
  }, []);

  // 🔥 新增：每週一自動更新贏家數據
  useEffect(() => {
    // 檢查是否需要更新數據
    const checkAndUpdateWinners = () => {
      const newWinners = getCurrentWeekWinners();
      // 比較第一個贏家的名字，如果不同則更新
      if (newWinners[0].name !== weeklyWinners[0].name) {
        setWeeklyWinners(newWinners);
        console.log('✅ 本週贏家榜已更新');
      }
    };

    // 立即檢查一次
    checkAndUpdateWinners();

    // 每天檢查一次（在凌晨 0:05 檢查）
    const now = new Date();
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 5, 0);
    const timeUntilCheck = tomorrow.getTime() - now.getTime();

    const timer = setTimeout(() => {
      checkAndUpdateWinners();
      // 之後每 24 小時檢查一次
      setInterval(checkAndUpdateWinners, 24 * 60 * 60 * 1000);
    }, timeUntilCheck);

    return () => clearTimeout(timer);
  }, [weeklyWinners]);

  // 🆕 新增：檢查是否已提交申請
  useEffect(() => {
    const submitted = localStorage.getItem('vip_application_submitted');
    if (submitted === 'true') {
      setIsSubmitted(true);
      // 🆕 新增：從 localStorage 讀取申請資料
      const savedData = localStorage.getItem('vip_application_data');
      if (savedData) {
        try {
          const data = JSON.parse(savedData);
          setSubmittedData(data);
        } catch (error) {
          console.error('Failed to parse saved data:', error);
        }
      }
    }
  }, []);

  // 🆕 新增：清除申請狀態的函數
  const clearApplicationStatus = () => {
    localStorage.removeItem('vip_application_submitted');
    localStorage.removeItem('vip_application_data');
    localStorage.removeItem('vip_success_shown');
    setIsSubmitted(false);
    setSubmittedData(null);
    setFormData({
      phone: '',
      plan: '進階方案 ⭐推薦',
      contactTime: '任何時間都可以'
    });
  };

  // 🔥 新增：抽红包状态
  const [showLuckyDraw, setShowLuckyDraw] = useState(false);
  const [luckyAmount, setLuckyAmount] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [selectedEnvelope, setSelectedEnvelope] = useState<number | null>(null);
  const [revealedEnvelopes, setRevealedEnvelopes] = useState<boolean[]>([false, false, false, false, false, false, false]);
  const [drawnAmount, setDrawnAmount] = useState<number | null>(null);

  // 🧧 紅包金額設定（新的獎項：10000X4 / 20000X5 / 30000X4）
  const baseRedEnvelopeAmounts = [
    10000, 10000, 10000, 10000,  // 10000 x 4
    20000, 20000, 20000, 20000, 20000,  // 20000 x 5
    30000, 30000, 30000, 30000   // 30000 x 4
  ];

  // 🔥 新增：洗牌函數（Fisher-Yates 演算法）
  const shuffleArray = (array: number[]) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // 🔥 新增：每次打開抽獎彈窗時隨機洗牌
  const [redEnvelopeAmounts, setRedEnvelopeAmounts] = useState(shuffleArray(baseRedEnvelopeAmounts));

  // 🔥 新增：紅包狀態陣列
  const [redEnvelopes, setRedEnvelopes] = useState(
    redEnvelopeAmounts.map((amount, index) => ({
      id: index,
      amount: amount,
      isOpened: false
    }))
  );

  // 🔥 修改：當顯示抽獎彈窗時，重新洗牌
  useEffect(() => {
    if (showLuckyDraw && !hasDrawn) {
      const shuffled = shuffleArray(baseRedEnvelopeAmounts);
      setRedEnvelopeAmounts(shuffled);
      setRedEnvelopes(
        shuffled.map((amount, index) => ({
          id: index,
          amount: amount,
          isOpened: false
        }))
      );
    }
  }, [showLuckyDraw]);

  // 🔥 新增：處理紅包點擊
  const handleDrawEnvelope = async (index: number) => {
    if (hasDrawn || isDrawing) return;
    
    setIsDrawing(true);
    setSelectedEnvelope(index);
    
    // 動畫效果
    setTimeout(async () => {
      const newEnvelopes = [...redEnvelopes];
      newEnvelopes[index].isOpened = true;
      setRedEnvelopes(newEnvelopes);
      const amount = redEnvelopeAmounts[index];
      setDrawnAmount(amount);
      setHasDrawn(true);
      setIsDrawing(false);
      
      // 🔥 修正：使用 application_number 來更新記錄，更可靠
      try {
        console.log('💾 開始儲存紅包金額到資料庫...');
        console.log('📱 電話號碼:', formData.phone);
        console.log('🎫 審核編號:', applicationNumber);
        console.log('💰 紅包金額:', amount);
        
        // 🔥 優先使用 application_number，如果沒有則使用 phone
        const updateCondition = applicationNumber 
          ? { application_number: applicationNumber }
          : { phone: formData.phone };
        
        console.log('🔍 更新條件:', updateCondition);
        
        const { data, error } = await supabase
          .from('vip_applications')
          .update({
            red_envelope_amount: amount,
            red_envelope_opened_at: new Date().toISOString()
          })
          .match(updateCondition)
          .select();

        if (error) {
          console.error('❌ 儲存紅包金額失敗:', error);
          console.error('錯誤詳情:', JSON.stringify(error, null, 2));
        } else {
          console.log('✅ 紅包金額已成功儲存到資料庫');
          console.log('📊 更新結果:', data);
          // 🔥 更新本地狀態
          setRedEnvelopeAmount(amount);
        }
      } catch (err) {
        console.error('❌ 儲存紅包金額時發生錯誤:', err);
      }
    }, 1000);
  };

  // 🔥 新增：重置抽獎狀態
  const resetLuckyDraw = () => {
    setShowLuckyDraw(false);
    setLuckyAmount(0);
    setIsDrawing(false);
    setHasDrawn(false);
    setSelectedEnvelope(null);
    setDrawnAmount(null);
    // 重新洗牌
    const shuffled = shuffleArray(baseRedEnvelopeAmounts);
    setRedEnvelopeAmounts(shuffled);
    setRedEnvelopes(
      shuffled.map((amount, index) => ({
        id: index,
        amount: amount,
        isOpened: false
      }))
    );
    
    // 🔥 修改：關閉抽獎彈窗後，顯示成功彈窗
    setShowSuccessModal(true);
  };

  // 🎁 處理紅包點擊
  const handleRedEnvelopeClick = async (amount: number) => {
    if (isRedEnvelopeOpened) return;
    
    setIsRedEnvelopeOpened(true);
    setSelectedAmount(amount);

    // 🎉 顯示中獎動畫
    setTimeout(() => {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
    }, 500);

    // 💾 記錄抽到的紅包金額到 Supabase
    try {
      const { error } = await supabase
        .from('vip_applications')
        .update({ 
          red_envelope_amount: amount,
          red_envelope_opened_at: new Date().toISOString()
        })
        .eq('phone', submittedPhone);

      if (error) {
        console.error('記錄紅包金額失敗:', error);
      }
    } catch (err) {
      console.error('記錄紅包金額錯誤:', err);
    }

    // 🔄 3秒後重新整理頁面
    setTimeout(() => {
      window.location.reload();
    }, 3000);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-[#FFD700] border-t-transparent rounded-full animate-spin"></div>
          <div className="text-[#FFD700] text-2xl">載入中...</div>
          <div className="text-gray-400 text-sm mt-2">正在準備頁面內容</div>
        </div>
      </div>
    );
  }

  // 🔥 新增：數據卡片資料
  const dataCards = [
    {
      icon: 'ri-user-star-line',
      value: realApplicationData.monthlyTotal,
      label: '本月服務 VIP 會員',
      color: 'from-[#FFD700] to-[#D4AF37]'
    },
    {
      icon: 'ri-time-line',
      value: '30 分鐘',
      label: '平均出金時間',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: 'ri-thumb-up-line',
      value: '92%',
      label: '會員滿意度',
      color: 'from-green-500 to-green-600'
    }
  ];

  // 🔥 新增：顯示的 FAQ 數量
  const displayedFAQs = showAllFAQs ? faqs : faqs.slice(0, 5);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Toast Container */}
      <ToastContainer />

      {/* 🔥 新增：调试面板 - 只在开发环境显示 */}
      {showDebugPanel && (
        <div className="fixed bottom-24 left-4 z-50 w-96 max-h-[600px] bg-black/95 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl overflow-hidden">
          <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-purple-500 p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="ri-bug-line text-xl text-white"></i>
              <h3 className="text-white font-bold">Facebook Pixel 追蹤調試</h3>
            </div>
            <button
              onClick={() => setShowDebugPanel(false)}
              className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center hover:bg-white/30 transition-all cursor-pointer"
            >
              <i className="ri-close-line text-white"></i>
            </button>
          </div>

          <div className="p-4 overflow-y-auto max-h-[500px]">
            {/* Pixel 狀態 */}
            <div className="mb-4 p-3 bg-white/5 rounded-lg border border-white/10">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">Pixel 狀態</span>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${pixelLoaded ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                  <span className={`text-xs font-semibold ${pixelLoaded ? 'text-green-400' : 'text-red-400'}`}>
                    {pixelLoaded ? '運行中' : '未載入'}
                  </span>
                </div>
              </div>
              {pixelConfig && (
                <div className="text-xs text-gray-500 font-mono">
                  ID: {pixelConfig.pixel_id}
                </div>
              )}
            </div>

            {/* 今日事件統計 */}
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                <i className="ri-bar-chart-line text-purple-400"></i>
                今日事件統計
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(todayEvents).map(([eventName, count]) => (
                  <div key={eventName} className="p-2 bg-white/5 rounded-lg border border-white/10">
                    <div className="text-xs text-gray-400 mb-1">{eventName}</div>
                    <div className="text-lg font-bold text-white">{count}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* 最近事件 */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                  <i className="ri-time-line text-purple-400"></i>
                  最近事件
                </h4>
                <button
                  onClick={clearDebugEvents}
                  className="text-xs text-red-400 hover:text-red-300 transition-colors cursor-pointer"
                >
                  清空
                </button>
              </div>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {debugEvents.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-sm">
                    <i className="ri-inbox-line text-3xl mb-2"></i>
                    <div>暫無事件記錄</div>
                  </div>
                ) : (
                  debugEvents.map((event, index) => (
                    <div key={index} className="p-3 bg-white/5 rounded-lg border border-white/10 hover:bg-white/10 transition-all">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-xs font-semibold px-2 py-1 rounded ${
                          event.name === 'PageView' ? 'bg-purple-500/20 text-purple-400' :
                          event.name === 'ViewContent' ? 'bg-blue-500/20 text-blue-400' :
                          event.name === 'InitiateCheckout' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-green-500/20 text-green-400'
                        }`}>
                          {event.name}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(event.time).toLocaleTimeString('zh-TW')}
                        </span>
                      </div>
                      {event.data && Object.keys(event.data).length > 0 && (
                        <div className="text-xs text-gray-400 font-mono bg-black/30 p-2 rounded mt-2 overflow-x-auto">
                          {JSON.stringify(event.data, null, 2)}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 🔥 新增：即時社交證明通知 */}
      {showSocialProof && (
        <div className="fixed top-24 right-4 z-50 animate-slide-in-right">
          <div className="bg-gradient-to-r from-green-500/90 to-emerald-500/90 backdrop-blur-md rounded-xl px-4 py-3 shadow-2xl border border-green-400/50 max-w-xs">
            <p className="text-white text-sm font-medium flex items-center gap-2">
              <i className="ri-notification-badge-fill text-lg"></i>
              {socialProofMessage}
            </p>
          </div>
        </div>
      )}

      {/* 導航列 - 優化手機版觸控區域 */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-black/90 backdrop-blur-md shadow-2xl' : 'bg-transparent'}`}>
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" aria-label="主導航">
          <div className="flex items-center justify-between h-20 sm:h-20 md:h-16">
            {/* LOGO - 🔥 優化：增大手機版尺寸 */}
            <h1 className="flex items-center gap-2 sm:gap-2 cursor-pointer touch-manipulation" onClick={() => scrollToTop()}>
              <div className="w-12 h-12 sm:w-12 sm:h-12 md:w-12 md:h-12 rounded-full overflow-hidden flex items-center justify-center bg-white/5">
                <LazyImage
                  src={settings?.logo_url || "https://static.readdy.ai/image/8802434771b975039c88ac015f580732/aca501978f09f0ee060a0930ef0cab86.png"}
                  alt="火星人娛樂城 LOGO"
                  className="w-full h-full"
                  objectFit="contain"
                  priority={true}
                />
              </div>
              <div>
                <a href="/" className="text-xl sm:text-lg md:text-lg font-bold bg-gradient-to-r from-[#D4AF37] to-[#FFD700] bg-clip-text text-transparent hover:from-[#FFD700] hover:to-[#D4AF37] transition-all">{settings?.brand_name || '火星人娛樂城'}</a>
                <p className="text-base sm:text-sm text-gray-400 hidden sm:block">{settings?.brand_subtitle || 'VIP 尊榮區域'}</p>
              </div>
            </h1>

            {/* 電腦版選單 */}
            <div className="hidden md:flex items-center gap-4 lg:gap-6 xl:gap-8">
              <a href="#philosophy" onClick={(e) => { e.preventDefault(); scrollToSection('philosophy'); }} className="text-base lg:text-base xl:text-base text-gray-300 hover:text-[#FFD700] transition-colors whitespace-nowrap">經營理念</a>
              <a href="#plans" onClick={(e) => { e.preventDefault(); scrollToSection('plans'); }} className="text-base lg:text-base xl:text-base text-gray-300 hover:text-[#FFD700] transition-colors whitespace-nowrap">VIP 方案</a>
              <a href="#cases" onClick={(e) => { e.preventDefault(); scrollToSection('cases'); }} className="text-base lg:text-base xl:text-base text-gray-300 hover:text-[#FFD700] transition-colors whitespace-nowrap">成功案例</a>
              <a href="#faq" onClick={(e) => { e.preventDefault(); scrollToSection('faq'); }} className="text-base lg:text-base xl:text-base text-gray-300 hover:text-[#FFD700] transition-colors whitespace-nowrap">常見問題</a>
              <a 
                href="https://www.08777.net/mobile/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-base lg:text-base xl:text-base text-gray-300 hover:text-[#FFD700] transition-colors whitespace-nowrap flex items-center gap-1"
              >
                遊戲平台
                <i className="ri-external-link-line text-xs"></i>
              </a>
              <a 
                href="#apply"
                onClick={(e) => { e.preventDefault(); scrollToSection('apply'); }}
                className="px-6 lg:px-6 xl:px-6 py-3 lg:py-3 xl:py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-black text-base sm:text-base font-bold rounded-full hover:from-[#FFD700] hover:to-[#D4AF37] transition-all duration-300 transform hover:scale-105 active:scale-95 whitespace-nowrap touch-manipulation"
              >
                立即申請
              </a>
            </div>

            {/* 手機版漢堡選單按鈕 - 🔥 優化：增大觸控區域 */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden min-w-[56px] min-h-[56px] flex items-center justify-center touch-manipulation active:scale-95 transition-transform"
              aria-label="開啟選單"
              aria-expanded={mobileMenuOpen}
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-4xl sm:text-4xl text-white`}></i>
            </button>
          </div>
        </nav>
      </header>

      {/* 手機版滑出式側邊欄 - 🔥 優化：增大文字和觸控區域 */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        >
          <div 
            className="absolute right-0 top-0 h-full w-80 bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col h-full">
              {/* 關閉按鈕 */}
              <div className="flex justify-end p-6">
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="min-w-[56px] min-h-[56px] flex items-center justify-center touch-manipulation active:scale-95 transition-transform"
                  aria-label="關閉選單"
                >
                  <i className="ri-close-line text-4xl text-white"></i>
                </button>
              </div>

              {/* 選單項目 */}
              <nav className="flex-1 px-6 py-4 space-y-3 overflow-y-auto">
                <a
                  href="#philosophy"
                  onClick={(e) => { e.preventDefault(); scrollToSection('philosophy'); setMobileMenuOpen(false); }}
                  className="block text-white text-xl font-medium py-3 hover:text-[#FFD771] transition-colors cursor-pointer"
                >
                  經營理念
                </a>
                <a
                  href="#plans"
                  onClick={(e) => { e.preventDefault(); scrollToSection('plans'); setMobileMenuOpen(false); }}
                  className="block text-white text-xl font-medium py-3 hover:text-[#FFD771] transition-colors cursor-pointer"
                >
                  會員方案
                </a>
                <a
                  href="#testimonials"
                  onClick={(e) => { e.preventDefault(); scrollToSection('testimonials'); setMobileMenuOpen(false); }}
                  className="block text-white text-xl font-medium py-3 hover:text-[#FFD771] transition-colors cursor-pointer"
                >
                  成功案例
                </a>
                <a
                  href="#faq"
                  onClick={(e) => { e.preventDefault(); scrollToSection('faq'); setMobileMenuOpen(false); }}
                  className="block text-white text-xl font-medium py-3 hover:text-[#FFD771] transition-colors cursor-pointer"
                >
                  常見問題
                </a>

                {/* 分隔線 */}
                <div className="border-t border-white/20 my-4"></div>

                {/* 外部連結 */}
                <a
                  href="https://www.08777.net/mobile/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between text-white text-xl font-medium py-3 hover:text-[#FFD771] transition-colors cursor-pointer"
                >
                  <span>遊戲平台</span>
                  <i className="ri-external-link-line text-lg"></i>
                </a>

                {/* 分隔線 */}
                <div className="border-t border-white/20 my-4"></div>

                {/* LINE 客服 */}
                <a
                  href="https://line.me/ti/p/YOUR_LINE_ID"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-[#06C755] text-xl font-medium py-3 hover:text-[#05B04C] transition-colors cursor-pointer"
                >
                  <i className="ri-line-fill text-2xl"></i>
                  <span>LINE 客服</span>
                </a>
              </nav>

              {/* CTA 按鈕 */}
              <div className="p-8">
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    scrollToForm();
                  }}
                  className="w-full min-h-[64px] bg-gradient-to-r from-[#BF9B30] to-[#FFD771] text-black text-xl font-bold rounded-xl hover:shadow-lg hover:shadow-[#FFD771]/30 active:scale-95 transition-all cursor-pointer whitespace-nowrap touch-manipulation"
                >
                  立即申請 VIP
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 🔥 新增：保存進度提示框 */}
      {showSavedFormPrompt && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-50 max-w-md w-full mx-4 animate-slideDown">
          <div className="bg-gradient-to-r from-[#FFD700]/20 to-[#D4AF37]/10 backdrop-blur-md rounded-2xl p-4 sm:p-6 border-2 border-[#FFD700]/50 shadow-2xl shadow-[#FFD700]/30 animate-pulse">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-[#FFD700] to-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-file-list-line text-xl sm:text-2xl text-black"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-base sm:text-lg font-bold text-white mb-2">檢測到未完成的申請</h3>
                <p className="text-xs sm:text-sm text-gray-300 mb-4">您上次填寫的資料還在，要繼續填寫嗎？</p>
                <div className="flex gap-2 sm:gap-3">
                  <button
                    onClick={restoreSavedForm}
                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-black text-sm sm:text-base font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all duration-300"
                  >
                    繼續填寫
                  </button>
                  <button
                    onClick={clearSavedForm}
                    className="flex-1 px-4 py-2.5 bg-white/10 border border-white/20 text-white text-sm sm:text-base font-bold rounded-lg hover:bg-white/20 transition-all duration-300"
                  >
                    重新開始
                  </button>
                </div>
              </div>
              <button
                onClick={() => setShowSavedFormPrompt(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors flex-shrink-0"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 主要內容區域 */}
      <main>
        {/* 1. Hero 區域（保持） */}
        <section className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 md:px-8 pt-24 sm:pt-24 md:pt-20 overflow-hidden" aria-labelledby="hero-title">
          {/* 🔥 動態背景層 */}
          <div className="absolute inset-0" aria-hidden="true">
            {/* 漸變背景 */}
            <div className="absolute inset-0 bg-gradient-radial from-[#D4AF37]/20 via-transparent to-transparent"></div>
            
            {/* 粒子效果 */}
            <div className="absolute inset-0">
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-1 h-1 bg-[#FFD700] rounded-full animate-float"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 5}s`,
                    animationDuration: `${5 + Math.random() * 10}s`,
                    opacity: Math.random() * 0.5 + 0.3,
                  }}
                ></div>
              ))}
            </div>
            
            {/* 光暈效果 */}
            <div className="absolute top-1/4 left-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-[#FFD700]/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-1/4 right-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-[#D4AF37]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          </div>

          <div className="relative z-10 max-w-5xl mx-auto text-center">
            {/* LOGO - 🔥 縮小尺寸 */}
            <div className="mb-4 sm:mb-4 flex justify-center">
              <div className="relative animate-float">
                <div className="absolute inset-0 w-16 h-16 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full bg-[#FFD700]/40 blur-2xl animate-pulse" aria-hidden="true"></div>
                <div className="relative w-16 h-16 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full overflow-hidden flex items-center justify-center shadow-2xl shadow-[#FFD700]/50 bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 hover:scale-110 transition-transform duration-500">
                  <LazyImage
                    src={settings?.logo_url || "https://static.readdy.ai/image/8802434771b975039c88ac015f580732/aca501978f09f0ee060a0930ef0cab86.png"}
                    alt="火星人娛樂城官方 LOGO - 台灣最受信賴的 VIP 信用版老虎機娛樂城，提供超過 500+ 款老虎機遊戲、快速出金服務、24 小時專屬客服"
                    className="w-full h-full p-2 sm:p-2 md:p-2.5"
                    objectFit="contain"
                    priority={true}
                  />
                </div>
              </div>
            </div>

            {/* 🔥 新增：抽獎橫幅 - 放在最上方 */}
            <div className="mb-8 sm:mb-10 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <div 
                onClick={() => {
                  // 🔥 點擊跳轉到試玩金抽獎激勵區域
                  const lotterySection = document.getElementById('lottery-incentive-section');
                  if (lotterySection) {
                    const yOffset = -80; // 偏移量，避免被導航欄遮擋
                    const y = lotterySection.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                  }
                }}
                className="relative bg-gradient-to-r from-red-600/90 via-orange-600/90 to-red-600/90 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border-2 border-yellow-400/60 shadow-2xl shadow-red-500/30 overflow-hidden cursor-pointer hover:scale-[1.02] transition-transform duration-300"
              >
                {/* 背景裝飾 */}
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjA1IiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>
                
                {/* 閃爍星星裝飾 */}
                <div className="absolute top-4 left-4 text-yellow-300 text-2xl animate-pulse">✨</div>
                <div className="absolute top-6 right-6 text-yellow-300 text-xl animate-pulse" style={{ animationDelay: '0.5s' }}>⭐</div>
                <div className="absolute bottom-4 left-8 text-yellow-300 text-lg animate-pulse" style={{ animationDelay: '1s' }}>💫</div>
                <div className="absolute bottom-6 right-4 text-yellow-300 text-2xl animate-pulse" style={{ animationDelay: '1.5s' }}>✨</div>

                <div className="relative z-10">
                  {/* 標題 */}
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center gap-2 bg-yellow-400/20 backdrop-blur-sm px-4 py-2 rounded-full border border-yellow-400/40 mb-4">
                      <span className="text-2xl animate-bounce">🎁</span>
                      <span className="text-yellow-100 font-bold text-sm sm:text-base">新會員專屬活動</span>
                      <span className="text-2xl animate-bounce" style={{ animationDelay: '0.2s' }}>💰</span>
                    </div>
                    
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-3 drop-shadow-lg">
                      <span className="inline-block animate-pulse">🎁</span>
                      <span className="mx-2">填表立即抽</span>
                    </h3>
                    
                    <div className="mb-4">
                      <div className="text-yellow-300 text-3xl sm:text-4xl md:text-5xl font-black mb-2">
                        現金紅包池 1,000 – 30,000
                      </div>
                      <p className="text-yellow-100 text-sm sm:text-base font-semibold">
                        抽的是「之後能多拿的現金上限」
                      </p>
                      <p className="text-yellow-200/80 text-xs sm:text-sm mt-1">
                        贏錢再給，不是一次給完
                      </p>
                    </div>

                    {/* 🎮 活動玩法說明 */}
                    <div className="bg-white/10 backdrop-blur-sm border border-yellow-400/30 rounded-xl p-4 mb-4">
                      <p className="text-yellow-100 text-base sm:text-lg font-bold mb-4 flex items-center justify-center gap-2">
                        <span className="text-2xl">🎮</span>
                        活動怎麼玩？（3 步就懂）
                      </p>
                      
                      {/* 第一階段 */}
                      <div className="bg-blue-500/20 rounded-lg p-3 mb-3 border border-blue-400/30">
                        <p className="text-white font-bold text-sm sm:text-base mb-2 flex items-center gap-2">
                          <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">第一階段</span>
                          填表抽紅包池
                        </p>
                        <div className="space-y-1 text-yellow-100 text-xs sm:text-sm">
                          <p>• 填寫資料即可抽</p>
                          <p>• 現金紅包池 1,000 – 30,000</p>
                          <p className="text-yellow-300 font-semibold">• 紅包池會先為你保留</p>
                        </div>
                      </div>

                      {/* 第二階段 */}
                      <div className="bg-green-500/20 rounded-lg p-3 mb-3 border border-green-400/30">
                        <p className="text-white font-bold text-sm sm:text-base mb-2 flex items-center gap-2">
                          <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">第二階段</span>
                          成為會員開始遊戲
                        </p>
                        <div className="space-y-1 text-yellow-100 text-xs sm:text-sm">
                          <p>• 完成入金後即可開始遊戲</p>
                          <p className="text-green-300 font-semibold">• 紅包池同步啟用</p>
                        </div>
                      </div>

                      {/* 第三階段 */}
                      <div className="bg-orange-500/20 rounded-lg p-3 border border-orange-400/30">
                        <p className="text-white font-bold text-sm sm:text-base mb-2 flex items-center gap-2">
                          <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">第三階段</span>
                          贏錢就能領紅包
                        </p>
                        <div className="space-y-1 text-yellow-100 text-xs sm:text-sm mb-2">
                          <p>每次結帳只要有贏錢：</p>
                          <p className="text-orange-300 font-bold text-base sm:text-lg">
                            贏多少 → 就能從紅包池 再多領同樣金額的現金
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* 📌 舉例說明 */}
                    <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-sm rounded-xl p-4 border border-purple-400/30 mb-4">
                      <p className="text-yellow-100 font-bold text-sm sm:text-base mb-3 flex items-center justify-center gap-2">
                        <span className="text-xl">📌</span>
                        舉例說明
                      </p>
                      <div className="space-y-2 text-left">
                        <div className="flex items-center gap-2 text-white text-xs sm:text-sm">
                          <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
                          <span>抽到紅包池 <span className="text-yellow-300 font-bold">20,000</span></span>
                        </div>
                        <div className="flex items-center gap-2 text-white text-xs sm:text-sm">
                          <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
                          <span>入金 <span className="text-yellow-300 font-bold">3,000</span></span>
                        </div>
                        <div className="flex items-center gap-2 text-white text-xs sm:text-sm">
                          <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
                          <span>遊戲贏 <span className="text-green-300 font-bold">5,000</span></span>
                        </div>
                        
                        <div className="border-t border-yellow-400/30 pt-2 mt-2">
                          <p className="text-yellow-300 font-bold text-sm sm:text-base mb-2">👉 結帳時可領：</p>
                          <div className="space-y-1 pl-4">
                            <p className="text-green-300 text-xs sm:text-sm">✓ 遊戲贏的 5,000</p>
                            <p className="text-green-300 text-xs sm:text-sm">✓ 紅包池再給 5,000</p>
                          </div>
                          <p className="text-yellow-200 text-xs sm:text-sm mt-2 pl-4">
                            👉 紅包池剩餘 <span className="text-yellow-300 font-bold">15,000</span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* 💎 VIP 會員區塊 */}
                    <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 backdrop-blur-sm rounded-xl p-4 border border-yellow-400/30">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-10 h-10 bg-yellow-400/30 rounded-full flex items-center justify-center">
                          <span className="text-2xl">💎</span>
                        </div>
                        <div className="flex-1 text-left">
                          <h4 className="text-yellow-100 font-bold text-base sm:text-lg mb-2">
                            升級 VIP 會員 · 回饋加碼
                          </h4>
                          <ul className="space-y-1.5 text-white/90 text-xs sm:text-sm">
                            <li className="flex items-center gap-2">
                              <span className="text-green-400">✓</span>
                              <span>贏的金額皆可申請結帳</span>
                            </li>
                            <li className="flex items-center gap-2">
                              <span className="text-green-400">✓</span>
                              <span>專屬客服服務</span>
                            </li>
                            <li className="flex items-center gap-2">
                              <span className="text-green-400">✓</span>
                              <span>快速處理出金</span>
                            </li>
                          </ul>
                          <p className="text-yellow-200/70 text-[10px] sm:text-xs mt-2">
                            * VIP 條件依實際會員方案說明為準
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 🔽 底部行動按鈕 */}
                  <div className="text-center mt-6">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const formSection = document.getElementById('apply');
                        if (formSection) {
                          formSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      }}
                      className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black text-base sm:text-lg font-black py-4 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-2xl animate-pulse"
                    >
                      👉 立即填表抽紅包池
                    </button>
                    <p className="text-yellow-200/60 text-[10px] sm:text-xs mt-2">
                      先抽上限，再靠實力慢慢拿
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 特色說明 - 🔥 优化手机版文字大小 */}
            <div className="mb-10 sm:mb-10 md:mb-12 animate-fade-in" style={{ animationDelay: '0.7s' }}>
              <p className="text-xl sm:text-lg md:text-xl text-gray-300 max-w-4xl mx-auto leading-loose px-4">
                我是<a href="#philosophy" onClick={(e) => { e.preventDefault(); scrollToSection('philosophy'); }} className="text-white hover:text-[#FFD700] transition-colors underline decoration-[#FFD700]/30 hover:decoration-[#FFD700]" title="了解高端代理經營理念"><strong>火星人娛樂城的高端代理</strong></a>，專門服務高端玩家。
                <br className="hidden sm:block" />
                <a href="#plans" onClick={(e) => { e.preventDefault(); scrollToSection('plans'); }} className="text-white hover:text-[#FFD700] transition-colors underline decoration-[#FFD700]/30 hover:decoration-[#FFD700]" title="查看快速出金服務">提領快速</a>，當天到手，<a href="#cases" onClick={(e) => { e.preventDefault(); scrollToSection('cases'); }} className="text-white hover:text-[#FFD700] transition-colors underline decoration-[#FFD700]/30 hover:decoration-[#FFD700]" title="查看成功案例">圈內玩家</a>都說放心。
              </p>
            </div>

            {/* 🔥 新增：Hero 快速申请表单 */}
            <div className="mb-8 sm:mb-8 md:mb-10 animate-fade-in max-w-xl mx-auto" style={{ animationDelay: '0.4s' }}>
              {/* 🆕 新增：已提交狀態顯示 */}
              {isSubmitted ? (
                <div className="bg-gradient-to-br from-green-500/20 via-emerald-500/10 to-transparent backdrop-blur-sm rounded-2xl p-6 sm:p-8 border-2 border-green-500/50 shadow-2xl shadow-green-500/20">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center animate-pulse">
                      <i className="ri-checkbox-circle-fill text-4xl text-white"></i>
                    </div>
                    <h3 className="text-2xl sm:text-2xl font-bold text-white mb-2">✅ 申請已提交</h3>
                    <p className="text-base text-gray-300">我們會盡快與您聯繫</p>
                  </div>

                  {/* 申請資訊 */}
                  <div className="space-y-3 mb-6">
                    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-400">審核編號</span>
                        <span className="text-base font-bold text-[#FFD700]">{applicationNumber}</span>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-400">申請方案</span>
                        <span className="text-base font-bold text-white">{getPlanText(formData.plan)}</span>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-400">聯繫電話</span>
                        <span className="text-base font-bold text-white">{formData.phone}</span>
                      </div>
                      {redEnvelopeAmount && (
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
                          <span className="text-sm text-gray-400">抽中紅包</span>
                          <span className="text-lg font-bold text-[#FFD700] flex items-center gap-1">
                            <i className="ri-gift-fill"></i>
                            {redEnvelopeAmount.toLocaleString()} 點
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 修改申請按鈕 */}
                  <button
                    onClick={() => setShowQuickApplyModal(true)}
                    className="w-full py-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-lg font-bold rounded-xl hover:from-blue-600 hover:to-cyan-600 transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg shadow-blue-500/30"
                  >
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-edit-line text-2xl"></i>
                      修改申請
                    </span>
                  </button>

                  <p className="text-xs text-gray-400 text-center mt-4">
                    <i className="ri-information-line text-blue-400 mr-1"></i>
                    如需修改申請資料，請點擊上方按鈕
                  </p>
                </div>
              ) : null}
            </div>

            {/* 🔥 優化：Hero 數據卡片 - 改為即時滾動數字 */}
            <div className="mb-8 sm:mb-8 md:mb-10 animate-fade-in" style={{ animationDelay: '0.6s' }}>
              <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-lg p-3 sm:p-3 border border-white/10 hover:border-[#FFD700]/50 transition-all duration-300 max-w-xs mx-auto">
                {/* 數據卡片內容 */}
                <div className="text-center">
                  <div className="w-9 h-9 sm:w-8 sm:h-8 mx-auto mb-2 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center animate-pulse">
                    <i className="ri-user-add-line text-xl sm:text-xl text-white"></i>
                  </div>
                  <div className="text-3xl sm:text-3xl font-bold text-[#FFD700] mb-2 animate-fade-in transition-all duration-500" key={todayApplicants}>
                    {todayApplicants}
                  </div>
                  <p className="text-base sm:text-sm text-gray-300 leading-relaxed">今日已有 <span className="text-[#FFD700] font-bold">{todayApplicants}</span> 人申請審核</p>
                  <div className="mt-2 flex items-center justify-center gap-1 text-sm text-green-400">
                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
                    <span>即時更新中</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 🔥 新增：贏錢數字展示區 */}
            <div className="mb-8 sm:mb-10 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
                {/* 本週最高贏家 */}
                <div className="bg-gradient-to-br from-[#FFD700]/20 via-[#D4AF37]/10 to-transparent backdrop-blur-sm rounded-xl p-5 border border-[#FFD700]/30 hover:border-[#FFD700]/50 transition-all duration-300 hover:scale-105">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <i className="ri-trophy-fill text-3xl text-[#FFD700] animate-pulse"></i>
                    <h3 className="text-sm text-gray-300 font-medium">本週最高贏家</h3>
                  </div>
                  <div className="text-4xl font-bold text-[#FFD700] mb-2 animate-bounce">
                    180 萬
                  </div>
                  <p className="text-xs text-gray-400">台北陳總 · 戰神賽特2</p>
                </div>

                {/* 今日已出金 */}
                <div className="bg-gradient-to-br from-green-500/20 via-emerald-500/10 to-transparent backdrop-blur-sm rounded-xl p-5 border border-green-500/30 hover:border-green-500/50 transition-all duration-300 hover:scale-105">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <i className="ri-money-dollar-circle-fill text-3xl text-green-400 animate-pulse"></i>
                    <h3 className="text-sm text-gray-300 font-medium">今日已出金</h3>
                  </div>
                  <div className="text-4xl font-bold text-green-400 mb-2 transition-all duration-500" key={todayWithdrawal}>
                    {(todayWithdrawal / 10000).toFixed(0)} 萬
                  </div>
                  <p className="text-xs text-gray-400 flex items-center justify-center gap-1">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    即時更新中
                  </p>
                </div>

                {/* 平均出金時間 */}
                <div className="bg-gradient-to-br from-blue-500/20 via-cyan-500/10 to-transparent backdrop-blur-sm rounded-xl p-5 border border-blue-500/30 hover:border-blue-500/50 transition-all duration-300 hover:scale-105">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <i className="ri-time-fill text-3xl text-blue-400 animate-pulse"></i>
                    <h3 className="text-sm text-gray-300 font-medium">平均出金時間</h3>
                  </div>
                  <div className="text-4xl font-bold text-blue-400 mb-2">
                    30 分鐘
                  </div>
                  <p className="text-xs text-gray-400">最快當天到帳</p>
                </div>
              </div>
            </div>

            {/* CTA 按鈕 + 倒數計時器 - 🔥 优化手机版尺寸 */}
            <div className="space-y-4 sm:space-y-5 animate-fade-in" style={{ animationDelay: '0.8s' }}>
              {/* 倒數計時器 */}
              <div className="inline-flex items-center gap-3 sm:gap-3 px-5 sm:px-5 py-4 sm:py-4 bg-gradient-to-r from-red-500/20 to-orange-500/10 rounded-lg border border-red-500/30 animate-pulse">
                <i className="ri-time-line text-red-400 text-3xl sm:text-3xl"></i>
                <div className="text-left">
                  <div className="text-base sm:text-sm text-red-400 font-medium">本月優惠倒數</div>
                  <div className="text-2xl sm:text-xl font-bold text-white">
                    {countdown.hours} 小時 {countdown.minutes} 分鐘
                  </div>
                </div>
              </div>

              {/* 次要 CTA 按鈕組 */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                <button
                  onClick={scrollToForm}
                  className="w-full sm:w-auto px-8 sm:px-8 py-4 sm:py-3 bg-white/10 hover:bg-white/20 text-white text-lg sm:text-base font-medium rounded-lg border border-white/30 hover:border-white/50 transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-chat-smile-3-line text-xl sm:text-lg"></i>
                  立即申請審核
                </button>
                <a
                  href="https://line.me/ti/p/your-line-id"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto px-8 sm:px-8 py-4 sm:py-3 bg-[#00B900] hover:bg-[#00A000] text-white text-lg sm:text-base font-medium rounded-lg transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-line-fill text-xl sm:text-lg"></i>
                  用 LINE 先聊
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* 2. 經營理念 + 信任符號（合併強化） */}
        <section id="philosophy" className="py-20 md:py-24 lg:py-32 px-4 sm:px-6 md:px-8 bg-gradient-to-b from-black via-[#0A0A0A] to-black relative overflow-hidden" aria-labelledby="philosophy-title">
          {/* 🔥 新增：漸層分隔線 */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FFD700]/30 to-transparent"></div>
          
          {/* 背景裝飾 - 增強 */}
          <div className="absolute inset-0 opacity-30" aria-hidden="true">
            <div className="absolute top-0 left-0 w-72 sm:w-96 h-72 sm:h-96 bg-[#D4AF37]/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-0 right-0 w-72 sm:w-96 h-72 sm:h-96 bg-[#FFD700]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            {/* 🔥 新增：額外裝飾 */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-[#FFD700]/5 to-transparent rounded-full"></div>
          </div>

          <div className="max-w-6xl mx-auto relative z-10">
            {/* 標題 - 優化手機版 */}
            <div className="text-center mb-16 sm:mb-20 md:mb-24">
              <div className="inline-flex items-center gap-2 sm:gap-3 px-5 sm:px-6 py-3 sm:py-3 bg-[#D4AF37]/10 rounded-full border border-[#D4AF37]/30 mb-5 sm:mb-6">
                <i className="ri-shield-star-line text-[#FFD700] text-2xl sm:text-2xl"></i>
                <span className="text-[#D4AF37] text-lg sm:text-lg font-medium">經營理念</span>
              </div>
              <h2 id="philosophy-title" className="text-4xl sm:text-4xl md:text-5xl font-bold text-white mb-6 sm:mb-6 leading-tight">
                為什麼選擇<span className="text-[#FFD700]">火星人娛樂</span>？
              </h2>
              <p className="text-xl sm:text-lg md:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
                我們不是最大的，但我們是最用心的
              </p>
            </div>

            {/* 🔥 新增：視覺化信任符號區塊 */}
            <div className="mb-16 sm:mb-20 md:mb-24 animate-fade-in">
              <div className="bg-gradient-to-br from-[#FFD700]/10 via-[#D4AF37]/5 to-transparent backdrop-blur-sm rounded-2xl p-8 sm:p-10 border border-[#FFD700]/30 hover:border-[#FFD700]/50 transition-all duration-500 hover:shadow-2xl hover:shadow-[#FFD700]/20">
                <div className="text-center mb-8">
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                    🏆 值得信賴的平台
                  </h3>
                  <p className="text-base sm:text-lg text-gray-400 leading-relaxed">用數據說話，用實力證明</p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
                  {/* 信任符號 1 */}
                  <div className="text-center group">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-500">
                      <i className="ri-shield-check-fill text-4xl sm:text-5xl text-white"></i>
                    </div>
                    <div className="text-3xl sm:text-4xl font-bold text-[#FFD700] mb-2">3 年+</div>
                    <p className="text-sm sm:text-base text-gray-400 leading-relaxed">穩定經營</p>
                  </div>

                  {/* 信任符號 2 */}
                  <div className="text-center group">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-500">
                      <i className="ri-user-star-fill text-4xl sm:text-5xl text-white"></i>
                    </div>
                    <div className="text-3xl sm:text-4xl font-bold text-[#FFD700] mb-2">2000+</div>
                    <p className="text-sm sm:text-base text-gray-400 leading-relaxed">VIP 會員</p>
                  </div>

                  {/* 信任符號 3 */}
                  <div className="text-center group">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-500">
                      <i className="ri-money-dollar-circle-fill text-4xl sm:text-5xl text-white"></i>
                    </div>
                    <div className="text-3xl sm:text-4xl font-bold text-[#FFD700] mb-2">5 億+</div>
                    <p className="text-sm sm:text-base text-gray-400 leading-relaxed">累計提領</p>
                  </div>

                  {/* 信任符號 4 */}
                  <div className="text-center group">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 bg-gradient-to-br from-[#FFD700] to-[#D4AF37] rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-500">
                      <i className="ri-thumb-up-fill text-4xl sm:text-5xl text-black"></i>
                    </div>
                    <div className="text-3xl sm:text-4xl font-bold text-[#FFD700] mb-2">92%</div>
                    <p className="text-sm sm:text-base text-gray-400 leading-relaxed">滿意度</p>
                  </div>
                </div>

                {/* 🔥 新增：認證標章 */}
                <div className="mt-10 pt-10 border-t border-white/10">
                  <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
                    <div className="flex items-center gap-2 px-5 py-3 bg-white/5 rounded-full border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300">
                      <i className="ri-verified-badge-fill text-green-400 text-2xl"></i>
                      <span className="text-base text-white">實名認證</span>
                    </div>
                    <div className="flex items-center gap-2 px-5 py-3 bg-white/5 rounded-full border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300">
                      <i className="ri-lock-password-fill text-blue-400 text-2xl"></i>
                      <span className="text-base text-white">資料加密</span>
                    </div>
                    <div className="flex items-center gap-2 px-5 py-3 bg-white/5 rounded-full border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300">
                      <i className="ri-customer-service-2-fill text-[#FFD700] text-2xl"></i>
                      <span className="text-base text-white">24H 客服</span>
                    </div>
                    <div className="flex items-center gap-2 px-5 py-3 bg-white/5 rounded-full border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300">
                      <i className="ri-time-fill text-purple-400 text-2xl"></i>
                      <span className="text-base text-white">快速出金</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 理念卡片 - 優化手機版 + 條列式內容 + 增強動畫 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10 md:gap-12">
              {[
                {
                  icon: 'ri-shield-check-line',
                  title: '審核制保障',
                  points: [
                    '只服務認真的玩家',
                    '確保每位會員真實可靠',
                    '避免糾紛，玩得安心',
                    '嚴格把關，品質保證'
                  ],
                  color: 'from-blue-500 to-cyan-500'
                },
                {
                  icon: 'ri-time-line',
                  title: '快速出金',
                  points: [
                    '贏了錢當天就能拿到手',
                    '30 分鐘內到帳',
                    '不拖延、不找藉口',
                    '口碑保證，值得信賴'
                  ],
                  color: 'from-green-500 to-emerald-500'
                },
                {
                  icon: 'ri-customer-service-2-line',
                  title: '專屬客服',
                  points: [
                    '24 小時真人客服待命',
                    '有問題馬上解決',
                    '不是機器人回覆',
                    '專業團隊，貼心服務'
                  ],
                  color: 'from-purple-500 to-pink-500'
                }
              ].map((item, index) => (
                <div 
                  key={index}
                  className="group relative bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm rounded-2xl p-8 sm:p-10 md:p-12 border border-white/10 hover:border-[#FFD700]/50 transition-all duration-500 card-flip hover:shadow-2xl hover:shadow-[#FFD700]/20 hover:-translate-y-2"
                  style={{ transformStyle: 'preserve-3d' }}
                >
                  {/* 圖標 */}
                  <div className={`w-16 h-16 sm:w-18 sm:h-18 md:w-20 md:h-20 mx-auto mb-6 sm:mb-8 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center transform group-hover:rotate-6 group-hover:scale-110 transition-all duration-500 shadow-xl`}>
                    <i className={`${item.icon} text-4xl sm:text-4xl md:text-5xl text-white`}></i>
                  </div>
                  
                  {/* 標題 */}
                  <h3 className="text-2xl sm:text-2xl md:text-3xl font-bold text-white mb-6 sm:mb-8 text-center leading-tight">
                    {item.title}
                  </h3>
                  
                  {/* 特點列表 */}
                  <ul className="space-y-4 sm:space-y-5">
                    {item.points.map((point, idx) => (
                      <li key={idx} className="flex items-start text-gray-300 text-base sm:text-lg leading-relaxed">
                        <i className="ri-check-line text-[#FFD700] mr-3 mt-1 flex-shrink-0 text-2xl sm:text-2xl"></i>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>

                  {/* 懸停效果 */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700]/0 to-[#D4AF37]/0 group-hover:from-[#FFD700]/5 group-hover:to-[#D4AF37]/5 rounded-2xl transition-all duration-500 pointer-events-none"></div>
                  
                  {/* 🔥 新增：光暈效果 */}
                  <div className="absolute -inset-1 bg-gradient-to-r from-[#FFD700]/0 via-[#FFD700]/0 to-[#FFD700]/0 group-hover:from-[#FFD700]/20 group-hover:via-[#FFD700]/10 group-hover:to-[#FFD700]/20 rounded-2xl blur-xl transition-all duration-500 -z-10"></div>
                </div>
              ))}
            </div>
          </div>
          
          {/* 🔥 新增：底部漸層分隔線 */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FFD700]/30 to-transparent"></div>
        </section>

        {/* 3. 本週贏家榜（社交證明前置） */}
        <section className="py-20 md:py-24 lg:py-32 px-4 sm:px-6 md:px-8 bg-gradient-to-b from-black via-[#0A0A0A] to-black relative overflow-hidden">
          {/* 🔥 新增：漸層分隔線 */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-green-500/30 to-transparent"></div>
          
          {/* 背景裝飾 - 增強 */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 left-0 w-96 h-96 bg-[#FFD700]/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-green-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }}></div>
            {/* 🔥 新增：額外裝飾 */}
            <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-[#FFD700]/5 rounded-full blur-2xl"></div>
          </div>

          <div className="max-w-6xl mx-auto relative z-10">
            {/* 標題 */}
            <div className="text-center mb-16 sm:mb-20 animate-fade-in">
              <div className="inline-flex items-center gap-2 mb-6 px-6 py-3 bg-gradient-to-r from-[#FFD700]/20 to-green-500/20 rounded-full border border-[#FFD700]/30">
                <i className="ri-trophy-fill text-[#FFD700] text-3xl animate-pulse"></i>
                <span className="text-lg text-[#FFD700] font-medium">本週贏家榜</span>
              </div>
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
                🏆 本週贏家排行榜
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
                恭喜本週贏家！下一個就是你
              </p>
            </div>

            {/* 贏家榜單 - 增強動畫和間距 */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-4 sm:p-5 border border-white/10 animate-fade-in hover:border-[#FFD700]/30 transition-all duration-500">
              <div className="space-y-2.5">
                {weeklyWinners.slice(0, 5).map((winner, index) => (
                  <div 
                    key={winner.rank}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all duration-500 hover:scale-[1.02] hover:shadow-lg ${
                      winner.rank === 1 ? 'bg-gradient-to-r from-[#FFD700]/20 to-[#D4AF37]/10 border border-[#FFD700]/50 hover:border-[#FFD700]/80' :
                      winner.rank === 2 ? 'bg-gradient-to-r from-gray-300/20 to-gray-400/10 border border-gray-400/50 hover:border-gray-400/80' :
                      winner.rank === 3 ? 'bg-gradient-to-r from-orange-400/20 to-orange-500/10 border border-orange-500/50 hover:border-orange-500/80' :
                      'bg-white/5 border border-white/10 hover:border-white/20'
                    }`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {/* 排名 */}
                    <div className="flex-shrink-0">
                      {winner.rank <= 3 ? (
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-base font-bold ${
                          winner.rank === 1 ? 'bg-gradient-to-br from-[#FFD700] to-[#D4AF37] text-black shadow-lg shadow-[#FFD700]/50' :
                          winner.rank === 2 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-gray-800 shadow-lg shadow-gray-400/50' :
                          'bg-gradient-to-br from-orange-400 to-orange-500 text-white shadow-lg shadow-orange-500/50'
                        }`}>
                          {winner.rank === 1 ? '👑' : winner.rank === 2 ? '🥈' : '🥉'}
                        </div>
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold text-gray-400">
                          {winner.rank}
                        </div>
                      )}
                    </div>

                    {/* 用戶信息 */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs text-gray-400">{winner.location}</span>
                        <span className="text-sm font-bold text-white truncate">{winner.name}</span>
                        {winner.rank === 1 && (
                          <span className="text-xs px-1.5 py-0.5 bg-[#FFD700]/20 text-[#FFD700] rounded-full font-bold whitespace-nowrap">
                            TOP 1
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400">
                        遊戲：<span className="text-white/90">{winner.game}</span>
                      </div>
                    </div>

                    {/* 贏額 */}
                    <div className="flex-shrink-0 text-right">
                      <div className={`text-base font-bold ${
                        winner.rank === 1 ? 'text-[#FFD700]' :
                        winner.rank === 2 ? 'text-gray-300' :
                        winner.rank === 3 ? 'text-orange-400' :
                        'text-white'
                      }`}>
                        {winner.amount}
                      </div>
                      <div className="text-xs text-gray-400 whitespace-nowrap">本週贏額</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* 🔥 新增：底部漸層分隔線 */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-green-500/30 to-transparent"></div>
        </section>

        {/* 4. VIP 方案（趁熱打鐵） */}
        <section id="plans" className="py-10 sm:py-12 md:py-16 lg:py-20 px-4 sm:px-6 md:px-8 lg:px-10 bg-gradient-to-b from-black via-[#0A0A0A] to-black relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-[#D4AF37]/10 rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-6xl mx-auto relative z-10">
            <div className="text-center mb-6 sm:mb-8 md:mb-10 animate-fade-in">
              <div className="inline-flex items-center gap-2 mb-2 sm:mb-3 px-3 sm:px-3 py-1.5 sm:py-1.5 bg-gradient-to-r from-[#D4AF37]/20 to-[#FFD700]/20 rounded-full border border-[#FFD700]/30">
                <i className="ri-vip-crown-fill text-[#FFD700] text-lg sm:text-lg"></i>
                <span className="text-sm sm:text-xs text-[#FFD700] font-medium">VIP 專屬方案</span>
              </div>
              <h2 className="text-2xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-3">
                VIP 專屬服務
              </h2>
              <p className="text-base sm:text-sm md:text-base text-gray-400 max-w-3xl mx-auto">
                根據您的需求選擇合適的方案，享受專屬 VIP 服務
              </p>
            </div>

            {/* 🔥 新增:財力證明優惠說明卡片 - 縮小 50% */}
            <div className="mb-6 sm:mb-8 animate-fade-in">
              <div className="bg-gradient-to-br from-green-500/20 via-emerald-500/10 to-transparent backdrop-blur-sm rounded-lg p-3 sm:p-4 border border-green-500/30 shadow-lg">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-shield-2-line text-base text-white"></i>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-white">
                    💎 提供財力證明，享受更優惠方案
                  </h3>
                </div>
                <p className="text-xs sm:text-sm text-gray-300 leading-relaxed mb-3">
                  提供財力證明可降低押金比例至 <span className="text-green-400 font-semibold">50%</span>，並提升額度上限 <span className="text-green-400 font-semibold">+40%</span>
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  <div className="bg-white/5 rounded-lg p-2 border border-white/10">
                    <div className="text-gray-400 mb-0.5">標準方案</div>
                    <div className="text-white font-semibold">押金 70% → 額度 1 萬</div>
                  </div>
                  <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-lg p-2 border border-green-500/30">
                    <div className="text-green-400 mb-0.5 flex items-center gap-1">
                      <i className="ri-vip-crown-line"></i>
                      財力證明方案
                    </div>
                    <div className="text-white font-semibold">押金 50% → 額度 1.4 萬</div>
                  </div>
                </div>
              </div>
            </div>

            {/* 🔥 新增：手機版 - 垂直卡片式排列 - 文字加大 */}
            <div className="lg:hidden mb-4 sm:mb-6 animate-fade-in">
              <div className="space-y-3">
                {vipPlans.map((plan, index) => (
                  <div 
                    key={index}
                    className={`relative overflow-hidden backdrop-blur-sm rounded-xl p-3 sm:p-4 border ${plan.borderColor} ${
                      plan.popular 
                        ? 'ring-1 ring-[#FFD700] ring-offset-1 ring-offset-black shadow-xl shadow-[#FFD700]/30' 
                        : plan.isTrial
                        ? 'ring-1 ring-blue-400 ring-offset-1 ring-offset-black shadow-xl shadow-blue-400/30'
                        : 'shadow-lg'
                    } transition-all duration-300`}
                  >
                    {/* 漸層背景層 */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${plan.gradient} opacity-95`}></div>
                    <div className={`absolute inset-0 ${plan.bgOverlay}`}></div>
                    
                    {/* 光澤效果 */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-white/5 via-transparent to-white/10"></div>
                    
                    {/* 內容層 */}
                    <div className="relative z-10">
                      {plan.popular && (
                        <div className="flex justify-center mb-2">
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-[#FFD700] to-[#D4AF37] text-black text-xs font-bold rounded-full animate-pulse shadow-lg">
                            <i className="ri-fire-fill"></i>
                            最受歡迎
                          </span>
                        </div>
                      )}
                      
                      {plan.isTrial && (
                        <div className="flex justify-center mb-2">
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs font-bold rounded-full animate-pulse shadow-lg">
                            <i className="ri-gift-fill"></i>
                            體驗版
                          </span>
                        </div>
                      )}
                      
                      {/* 方案標題 */}
                      <div className="text-center mb-3">
                        <div className={`text-3xl sm:text-3xl mb-2 ${plan.textShadow} ${plan.popular || plan.isTrial ? 'animate-bounce' : ''}`}>{plan.icon}</div>
                        <h3 className={`text-xl sm:text-xl font-bold text-white mb-1 ${plan.textShadow}`}>{plan.name}</h3>
                        <p className={`text-base sm:text-sm text-white/90 ${plan.textShadow}`}>{plan.description}</p>
                      </div>
                      
                      {/* 方案詳情 - 卡片式垂直排列 - 文字加大 */}
                      <div className="space-y-2">
                        {/* 押金 */}
                        <div className="bg-white/10 rounded-lg p-2 backdrop-blur-sm">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <i className="ri-wallet-line text-white text-lg"></i>
                              <span className="text-white/90 font-medium text-sm">押金</span>
                            </div>
                            <div className="text-right">
                              <div className={`text-white font-bold text-lg ${plan.textShadow} ${plan.isTrial ? 'text-cyan-300' : ''}`}>{plan.deposit}</div>
                            </div>
                          </div>
                        </div>
                        
                        {/* 額度 */}
                        <div className="bg-white/10 rounded-lg p-2 backdrop-blur-sm">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <i className={`ri-money-dollar-circle-line text-lg ${plan.isTrial ? 'text-cyan-400' : 'text-[#FFD700]'}`}></i>
                              <span className="text-white/90 font-medium text-sm">{plan.isTrial ? '試玩金' : '額度'}</span>
                            </div>
                            <div className="text-right">
                              <div className={`text-white font-bold text-lg ${plan.textShadow}`}>{plan.credit}</div>
                              {!plan.isTrial && (
                                <div className="text-xs text-green-400 mt-0.5">💎 最高 {(() => {
                                  const creditStr = plan.credit.replace(/,/g, '').replace(/\+/g, '');
                                  let amount = 0;
                                  if (creditStr.includes('萬')) {
                                    amount = parseFloat(creditStr.replace(/[^\d.]/g, '')) * 10000;
                                  } else {
                                    amount = parseInt(creditStr);
                                  }
                                  const increasedAmount = amount * 1.4;
                                  return (increasedAmount / 10000).toFixed(1) + ' 萬';
                                })()}</div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* 結算 */}
                        <div className="bg-white/10 rounded-lg p-2 backdrop-blur-sm">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <i className={`ri-calendar-check-line text-lg ${plan.isTrial ? 'text-cyan-400' : 'text-blue-400'}`}></i>
                              <span className="text-white/90 font-medium text-sm">結算</span>
                            </div>
                            <span className="text-white/95 text-sm">{plan.settlement}</span>
                          </div>
                        </div>
                        
                        {/* 提前結算 */}
                        <div className="bg-white/10 rounded-lg p-2 backdrop-blur-sm">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <i className={`ri-flashlight-line text-lg ${plan.isTrial ? 'text-red-400' : 'text-yellow-400'}`}></i>
                              <span className="text-white/90 font-medium text-sm">{plan.isTrial ? '提領規則' : '提前結算'}</span>
                            </div>
                            <span className={`text-sm ${plan.isTrial ? 'text-red-400 font-bold' : 'text-white/95'}`}>{plan.earlySettlement}</span>
                          </div>
                        </div>
                        
                        {/* 客服 */}
                        <div className="bg-white/10 rounded-lg p-2 backdrop-blur-sm">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <i className="ri-customer-service-2-line text-purple-400 text-lg"></i>
                              <span className="text-white/90 font-medium text-sm">客服</span>
                            </div>
                            <span className="text-white/95 text-sm">{plan.support}</span>
                          </div>
                        </div>
                      </div>

                      {/* 🔥 新增：試玩帳號特別說明 */}
                      {plan.isTrial && (
                        <div className="mt-3 p-2 bg-red-500/20 rounded-lg border border-red-500/30">
                          <div className="flex items-start gap-1.5">
                            <i className="ri-information-fill text-red-400 text-sm mt-0.5"></i>
                            <div>
                              <p className="text-xs font-bold text-red-400 mb-0.5">⚠️ 重要說明</p>
                              <p className="text-xs text-white/90 leading-relaxed">
                                試玩金僅供體驗遊戲使用，贏的無法提領。<br />
                                <span className="text-yellow-300 font-bold">升級 VIP 會員後，贏的都可提領！</span>
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* 財力證明優惠提示 - 只顯示給非試玩帳號 */}
                      {!plan.isTrial && (
                        <div className="mt-3 p-2 bg-green-500/20 rounded-lg border border-green-500/30">
                          <div className="flex items-start gap-1.5">
                            <i className="ri-file-shield-2-line text-green-400 text-sm mt-0.5"></i>
                            <div>
                              <p className="text-xs font-bold text-green-400 mb-0.5">💎 財力證明優惠</p>
                              <p className="text-xs text-white/90">押金比例降至 50% / 額度提升 40%</p>
                            </div>
                          </div>
                        </div>
                      )}

                      <button
                        onClick={() => {
                          const planValueMap: { [key: string]: string } = {
                            '試玩帳號': 'trial',
                            '入門方案': 'starter',
                            '進階方案': 'advanced',
                            '高端方案': 'premium',
                            '頂級方案': 'elite'
                          };
                          handlePlanSelect(planValueMap[plan.name], plan.name);
                        }}
                        className={`w-full mt-3 px-4 py-2.5 ${plan.buttonClass} text-sm sm:text-sm font-bold rounded-full transition-all duration-300 transform hover:scale-105 active:scale-95`}
                      >
                        {plan.isTrial ? '立即體驗' : plan.popular ? '立即選擇（推薦）' : '選擇方案'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 🔥 優化：方案對比表格 - 電腦版突出「最受歡迎」方案 - 文字加大 */}
            <div className="hidden lg:block overflow-x-auto mb-4 sm:mb-6 animate-fade-in">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-[#FFD700]/30">
                    <th className="p-2 text-left text-sm font-bold text-gray-400 pt-8">方案特色</th>
                    {vipPlans.map((plan, index) => (
                      <th key={index} className={`p-2 text-center relative pt-8 ${plan.popular ? 'bg-gradient-to-b from-[#FFD700]/10 to-transparent' : plan.isTrial ? 'bg-gradient-to-b from-blue-500/10 to-transparent' : ''}`}>
                        {plan.popular && (
                          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 z-10">
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-[#FFD700] to-[#D4AF37] text-black text-xs font-bold rounded-full whitespace-nowrap shadow-lg animate-pulse">
                              <i className="ri-fire-fill"></i>
                              最受歡迎
                            </span>
                          </div>
                        )}
                        {plan.isTrial && (
                          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 z-10">
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs font-bold rounded-full whitespace-nowrap shadow-lg animate-pulse">
                              <i className="ri-gift-fill"></i>
                              體驗版
                            </span>
                          </div>
                        )}
                        <div className={`text-2xl mb-2 ${plan.popular || plan.isTrial ? 'animate-bounce' : ''}`}>{plan.icon}</div>
                        <div className={`text-base font-bold ${plan.popular ? 'text-[#FFD700]' : plan.isTrial ? 'text-cyan-400' : 'text-white'}`}>{plan.name}</div>
                        <div className="text-sm text-gray-400 mt-1">{plan.description}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="border-b border-white/10 hover:bg-white/5 transition-colors">
                    <td className="p-2 text-gray-400 font-medium">押金</td>
                    {vipPlans.map((plan, index) => (
                      <td key={index} className={`p-2 text-center font-bold text-base ${plan.popular ? 'bg-[#FFD700]/5 text-white' : plan.isTrial ? 'bg-blue-500/5 text-cyan-300' : 'text-white'}`}>{plan.deposit}</td>
                    ))}
                  </tr>
                  <tr className="border-b border-white/10 hover:bg-white/5 transition-colors">
                    <td className="p-2 text-gray-400 font-medium">額度</td>
                    {vipPlans.map((plan, index) => (
                      <td key={index} className={`p-2 text-center text-[#FFD700] font-bold text-base ${plan.popular ? 'bg-[#FFD700]/5' : plan.isTrial ? 'bg-blue-500/5' : ''}`}>{plan.credit}</td>
                    ))}
                  </tr>
                  <tr className="border-b border-white/10 hover:bg-white/5 transition-colors">
                    <td className="p-2 text-gray-400 font-medium">結算方式</td>
                    {vipPlans.map((plan, index) => (
                      <td key={index} className={`p-2 text-center text-white text-sm ${plan.popular ? 'bg-[#FFD700]/5' : plan.isTrial ? 'bg-blue-500/5' : ''}`}>{plan.settlement}</td>
                    ))}
                  </tr>
                  <tr className="border-b border-white/10 hover:bg-white/5 transition-colors">
                    <td className="p-2 text-gray-400 font-medium">提領規則</td>
                    {vipPlans.map((plan, index) => (
                      <td key={index} className={`p-2 text-center text-sm ${plan.popular ? 'bg-[#FFD700]/5 text-white' : plan.isTrial ? 'bg-blue-500/5 text-red-400 font-bold' : 'text-white'}`}>{plan.earlySettlement}</td>
                    ))}
                  </tr>
                  <tr className="border-b border-white/10 hover:bg-white/5 transition-colors">
                    <td className="p-2 text-gray-400 font-medium">客服支援</td>
                    {vipPlans.map((plan, index) => (
                      <td key={index} className={`p-2 text-center text-white text-sm ${plan.popular ? 'bg-[#FFD700]/5' : plan.isTrial ? 'bg-blue-500/5' : ''}`}>{plan.support}</td>
                    ))}
                  </tr>
                  <tr>
                    <td className="p-2"></td>
                    {vipPlans.map((plan, index) => (
                      <td key={index} className={`p-2 text-center ${plan.popular ? 'bg-[#FFD700]/5' : plan.isTrial ? 'bg-blue-500/5' : ''}`}>
                        <button
                          onClick={() => {
                            const planValueMap: { [key: string]: string } = {
                              '試玩帳號': 'trial',
                              '入門方案': 'starter',
                              '進階方案': 'advanced',
                              '高端方案': 'premium',
                              '頂級方案': 'elite'
                            };
                            handlePlanSelect(planValueMap[plan.name], plan.name);
                          }}
                          className={`w-full px-3 py-2 ${plan.buttonClass} text-xs font-bold rounded-full transition-all duration-300 transform hover:scale-105 active:scale-95`}
                        >
                          {plan.isTrial ? '立即體驗' : plan.popular ? '立即選擇（推薦）' : '選擇方案'}
                        </button>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* 方案說明 - 縮小 50% */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 animate-fade-in">
              <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 backdrop-blur-sm rounded-lg p-3 border border-green-500/30">
                <div className="flex items-start gap-2">
                  <div className="w-7 h-7 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-check-line text-base text-green-400"></i>
                  </div>
                  <div>
                    <h4 className="text-sm sm:text-sm font-bold text-white mb-1">週一結算</h4>
                    <p className="text-xs sm:text-xs text-gray-400 leading-relaxed">輸了不用急，等到週一再結算</p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-[#FFD700]/20 to-[#D4AF37]/10 backdrop-blur-sm rounded-lg p-3 border border-[#FFD700]/30">
                <div className="flex items-start gap-2">
                  <div className="w-7 h-7 bg-[#FFD700]/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-trophy-line text-base text-[#FFD700]"></i>
                  </div>
                  <div>
                    <h4 className="text-sm sm:text-sm font-bold text-white mb-1">贏 3 倍提前結</h4>
                    <p className="text-xs sm:text-xs text-gray-400 leading-relaxed">贏超過額度 3 倍，立即提前結算拿錢</p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 backdrop-blur-sm rounded-lg p-3 border border-blue-500/30">
                <div className="flex items-start gap-2">
                  <div className="w-7 h-7 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-base text-blue-400"></i>
                  </div>
                  <div>
                    <h4 className="text-sm sm:text-sm font-bold text-white mb-1">快速出金</h4>
                    <p className="text-xs sm:text-xs text-gray-400 leading-relaxed">平均 30 分鐘到帳，最快當天處理</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 5. 試玩金抽獎激勵（降低門檻） */}
        {!isSubmitted && (
          <section id="lottery-incentive-section" className="py-8 sm:py-10 md:py-12 px-4 sm:px-6 md:px-8 bg-gradient-to-b from-black via-[#0A0A0A] to-black relative overflow-hidden">
            {/* 🔥 新增：動態背景效果 */}
            <div className="absolute inset-0 opacity-30">
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-red-500/20 via-orange-500/10 to-yellow-500/20 animate-pulse"></div>
              <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-yellow-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '0.5s' }}></div>
              <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-red-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>

            <div className="max-w-4xl mx-auto relative z-10">
              {/* 刪除整個抽獎橫幅內容 */}
            </div>
          </section>
        )}

        {/* 6. 立即申請表單（第一次 CTA） */}
        <section id="apply" className="py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 md:px-8 bg-gradient-to-b from-black via-[#0A0A0A] to-black relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-[#D4AF37]/10 rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-3xl mx-auto relative z-10">
            <div className="text-center mb-10 sm:mb-12 animate-fade-in">
              <div className="inline-flex items-center gap-2 mb-4 sm:mb-5 px-4 sm:px-5 py-2.5 sm:py-3 bg-gradient-to-r from-[#D4AF37]/20 to-[#FFD700]/20 rounded-full border border-[#FFD700]/30">
                <i className="ri-vip-crown-fill text-[#FFD700] text-xl sm:text-2xl animate-pulse"></i>
                <span className="text-sm sm:text-base text-[#FFD700] font-medium">立即加入</span>
              </div>
            </div>

            {/* 🔥 新增：兩步驟表單 */}
            {!isSubmitted && (
              <div 
                ref={applicationFormRef}
                className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-white/10 animate-fade-in"
              >
                {/* 表單標題 */}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-white mb-2">
                    立即申請 VIP 會員
                  </h3>
                  <p className="text-gray-300 text-sm">
                    填寫以下資訊，專屬客服將立即為您服務
                  </p>
                </div>

                {/* 🎁 抽獎提示橫幅 */}
                <div className="mb-4 bg-gradient-to-r from-yellow-500/20 via-yellow-400/20 to-yellow-500/20 border border-yellow-500/50 rounded-lg p-3 animate-pulse-slow">
                  <div className="flex items-center gap-2">
                    <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center animate-bounce">
                      <i className="ri-gift-fill text-base text-white"></i>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-white font-bold text-sm mb-0.5 flex items-center gap-1.5">
                        🎰 填單即抽試玩體驗金
                        <span className="bg-gradient-to-r from-red-500 to-orange-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold animate-pulse">
                          HOT
                        </span>
                      </h4>
                      <p className="text-yellow-200 text-xs leading-tight">
                        完成表單立即抽取 1,000 - 5,000 點試玩金
                      </p>
                    </div>
                  </div>
                </div>

                {/* 🔥 新增：表單進度條 */}
                {formProgressPercent > 0 && (
                  <div className="mb-6 bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-400">表單完成度</span>
                      <span className="text-lg font-bold text-[#FFD700]">{formProgressPercent}%</span>
                    </div>
                    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-[#D4AF37] to-[#FFD700] transition-all duration-500 ease-out"
                        style={{ width: `${formProgressPercent}%` }}
                      ></div>
                    </div>
                    {formProgressPercent === 100 && (
                      <p className="mt-2 text-sm text-green-400 flex items-center gap-1">
                        <i className="ri-checkbox-circle-fill"></i>
                        所有欄位已填寫完成，可以提交了！
                      </p>
                    )}
                  </div>
                )}

                <form 
                  data-readdy-form
                  onSubmit={handleFormSubmit} 
                  className="space-y-5"
                >
                  {/* 遊戲頻率選擇 */}
                  <div>
                    <label className="block text-white font-medium mb-3 text-sm">
                      1. 選擇方案
                      <span className="text-gray-400 text-xs ml-2">根據您的遊戲頻率推薦</span>
                    </label>
                    <div className="relative">
                      <select
                        name="plan"
                        value={formData.plan}
                        onChange={(e) => handleFormInputChange('plan', e.target.value)}
                        onFocus={() => {
                          // 🔥 新增：用戶開始填寫表單時觸發 InitiateCheckout
                          if (pixelLoaded && !hasInteractedWithForm) {
                            setHasInteractedWithForm(true);
                            console.log('🎯 用戶開始填寫主表單（選擇方案）');
                            trackInitiateCheckout('主申請表單 - 選擇方案', 'form');
                          }
                        }}
                        required
                        className="w-full px-4 py-3.5 bg-white/5 border border-white/10 rounded-lg text-white appearance-none cursor-pointer hover:bg-white/10 transition-all focus:outline-none focus:ring-2 focus:ring-[#FFD700] focus:border-transparent"
                      >
                        <option value="" className="bg-gray-900">選擇方案</option>
                        <option value="starter" className="bg-gray-900">🎁 入門方案（押金 3,000 / 額度 5,000）</option>
                        <option value="advanced" className="bg-gray-900">⭐ VIP進階方案（押金 5,000 / 額度 1 萬）</option>
                        <option value="premium" className="bg-gray-900">💎 高端方案（押金 3.5 萬 / 額度 5 萬）</option>
                        <option value="elite" className="bg-gray-900">👑 頂級方案（押金 17.5 萬+ / 額度 25 萬+）</option>
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-white text-xl pointer-events-none"></i>
                    </div>
                    {formValidation.plan.message && !formValidation.plan.valid && (
                      <p className="text-red-400 text-xs mt-2 flex items-center gap-1">
                        <i className="ri-error-warning-line"></i>
                        {formValidation.plan.message}
                      </p>
                    )}
                  </div>

                  {/* 聯繫電話 */}
                  <div>
                    <label htmlFor="phone" className="block text-white font-medium mb-2 text-sm">
                      2. 聯繫電話 <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => handleFormInputChange('phone', e.target.value)}
                        onFocus={() => {
                          // 🔥 新增：用戶開始填寫表單時觸發 InitiateCheckout
                          if (pixelLoaded && !hasInteractedWithForm) {
                            setHasInteractedWithForm(true);
                            console.log('🎯 用戶開始填寫主表單（輸入電話）');
                            trackInitiateCheckout('主申請表單 - 輸入電話', 'form');
                          }
                        }}
                        className={`w-full px-4 py-3 bg-white/10 border-2 ${
                          formValidation.phone.valid 
                            ? 'border-green-500/50' 
                            : formValidation.phone.message && !formValidation.phone.valid
                            ? 'border-red-500/50'
                            : 'border-white/20'
                        } rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 transition-all text-sm`}
                        placeholder="請輸入您的電話（例如：0912345678）"
                        maxLength={10}
                        required
                      />
                      {formValidation.phone.valid && (
                        <div className="absolute right-4 top-1/2 -translate-y-1/2">
                          <i className="ri-checkbox-circle-fill text-xl text-green-400"></i>
                        </div>
                      )}
                    </div>
                    {formValidation.phone.message && (
                      <p className={`mt-2 text-sm flex items-center gap-1 ${
                        formValidation.phone.valid ? 'text-green-400' : 'text-red-400'
                      }`}>
                        <i className={formValidation.phone.valid ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i>
                        {formValidation.phone.message}
                      </p>
                    )}
                  </div>

                  {/* 方便聯繫時間 */}
                  <div>
                    <label htmlFor="contactTime" className="block text-white font-medium mb-2 text-sm">
                      3. 方便聯絡的時間
                    </label>
                    <div className="relative">
                      <select
                        id="contactTime"
                        name="contactTime"
                        value={formData.contactTime}
                        onChange={(e) => handleFormInputChange('contactTime', e.target.value)}
                        onFocus={() => {
                          // 🔥 新增：用戶開始填寫表單時觸發 InitiateCheckout
                          if (pixelLoaded && !hasInteractedWithForm) {
                            setHasInteractedWithForm(true);
                            console.log('🎯 用戶開始填寫主表單（選擇聯繫時間）');
                            trackInitiateCheckout('主申請表單 - 選擇聯繫時間', 'form');
                          }
                        }}
                        className={`w-full px-4 py-3 bg-white/10 border-2 ${
                          formValidation.contactTime.valid 
                            ? 'border-green-500/50' 
                            : 'border-white/20'
                        } rounded-xl text-white appearance-none cursor-pointer transition-all duration-300 focus:border-blue-400 focus:bg-white/15 focus:outline-none hover:border-blue-400/50`}
                      >
                        <option value="任何時間都可以" className="bg-gray-900 text-white">立即聯絡（任何時間都可以）</option>
                        <option value="上午 9:00-12:00" className="bg-gray-900 text-white">上午 9:00-12:00</option>
                        <option value="下午 12:00-18:00" className="bg-gray-900 text-white">下午 12:00-18:00</option>
                        <option value="晚上 18:00-21:00" className="bg-gray-900 text-white">晚上 18:00-21:00</option>
                        <option value="晚上 21:00 後" className="bg-gray-900 text-white">晚上 21:00 後</option>
                      </select>
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none flex items-center gap-2">
                        {formValidation.contactTime.valid && (
                          <i className="ri-checkbox-circle-fill text-xl text-green-400"></i>
                        )}
                        <i className="ri-arrow-down-s-line text-xl text-blue-400"></i>
                      </div>
                    </div>
                    {formValidation.contactTime.message && (
                      <p className="mt-2 text-sm text-green-400 flex items-center gap-1">
                        <i className="ri-checkbox-circle-line"></i>
                        {formValidation.contactTime.message}
                      </p>
                    )}
                  </div>

                  {/* 提交按鈕 */}
                  <button
                    type="submit"
                    disabled={isSubmitting || formProgressPercent < 100}
                    className={`w-full min-h-[64px] font-bold py-4 rounded-xl transition-all duration-300 text-base whitespace-nowrap ${
                      formProgressPercent === 100 && !isSubmitting
                        ? 'bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 text-black hover:shadow-2xl hover:shadow-yellow-400/50 hover:scale-[1.02] cursor-pointer'
                        : 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
                    }`}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        提交中...
                      </span>
                    ) : formProgressPercent < 100 ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-lock-line"></i>
                        請完成所有欄位（{formProgressPercent}%）
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-send-plane-fill"></i>
                        立即提交申請
                      </span>
                    )}
                  </button>

                  {/* 提示文字 */}
                  <p className="text-center text-xs text-gray-400 mt-4">
                    <i className="ri-shield-check-line mr-1"></i>
                    您的資料將受到嚴格保密，僅用於 VIP 會員申請
                  </p>
                </form>
              </div>
            )}

            {/* 🆕 新增：平台官網按鈕 - 移到表單外面，永遠顯示 */}
            <div className="mt-6 sm:mt-8 text-center animate-fade-in">
              <a
                href="https://www.08777.net"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-500/20 to-cyan-500/10 border-2 border-blue-500/40 text-blue-400 text-base sm:text-lg font-bold rounded-full hover:from-blue-500/30 hover:to-cyan-500/20 hover:border-blue-400/60 hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300 transform hover:scale-105 active:scale-95"
              >
                <i className="ri-global-line text-xl sm:text-2xl"></i>
                <span>火星人官方網站</span>
                <i className="ri-external-link-line text-base sm:text-lg"></i>
              </a>
              <p className="mt-3 text-xs sm:text-sm text-gray-500">
                <i className="ri-information-line mr-1"></i>
                點擊查看火星人娛樂城官方遊戲平台
              </p>
            </div>

            {/* 🆕 新增：已提交狀態顯示 */}
            {isSubmitted && (
              <div className="bg-gradient-to-br from-green-500/20 via-emerald-500/10 to-transparent backdrop-blur-sm rounded-2xl p-6 sm:p-8 border-2 border-green-500/50 shadow-2xl shadow-green-500/20 animate-fade-in">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center animate-pulse">
                    <i className="ri-checkbox-circle-fill text-4xl text-white"></i>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">✅ 申請已提交</h3>
                  <p className="text-base text-gray-300">我們會盡快與您聯繫</p>
                  <button
                    onClick={clearApplicationStatus}
                    className="mt-3 text-sm text-gray-400 hover:text-white transition-colors underline"
                  >
                    清除申請記錄
                  </button>
                </div>

                {/* 申請資訊 */}
                <div className="space-y-3 mb-6">
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-400">審核編號</span>
                      <span className="text-base font-bold text-[#FFD700]">
                        {savedApplication?.applicationId || applicationNumber || '載入中...'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-400">申請方案</span>
                      <span className="text-base font-bold text-white">
                        {getPlanText(savedApplication?.formData?.plan || savedApplication?.formData?.selected_plan || '')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">聯繫電話</span>
                      <span className="text-base font-bold text-white">
                        {savedApplication?.formData?.phone || '載入中...'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 修改申請按鈕 */}
                <button
                  onClick={() => setShowQuickApplyModal(true)}
                  className="w-full py-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-lg font-bold rounded-xl hover:from-blue-600 hover:to-cyan-600 transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg shadow-blue-500/30"
                >
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-edit-line text-2xl"></i>
                    修改申請
                  </span>
                </button>

                <p className="text-xs text-gray-400 text-center mt-4">
                  <i className="ri-information-line text-blue-400 mr-1"></i>
                  如需修改申請資料，請點擊上方按鈕
                </p>
              </div>
            )}
          </div>
        </section>

        {/* 7. 成功案例（消除疑慮） */}
        <section className="py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 md:px-8 bg-gradient-to-b from-black via-[#0A0A0A] to-black relative overflow-hidden">
          {/* 背景裝飾 */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 left-0 w-64 h-64 bg-[#FFD700]/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-[#D4AF37]/10 rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-6xl mx-auto relative z-10">
            {/* 標題 - 加大手機版文字 */}
            <div className="text-center mb-8 sm:mb-12 md:mb-16">
              <div className="inline-flex items-center gap-2 mb-4 sm:mb-5 px-4 sm:px-5 py-3 sm:py-2.5 bg-gradient-to-r from-[#D4AF37]/20 to-[#FFD700]/20 rounded-full border border-[#FFD700]/30">
                <i className="ri-vip-diamond-fill text-[#FFD700] text-2xl sm:text-2xl"></i>
                <span className="text-lg sm:text-base md:text-lg text-[#FFD700] font-medium">圈內認證與真實案例</span>
              </div>
              <h2 className="text-3xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-4">
                💎 圈內推薦與會員見證
              </h2>
              <p className="text-xl sm:text-lg md:text-xl text-gray-400 max-w-2xl mx-auto px-4">
                同等級的玩家都在用，真實數據與案例見證
              </p>
            </div>

            {/* 🔥 新增：輪播式推薦卡片 - 每次顯示 2 個 */}
            <div className="relative mb-8 sm:mb-12">
              {/* 推薦卡片容器 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 md:gap-6">
                {displayedCases.map((caseItem, index) => (
                  <div 
                    key={`${caseItem.name}-${currentCaseIndex}-${index}`}
                    onClick={() => {
                      // 🔥 修正：添加成功案例點擊追蹤
                      if (pixelLoaded) {
                        const contentKey = `case_${caseItem.name}`;
                        if (!trackedViewContent.has(contentKey)) {
                          console.log('🎯 成功案例點擊事件被觸發:', caseItem.name);
                          trackViewContent(
                            `成功案例 - ${caseItem.name}`,
                            '成功案例',
                            {
                              case_name: caseItem.name,
                              action: 'click'
                            }
                          );
                          setTrackedViewContent(prev => new Set(prev).add(contentKey));
                        }
                      }
                    }}
                    className="group bg-gradient-to-br from-[#D4AF37]/10 via-[#FFD700]/5 to-transparent backdrop-blur-sm rounded-xl p-4 sm:p-5 md:p-6 border border-[#D4AF37]/30 hover:border-[#FFD700]/50 transition-all duration-500 hover:shadow-xl hover:shadow-[#FFD700]/20 animate-fade-in cursor-pointer card-flip"
                    style={{ 
                      animationDelay: `${index * 0.1}s`,
                      transformStyle: 'preserve-3d'
                    }}
                  >
                    {/* 引號裝飾 */}
                    <div className="text-4xl sm:text-4xl md:text-4xl text-[#FFD700]/20 leading-none mb-3 sm:mb-3">"</div>
                    
                    {/* 推薦內容 */}
                    <p className="text-base sm:text-base text-gray-300 mb-5 sm:mb-5 leading-relaxed group-hover:text-white transition-colors duration-300">
                      {caseItem.testimonial}
                    </p>
                    
                    {/* 推薦人資訊 */}
                    <div className="flex items-center justify-between pt-5 sm:pt-5 border-t border-[#D4AF37]/20">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-lg sm:text-lg font-bold text-[#FFD700] group-hover:text-[#FFD700] transition-colors duration-300">
                            {caseItem.name}
                          </h4>
                          <span className="text-base sm:text-sm text-gray-400">{caseItem.title}</span>
                        </div>
                        <div className="flex items-center gap-2 text-base sm:text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <i className="ri-vip-crown-fill text-[#FFD700]"></i>
                            {caseItem.level}
                          </span>
                          <span>•</span>
                          <span>{caseItem.duration}</span>
                        </div>
                      </div>
                      <div className="text-2xl sm:text-2xl">{caseItem.badge}</div>
                    </div>
                    
                    {/* 成就標籤 */}
                    <div className="mt-4 sm:mt-4 flex flex-wrap gap-2">
                      {caseItem.achievements.map((achievement, i) => (
                        <span 
                          key={i}
                          className="text-sm px-2 py-1 bg-[#D4AF37]/20 text-[#FFD700] rounded-full border border-[#D4AF37]/30"
                        >
                          {achievement}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* 🔥 新增：輪播指示器 */}
              <div className="flex items-center justify-center gap-2 mt-8">
                {Array.from({ length: Math.ceil(successCases.length / 2) }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentCaseIndex(index * 2)}
                    className={`transition-all duration-300 cursor-pointer ${
                      Math.floor(currentCaseIndex / 2) === index
                        ? 'w-8 h-2 bg-[#FFD700] rounded-full'
                        : 'w-2 h-2 bg-white/30 rounded-full hover:bg-white/50'
                    }`}
                    aria-label={`切換到案例組 ${index + 1}`}
                  ></button>
                ))}
              </div>

              {/* 🔥 新增：左右切換按鈕（電腦版和手機版） */}
              <button
                onClick={() => setCurrentCaseIndex((prev) => (prev - 2 + successCases.length) % successCases.length)}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-16 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer border border-white/20 hover:border-[#FFD700]/50 z-10"
                aria-label="上一組案例"
              >
                <i className="ri-arrow-left-s-line text-2xl text-white"></i>
              </button>
              <button
                onClick={() => setCurrentCaseIndex((prev) => (prev + 2) % successCases.length)}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-16 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer border border-white/20 hover:border-[#FFD700]/50 z-10"
                aria-label="下一組案例"
              >
                <i className="ri-arrow-right-s-line text-2xl text-white"></i>
              </button>
            </div>
          </div>
        </section>

        {/* 8. 遊戲排行榜（展示內容） */}
        <section className="py-10 sm:py-12 md:py-16 lg:py-20 px-4 sm:px-6 md:px-8 lg:px-10 bg-gradient-to-b from-black via-[#0A0A0A] to-black relative overflow-hidden">
          {/* 背景裝飾 */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#FFD700]/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#D4AF37]/10 rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-7xl mx-auto relative z-10">
            {/* 標題 - 優化手機版 */}
            <div className="text-center mb-8 sm:mb-10 md:mb-12 animate-fade-in">
              <div className="inline-flex items-center gap-2 mb-3 sm:mb-4 px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-[#D4AF37]/20 to-[#FFD700]/20 rounded-full border border-[#FFD700]/30">
                <i className="ri-trophy-line text-[#FFD700] text-2xl sm:text-2xl"></i>
                <span className="text-lg sm:text-base text-[#FFD700] font-medium">熱門遊戲</span>
              </div>
              <h2 className="text-3xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-4 px-4">
                🎰 火星人老虎機遊戲排行
              </h2>
              <p className="text-xl sm:text-lg md:text-xl text-gray-400 max-w-3xl mx-auto px-4">
                精選 9 款最受歡迎的老虎機遊戲，高爆分、高回報
              </p>
            </div>

            {/* 🔥 手機版：兩行兩列網格佈局 */}
            <div className="lg:hidden mb-8 animate-fade-in px-4">
              {/* 網格容器 */}
              <div className="grid grid-cols-2 gap-3">
                {/* 1. 戰神賽特2-覺醒之力 */}
                <div className="group relative bg-gradient-to-br from-gray-900/90 to-gray-800/90 rounded-xl overflow-hidden border border-[#FFD700]/30 hover:border-[#FFD700]/60 transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,215,0,0.3)] hover:scale-[1.02]">
                  {/* 遊戲圖片 - 改為正方形 */}
                  <div className="relative w-full aspect-square overflow-hidden">
                    <img 
                      src="https://i.ibb.co/nMSpPzzy/image.gif" 
                      alt="戰神賽特2-覺醒之力"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>

                  {/* 內容區域 */}
                  <div className="p-3">
                    {/* 排名與標籤 */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                        <span className="text-white font-bold text-xs">1</span>
                      </div>
                      <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white text-[9px] px-2 py-0.5 rounded-full font-bold animate-pulse">
                        🔥 最熱門
                      </div>
                    </div>

                    <h3 className="text-sm font-bold text-white mb-1 line-clamp-1">
                      戰神賽特2-覺醒之力
                    </h3>
                    <p className="text-[10px] text-gray-300 mb-2 line-clamp-2">
                      經典埃及神話主題，超高爆分率，免費旋轉獎勵豐富
                    </p>
                    <div className="flex flex-wrap gap-1">
                      <span className="text-[9px] bg-[#FFD700]/20 text-[#FFD700] px-1.5 py-0.5 rounded">高爆分</span>
                      <span className="text-[9px] bg-[#FFD700]/20 text-[#FFD700] px-1.5 py-0.5 rounded">高回報</span>
                      <span className="text-[9px] bg-[#FFD700]/20 text-[#FFD700] px-1.5 py-0.5 rounded">免費旋轉</span>
                    </div>
                  </div>
                </div>

                {/* 2. 奢華 */}
                <div className="group relative bg-gradient-to-br from-gray-900/90 to-gray-800/90 rounded-xl overflow-hidden border border-[#C0C0C0]/30 hover:border-[#C0C0C0]/60 transition-all duration-300 hover:shadow-[0_0_30px_rgba(192,192,192,0.3)] hover:scale-[1.02]">
                  {/* 遊戲圖片 - 改為正方形 */}
                  <div className="relative w-full aspect-square overflow-hidden">
                    <img 
                      src="https://i.ibb.co/PGydrrQY/image.gif" 
                      alt="奢華"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>

                  {/* 內容區域 */}
                  <div className="p-3">
                    {/* 排名與標籤 */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-gray-300 to-gray-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                        <span className="text-white font-bold text-xs">2</span>
                      </div>
                      <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-[9px] px-2 py-0.5 rounded-full font-bold">
                        ✨ 奢華體驗
                      </div>
                    </div>

                    <h3 className="text-sm font-bold text-white mb-1 line-clamp-1">
                      奢華
                    </h3>
                    <p className="text-[10px] text-gray-300 mb-2 line-clamp-2">
                      頂級奢華風格，精緻畫面設計，多重獎勵機制
                    </p>
                    <div className="flex flex-wrap gap-1">
                      <span className="text-[9px] bg-[#C0C0C0]/20 text-[#C0C0C0] px-1.5 py-0.5 rounded">精緻畫面</span>
                      <span className="text-[9px] bg-[#C0C0C0]/20 text-[#C0C0C0] px-1.5 py-0.5 rounded">多重獎勵</span>
                    </div>
                  </div>
                </div>

                {/* 3. 雷神之錘 */}
                <div className="group relative bg-gradient-to-br from-gray-900/90 to-gray-800/90 rounded-xl overflow-hidden border border-[#CD7F32]/30 hover:border-[#CD7F32]/60 transition-all duration-300 hover:shadow-[0_0_30px_rgba(205,127,50,0.3)] hover:scale-[1.02]">
                  {/* 遊戲圖片 - 改為正方形 */}
                  <div className="relative w-full aspect-square overflow-hidden">
                    <img 
                      src="https://i.ibb.co/wrY0qJ9X/image.gif" 
                      alt="雷神之錘"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>

                  {/* 內容區域 */}
                  <div className="p-3">
                    {/* 排名與標籤 */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                        <span className="text-white font-bold text-xs">3</span>
                      </div>
                      <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-[9px] px-2 py-0.5 rounded-full font-bold">
                        ⚡ 高刺激
                      </div>
                    </div>

                    <h3 className="text-sm font-bold text-white mb-1 line-clamp-1">
                      雷神之錘
                    </h3>
                    <p className="text-[10px] text-gray-300 mb-2 line-clamp-2">
                      北歐神話題材，雷電特效震撼，連線爆分機制
                    </p>
                    <div className="flex flex-wrap gap-1">
                      <span className="text-[9px] bg-[#CD7F32]/20 text-[#CD7F32] px-1.5 py-0.5 rounded">震撼特效</span>
                      <span className="text-[9px] bg-[#CD7F32]/20 text-[#CD7F32] px-1.5 py-0.5 rounded">連線爆分</span>
                    </div>
                  </div>
                </div>

                {/* 4. 塔樓衝刺 */}
                <div className="group relative bg-gradient-to-br from-gray-900/90 to-gray-800/90 rounded-xl overflow-hidden border border-gray-700/30 hover:border-gray-600/60 transition-all duration-300 hover:shadow-[0_0_30px_rgba(156,163,175,0.3)] hover:scale-[1.02]">
                  {/* 遊戲圖片 - 改為正方形 */}
                  <div className="relative w-full aspect-square overflow-hidden">
                    <img 
                      src="https://i.ibb.co/LM31MRw/image.png" 
                      alt="塔樓衝刺"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>

                  {/* 內容區域 */}
                  <div className="p-3">
                    {/* 排名與標籤 */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-gray-500 to-gray-700 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                        <span className="text-white font-bold text-xs">4</span>
                      </div>
                      <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-[9px] px-2 py-0.5 rounded-full font-bold">
                        🚀 快節奏
                      </div>
                    </div>

                    <h3 className="text-sm font-bold text-white mb-1 line-clamp-1">
                      塔樓衝刺
                    </h3>
                    <p className="text-[10px] text-gray-300 mb-2 line-clamp-2">
                      創新玩法設計，快節奏遊戲體驗，層層突破挑戰
                    </p>
                    <div className="flex flex-wrap gap-1">
                      <span className="text-[9px] bg-gray-700/30 text-gray-300 px-1.5 py-0.5 rounded">創新玩法</span>
                      <span className="text-[9px] bg-gray-700/30 text-gray-300 px-1.5 py-0.5 rounded">層層突破</span>
                    </div>
                  </div>
                </div>

                {/* 5. 推筒子 */}
                <div className="group relative bg-gradient-to-br from-gray-900/90 to-gray-800/90 rounded-xl overflow-hidden border border-gray-700/30 hover:border-gray-600/60 transition-all duration-300 hover:shadow-[0_0_30px_rgba(156,163,175,0.3)] hover:scale-[1.02]">
                  {/* 遊戲圖片 - 改為正方形 */}
                  <div className="relative w-full aspect-square overflow-hidden">
                    <img 
                      src="https://i.ibb.co/MkD0J72z/image.gif" 
                      alt="推筒子"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>

                  {/* 內容區域 */}
                  <div className="p-3">
                    {/* 排名與標籤 */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-gray-500 to-gray-700 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                        <span className="text-white font-bold text-xs">5</span>
                      </div>
                      <div className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white text-[9px] px-2 py-0.5 rounded-full font-bold">
                        🎯 經典
                      </div>
                    </div>

                    <h3 className="text-sm font-bold text-white mb-1 line-clamp-1">
                      推筒子
                    </h3>
                    <p className="text-[10px] text-gray-300 mb-2 line-clamp-2">
                      傳統中式遊戲改編，簡單易懂，穩定中獎機制
                    </p>
                    <div className="flex flex-wrap gap-1">
                      <span className="text-[9px] bg-gray-700/30 text-gray-300 px-1.5 py-0.5 rounded">經典玩法</span>
                      <span className="text-[9px] bg-gray-700/30 text-gray-300 px-1.5 py-0.5 rounded">穩定中獎</span>
                    </div>
                  </div>
                </div>

                {/* 6. 急速糖果 */}
                <div className="group relative bg-gradient-to-br from-gray-900/90 to-gray-800/90 rounded-xl overflow-hidden border border-gray-700/30 hover:border-gray-600/60 transition-all duration-300 hover:shadow-[0_0_30px_rgba(156,163,175,0.3)] hover:scale-[1.02]">
                  {/* 遊戲圖片 - 改為正方形 */}
                  <div className="relative w-full aspect-square overflow-hidden">
                    <img 
                      src="https://i.ibb.co/zTR5fQ78/image.gif" 
                      alt="急速糖果"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>

                  {/* 內容區域 */}
                  <div className="p-3">
                    {/* 排名與標籤 */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-gray-500 to-gray-700 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                        <span className="text-white font-bold text-xs">6</span>
                      </div>
                      <div className="bg-gradient-to-r from-pink-500 to-rose-500 text-white text-[9px] px-2 py-0.5 rounded-full font-bold">
                        🍬 可愛風
                      </div>
                    </div>

                    <h3 className="text-sm font-bold text-white mb-1 line-clamp-1">
                      急速糖果
                    </h3>
                    <p className="text-[10px] text-gray-300 mb-2 line-clamp-2">
                      繽紛可愛畫風，輕鬆愉快氛圍，連鎖反應獎勵
                    </p>
                    <div className="flex flex-wrap gap-1">
                      <span className="text-[9px] bg-gray-700/30 text-gray-300 px-1.5 py-0.5 rounded">可愛畫風</span>
                      <span className="text-[9px] bg-gray-700/30 text-gray-300 px-1.5 py-0.5 rounded">連鎖反應</span>
                    </div>
                  </div>
                </div>

                {/* 7. 急速天九 */}
                <div className="group relative bg-gradient-to-br from-gray-900/90 to-gray-800/90 rounded-xl overflow-hidden border border-gray-700/30 hover:border-gray-600/60 transition-all duration-300 hover:shadow-[0_0_30px_rgba(156,163,175,0.3)] hover:scale-[1.02]">
                  {/* 遊戲圖片 - 改為正方形 */}
                  <div className="relative w-full aspect-square overflow-hidden">
                    <img 
                      src="https://i.ibb.co/RkRn9jsZ/image.png" 
                      alt="急速天九"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>

                  {/* 內容區域 */}
                  <div className="p-3">
                    {/* 排名與標籤 */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-gray-500 to-gray-700 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                        <span className="text-white font-bold text-xs">7</span>
                      </div>
                      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-[9px] px-2 py-0.5 rounded-full font-bold">
                        ⚡ 快速
                      </div>
                    </div>

                    <h3 className="text-sm font-bold text-white mb-1 line-clamp-1">
                      急速天九
                    </h3>
                    <p className="text-[10px] text-gray-300 mb-2 line-clamp-2">
                      傳統天九牌創新改編，快速開獎，高頻中獎體驗
                    </p>
                    <div className="flex flex-wrap gap-1">
                      <span className="text-[9px] bg-gray-700/30 text-gray-300 px-3 py-1 rounded-full font-medium">快速開獎</span>
                      <span className="text-[9px] bg-gray-700/30 text-gray-300 px-3 py-1 rounded-full font-medium">高頻中獎</span>
                    </div>
                  </div>
                </div>

                {/* 8. 仙境傳說40K */}
                <div className="group relative bg-gradient-to-br from-gray-900/90 to-gray-800/90 rounded-xl overflow-hidden border border-gray-700/30 hover:border-gray-600/60 transition-all duration-300 hover:shadow-[0_0_30px_rgba(156,163,175,0.3)] hover:scale-[1.02]">
                  {/* 遊戲圖片 - 改為正方形 */}
                  <div className="relative w-full aspect-square overflow-hidden">
                    <img 
                      src="https://i.ibb.co/XZ5n1Hkf/image.jpg" 
                      alt="仙境傳說40K"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>

                  {/* 內容區域 */}
                  <div className="p-3">
                    {/* 排名與標籤 */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-gray-500 to-gray-700 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                        <span className="text-white font-bold text-xs">8</span>
                      </div>
                      <div className="bg-gradient-to-r from-violet-500 to-purple-500 text-white text-[9px] px-2 py-0.5 rounded-full font-bold">
                        🎮 奇幻
                      </div>
                    </div>

                    <h3 className="text-sm font-bold text-white mb-1 line-clamp-1">
                      仙境傳說40K
                    </h3>
                    <p className="text-[10px] text-gray-300 mb-2 line-clamp-2">
                      經典 IP 改編，奇幻冒險主題，高獎金池設計
                    </p>
                    <div className="flex flex-wrap gap-1">
                      <span className="text-[9px] bg-gray-700/30 text-gray-300 px-3 py-1 rounded-full font-medium">經典 IP</span>
                      <span className="text-[9px] bg-gray-700/30 text-gray-300 px-3 py-1 rounded-full font-medium">高獎金池</span>
                    </div>
                  </div>
                </div>

                {/* 9. 小雞快跑 - 佔滿兩列 */}
                <div className="group relative bg-gradient-to-br from-gray-900/90 to-gray-800/90 rounded-xl overflow-hidden border border-gray-700/30 hover:border-gray-600/60 transition-all duration-300 hover:shadow-[0_0_30px_rgba(156,163,175,0.3)] hover:scale-[1.02] col-span-2">
                  <div className="flex">
                    {/* 遊戲圖片 - 改為正方形 */}
                    <div className="relative w-32 aspect-square overflow-hidden flex-shrink-0">
                      <img 
                        src="https://i.ibb.co/4w3fFBqW/image.png" 
                        alt="小雞快跑"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>

                    {/* 內容區域 */}
                    <div className="p-3 flex-1">
                      {/* 排名與標籤 */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-gray-500 to-gray-700 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                          <span className="text-white font-bold text-xs">9</span>
                        </div>
                        <div className="bg-gradient-to-r from-yellow-500 to-amber-500 text-white text-[9px] px-2 py-0.5 rounded-full font-bold">
                          😄 趣味
                        </div>
                      </div>

                      <h3 className="text-sm font-bold text-white mb-1 line-clamp-1">
                        小雞快跑
                      </h3>
                      <p className="text-[10px] text-gray-300 mb-2 line-clamp-2">
                        輕鬆趣味風格，簡單操作玩法，適合小額投注
                      </p>
                      <div className="flex flex-wrap gap-1">
                        <span className="text-[9px] bg-gray-700/30 text-gray-300 px-3 py-1 rounded-full font-medium">輕鬆趣味</span>
                        <span className="text-[9px] bg-gray-700/30 text-gray-300 px-3 py-1 rounded-full font-medium">小額投注</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 🔥 優化：電腦版 - 3 列網格佈局，更精緻的設計 */}
            <div className="hidden lg:block mb-8 animate-fade-in">
              <div className="grid grid-cols-3 gap-4">
                {/* 1. 戰神賽特2-覺醒之力 */}
                <div className="group relative bg-gradient-to-br from-gray-900/90 to-gray-800/90 rounded-xl overflow-hidden border border-[#FFD700]/30 hover:border-[#FFD700]/60 transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,215,0,0.3)] hover:scale-[1.02]">
                  {/* 遊戲圖片 - 改為正方形 */}
                  <div className="relative w-full aspect-square overflow-hidden">
                    <img 
                      src="https://i.ibb.co/nMSpPzzy/image.gif" 
                      alt="戰神賽特2-覺醒之力"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>

                  {/* 內容區域 */}
                  <div className="p-4">
                    {/* 排名與標籤 */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                        <span className="text-white font-bold text-lg">1</span>
                      </div>
                      <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs px-3 py-1 rounded-full font-bold animate-pulse shadow-lg">
                        🔥 最熱門
                      </div>
                    </div>

                    <h3 className="text-base font-bold text-white mb-2 line-clamp-1">
                      戰神賽特2-覺醒之力
                    </h3>
                    <p className="text-xs text-gray-300 mb-3 line-clamp-2 leading-relaxed">
                      經典埃及神話主題，超高爆分率，免費旋轉獎勵豐富
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      <span className="text-xs bg-[#FFD700]/20 text-[#FFD700] px-2 py-0.5 rounded-full font-medium">高爆分</span>
                      <span className="text-xs bg-[#FFD700]/20 text-[#FFD700] px-2 py-0.5 rounded-full font-medium">高回報</span>
                      <span className="text-xs bg-[#FFD700]/20 text-[#FFD700] px-2 py-0.5 rounded-full font-medium">免費旋轉</span>
                    </div>
                  </div>
                </div>

                {/* 2. 奢華 */}
                <div className="group relative bg-gradient-to-br from-gray-900/90 to-gray-800/90 rounded-xl overflow-hidden border border-[#C0C0C0]/30 hover:border-[#C0C0C0]/60 transition-all duration-300 hover:shadow-[0_0_30px_rgba(192,192,192,0.3)] hover:scale-[1.02]">
                  <div className="relative w-full aspect-square overflow-hidden">
                    <img 
                      src="https://i.ibb.co/PGydrrQY/image.gif" 
                      alt="奢華"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-gray-300 to-gray-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                        <span className="text-white font-bold text-lg">2</span>
                      </div>
                      <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs px-3 py-1 rounded-full font-bold shadow-lg">
                        ✨ 奢華體驗
                      </div>
                    </div>
                    <h3 className="text-base font-bold text-white mb-2 line-clamp-1">
                      奢華
                    </h3>
                    <p className="text-xs text-gray-300 mb-3 line-clamp-2 leading-relaxed">
                      頂級奢華風格，精緻畫面設計，多重獎勵機制
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      <span className="text-xs bg-[#C0C0C0]/20 text-[#C0C0C0] px-2 py-0.5 rounded-full font-medium">精緻畫面</span>
                      <span className="text-xs bg-[#C0C0C0]/20 text-[#C0C0C0] px-2 py-0.5 rounded-full font-medium">多重獎勵</span>
                    </div>
                  </div>
                </div>

                {/* 3. 雷神之錘 */}
                <div className="group relative bg-gradient-to-br from-gray-900/90 to-gray-800/90 rounded-xl overflow-hidden border border-[#CD7F32]/30 hover:border-[#CD7F32]/60 transition-all duration-300 hover:shadow-[0_0_30px_rgba(205,127,50,0.3)] hover:scale-[1.02]">
                  <div className="relative w-full aspect-square overflow-hidden">
                    <img 
                      src="https://i.ibb.co/wrY0qJ9X/image.gif" 
                      alt="雷神之錘"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                        <span className="text-white font-bold text-lg">3</span>
                      </div>
                      <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs px-3 py-1 rounded-full font-bold shadow-lg">
                        ⚡ 高刺激
                      </div>
                    </div>
                    <h3 className="text-base font-bold text-white mb-2 line-clamp-1">
                      雷神之錘
                    </h3>
                    <p className="text-xs text-gray-300 mb-3 line-clamp-2 leading-relaxed">
                      北歐神話題材，雷電特效震撼，連線爆分機制
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      <span className="text-xs bg-[#CD7F32]/20 text-[#CD7F32] px-2 py-0.5 rounded-full font-medium">震撼特效</span>
                      <span className="text-xs bg-[#CD7F32]/20 text-[#CD7F32] px-2 py-0.5 rounded-full font-medium">連線爆分</span>
                    </div>
                  </div>
                </div>

                {/* 4. 塔樓衝刺 */}
                <div className="group relative bg-gradient-to-br from-gray-900/90 to-gray-800/90 rounded-xl overflow-hidden border border-gray-700/30 hover:border-gray-600/60 transition-all duration-300 hover:shadow-[0_0_30px_rgba(156,163,175,0.3)] hover:scale-[1.02]">
                  <div className="relative w-full aspect-square overflow-hidden">
                    <img 
                      src="https://i.ibb.co/LM31MRw/image.png" 
                      alt="塔樓衝刺"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-gray-500 to-gray-700 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                        <span className="text-white font-bold text-lg">4</span>
                      </div>
                      <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs px-3 py-1 rounded-full font-bold shadow-lg">
                        🚀 快節奏
                      </div>
                    </div>
                    <h3 className="text-base font-bold text-white mb-2 line-clamp-1">
                      塔樓衝刺
                    </h3>
                    <p className="text-xs text-gray-300 mb-3 line-clamp-2 leading-relaxed">
                      創新玩法設計，快節奏遊戲體驗，層層突破挑戰
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      <span className="text-xs bg-gray-700/30 text-gray-300 px-2 py-0.5 rounded-full font-medium">創新玩法</span>
                      <span className="text-xs bg-gray-700/30 text-gray-300 px-2 py-0.5 rounded-full font-medium">層層突破</span>
                    </div>
                  </div>
                </div>

                {/* 5. 推筒子 */}
                <div className="group relative bg-gradient-to-br from-gray-900/90 to-gray-800/90 rounded-xl overflow-hidden border border-gray-700/30 hover:border-gray-600/60 transition-all duration-300 hover:shadow-[0_0_30px_rgba(156,163,175,0.3)] hover:scale-[1.02]">
                  <div className="relative w-full aspect-square overflow-hidden">
                    <img 
                      src="https://i.ibb.co/MkD0J72z/image.gif" 
                      alt="推筒子"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-gray-500 to-gray-700 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                        <span className="text-white font-bold text-lg">5</span>
                      </div>
                      <div className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white text-xs px-3 py-1 rounded-full font-bold shadow-lg">
                        🎯 經典
                      </div>
                    </div>
                    <h3 className="text-base font-bold text-white mb-2 line-clamp-1">
                      推筒子
                    </h3>
                    <p className="text-xs text-gray-300 mb-3 line-clamp-2 leading-relaxed">
                      傳統中式遊戲改編，簡單易懂，穩定中獎機制
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      <span className="text-xs bg-gray-700/30 text-gray-300 px-2 py-0.5 rounded-full font-medium">經典玩法</span>
                      <span className="text-xs bg-gray-700/30 text-gray-300 px-2 py-0.5 rounded-full font-medium">穩定中獎</span>
                    </div>
                  </div>
                </div>

                {/* 6. 急速糖果 */}
                <div className="group relative bg-gradient-to-br from-gray-900/90 to-gray-800/90 rounded-xl overflow-hidden border border-gray-700/30 hover:border-gray-600/60 transition-all duration-300 hover:shadow-[0_0_30px_rgba(156,163,175,0.3)] hover:scale-[1.02]">
                  <div className="relative w-full aspect-square overflow-hidden">
                    <img 
                      src="https://i.ibb.co/zTR5fQ78/image.gif" 
                      alt="急速糖果"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-gray-500 to-gray-700 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                        <span className="text-white font-bold text-lg">6</span>
                      </div>
                      <div className="bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs px-3 py-1 rounded-full font-bold shadow-lg">
                        🍬 可愛風
                      </div>
                    </div>
                    <h3 className="text-base font-bold text-white mb-2 line-clamp-1">
                      急速糖果
                    </h3>
                    <p className="text-xs text-gray-300 mb-3 line-clamp-2 leading-relaxed">
                      繽紛可愛畫風，輕鬆愉快氛圍，連鎖反應獎勵
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      <span className="text-xs bg-gray-700/30 text-gray-300 px-2 py-0.5 rounded-full font-medium">可愛畫風</span>
                      <span className="text-xs bg-gray-700/30 text-gray-300 px-2 py-0.5 rounded-full font-medium">連鎖反應</span>
                    </div>
                  </div>
                </div>

                {/* 7. 急速天九 */}
                <div className="group relative bg-gradient-to-br from-gray-900/90 to-gray-800/90 rounded-xl overflow-hidden border border-gray-700/30 hover:border-gray-600/60 transition-all duration-300 hover:shadow-[0_0_30px_rgba(156,163,175,0.3)] hover:scale-[1.02]">
                  <div className="relative w-full aspect-square overflow-hidden">
                    <img 
                      src="https://i.ibb.co/RkRn9jsZ/image.png" 
                      alt="急速天九"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-gray-500 to-gray-700 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                        <span className="text-white font-bold text-lg">7</span>
                      </div>
                      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs px-3 py-1 rounded-full font-bold shadow-lg">
                        ⚡ 快速
                      </div>
                    </div>
                    <h3 className="text-base font-bold text-white mb-2 line-clamp-1">
                      急速天九
                    </h3>
                    <p className="text-xs text-gray-300 mb-3 line-clamp-2 leading-relaxed">
                      傳統天九牌創新改編，快速開獎，高頻中獎體驗
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      <span className="text-xs bg-gray-700/30 text-gray-300 px-2 py-0.5 rounded-full font-medium">快速開獎</span>
                      <span className="text-xs bg-gray-700/30 text-gray-300 px-2 py-0.5 rounded-full font-medium">高頻中獎</span>
                    </div>
                  </div>
                </div>

                {/* 8. 仙境傳說40K */}
                <div className="group relative bg-gradient-to-br from-gray-900/90 to-gray-800/90 rounded-xl overflow-hidden border border-gray-700/30 hover:border-gray-600/60 transition-all duration-300 hover:shadow-[0_0_30px_rgba(156,163,175,0.3)] hover:scale-[1.02]">
                  <div className="relative w-full aspect-square overflow-hidden">
                    <img 
                      src="https://i.ibb.co/XZ5n1Hkf/image.jpg" 
                      alt="仙境傳說40K"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-gray-500 to-gray-700 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                        <span className="text-white font-bold text-lg">8</span>
                      </div>
                      <div className="bg-gradient-to-r from-violet-500 to-purple-500 text-white text-xs px-3 py-1 rounded-full font-bold shadow-lg">
                        🎮 奇幻
                      </div>
                    </div>
                    <h3 className="text-base font-bold text-white mb-2 line-clamp-1">
                      仙境傳說40K
                    </h3>
                    <p className="text-xs text-gray-300 mb-3 line-clamp-2 leading-relaxed">
                      經典 IP 改編，奇幻冒險主題，高獎金池設計
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      <span className="text-xs bg-gray-700/30 text-gray-300 px-2 py-0.5 rounded-full font-medium">經典 IP</span>
                      <span className="text-xs bg-gray-700/30 text-gray-300 px-2 py-0.5 rounded-full font-medium">高獎金池</span>
                    </div>
                  </div>
                </div>

                {/* 9. 小雞快跑 */}
                <div className="group relative bg-gradient-to-br from-gray-900/90 to-gray-800/90 rounded-xl overflow-hidden border border-gray-700/30 hover:border-gray-600/60 transition-all duration-300 hover:shadow-[0_0_30px_rgba(156,163,175,0.3)] hover:scale-[1.02]">
                  <div className="relative w-full aspect-square overflow-hidden">
                    <img 
                      src="https://i.ibb.co/4w3fFBqW/image.png" 
                      alt="小雞快跑"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-gray-500 to-gray-700 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                        <span className="text-white font-bold text-lg">9</span>
                      </div>
                      <div className="bg-gradient-to-r from-yellow-500 to-amber-500 text-white text-xs px-3 py-1 rounded-full font-bold shadow-lg">
                        😄 趣味
                      </div>
                    </div>
                    <h3 className="text-base font-bold text-white mb-2 line-clamp-1">
                      小雞快跑
                    </h3>
                    <p className="text-xs text-gray-300 mb-3 line-clamp-2 leading-relaxed">
                      輕鬆趣味風格，簡單操作玩法，適合小額投注
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      <span className="text-xs bg-gray-700/30 text-gray-300 px-2 py-0.5 rounded-full font-medium">輕鬆趣味</span>
                      <span className="text-xs bg-gray-700/30 text-gray-300 px-2 py-0.5 rounded-full font-medium">小額投注</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 底部說明 */}
            <div className="mt-8 sm:mt-10 md:mt-12 text-center animate-fade-in">
              <div className="bg-gradient-to-br from-[#FFD700]/10 to-transparent backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-[#FFD700]/30">
                <div className="flex items-start gap-4 max-w-3xl mx-auto">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#FFD700] to-[#D4AF37] rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-information-line text-2xl text-black"></i>
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="text-lg sm:text-xl font-bold text-white mb-3">🎮 更多精彩遊戲等你體驗</h3>
                    <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-4">
                      火星人娛樂城提供超過 <strong className="text-[#FFD700]">500+ 款</strong>老虎機遊戲，包含各大知名遊戲廠商作品。無論你喜歡經典風格還是創新玩法，這裡都能找到適合你的遊戲！
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1.5 bg-white/5 text-gray-300 text-xs font-medium rounded-full border border-white/10">PG Soft</span>
                      <span className="px-3 py-1.5 bg-white/5 text-gray-300 text-xs font-medium rounded-full border border-white/10">Pragmatic Play</span>
                      <span className="px-3 py-1.5 bg-white/5 text-gray-300 text-xs font-medium rounded-full border border-white/10">CQ9</span>
                      <span className="px-3 py-1.5 bg-white/5 text-gray-300 text-xs font-medium rounded-full border border-white/10">JDB</span>
                      <span className="px-3 py-1.5 bg-white/5 text-gray-300 text-xs font-medium rounded-full border border-white/10">更多廠商...</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 9. 市場模式比較（深度說明） */}
        <section className="py-10 sm:py-12 md:py-16 lg:py-20 px-4 sm:px-6 md:px-8 lg:px-10 bg-gradient-to-b from-black via-[#0A0A0A] to-black relative overflow-hidden">
          {/* 背景裝飾 */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-[#FFD700]/10 rounded-full blur-3xl"></div>
            <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#D4AF37]/10 rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-7xl mx-auto relative z-10">
            {/* 標題 */}
            <div className="text-center mb-8 sm:mb-10 md:mb-12 animate-fade-in">
              <div className="inline-flex items-center gap-2 mb-3 sm:mb-4 px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-[#D4AF37]/20 to-[#FFD700]/20 rounded-full border border-[#FFD700]/30">
                <i className="ri-contrast-2-line text-[#FFD700] text-lg sm:text-xl"></i>
                <span className="text-xs sm:text-sm text-[#FFD700] font-medium">市場模式對比</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4">
                全新 VIP 信用制模式
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-gray-400 max-w-3xl mx-auto">
                火星人娛樂城獨家推出，彈性大、可控、節奏舒服
              </p>
            </div>

            {/* 🔥 只保留火星人 VIP 信用制卡片 + 條列式優化 */}
            <div className="max-w-xl mx-auto mb-8 animate-fade-in">
              {/* 火星人 VIP 信用制 */}
              <div className="bg-gradient-to-br from-[#FFD700]/20 via-[#D4AF37]/10 to-transparent backdrop-blur-sm rounded-xl p-4 sm:p-5 border border-[#FFD700] shadow-lg shadow-[#FFD700]/10 relative overflow-hidden">
                {/* 推薦標籤 */}
                <div className="absolute top-2 right-2 z-10">
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-[#FFD700] to-[#D4AF37] text-black text-xs font-bold rounded-full shadow-md">
                    <i className="ri-star-fill text-xs"></i>
                    全新方案
                  </span>
                </div>

                {/* 標題 */}
                <div className="flex items-center gap-2 mb-4 pt-6">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FFD700] to-[#D4AF37] flex items-center justify-center shadow-lg">
                    <i className="ri-vip-crown-fill text-black text-base"></i>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-white">
                    火星人 VIP 信用制
                  </h3>
                </div>

                {/* 核心說明 */}
                <div className="bg-black/30 rounded-lg p-3 mb-4">
                  <div className="flex items-start gap-2 mb-2">
                    <i className="ri-funds-line text-[#FFD700] text-base mt-0.5"></i>
                    <div className="flex-1">
                      <p className="text-xs text-gray-300 mb-1">玩法/額度</p>
                      <p className="text-sm text-white font-semibold">
                        押金 10,000 → 可用額度 20,000
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-time-line text-[#FFD700] text-base mt-0.5"></i>
                    <div className="flex-1">
                      <p className="text-xs text-gray-300 mb-1">結算規則</p>
                      <p className="text-sm text-white font-semibold">
                        贏到「額度 3 倍」可提前結算
                      </p>
                    </div>
                  </div>
                </div>

                {/* 特色重點 */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2">
                    <i className="ri-checkbox-circle-fill text-[#FFD700] text-sm"></i>
                    <span className="text-xs text-gray-300">彈性大：押金 1 萬可玩 2 萬額度</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-checkbox-circle-fill text-[#FFD700] text-sm"></i>
                    <span className="text-xs text-gray-300">可控：照自己節奏玩</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-checkbox-circle-fill text-[#FFD700] text-sm"></i>
                    <span className="text-xs text-gray-300">節奏舒服：不被時間綁住</span>
                  </div>
                </div>

                {/* CTA 按鈕 */}
                <button 
                  onClick={() => {
                    const formSection = document.getElementById('apply');
                    if (formSection) {
                      formSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }}
                  className="w-full bg-gradient-to-r from-[#FFD700] to-[#D4AF37] text-black text-sm font-bold py-2.5 rounded-lg hover:shadow-lg hover:shadow-[#FFD700]/30 transition-all duration-300 hover:scale-[1.02] whitespace-nowrap"
                >
                  立即申請全新 VIP 方案
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* 11. FAQ（解決最後疑問） */}
        <section id="faq" className="py-12 sm:py-12 md:py-16 lg:py-20 px-4 sm:px-6 md:px-8 lg:px-10 bg-gradient-to-b from-black via-[#0A0A0A] to-black relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#FFD700]/10 rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-4xl mx-auto relative z-10">
            <div className="text-center mb-10 sm:mb-10 md:mb-12 animate-fade-in">
              <div className="inline-flex items-center gap-2 mb-4 sm:mb-4 px-4 sm:px-4 py-2.5 sm:py-2.5 bg-gradient-to-r from-[#D4AF37]/20 to-[#FFD700]/20 rounded-full border border-[#FFD700]/30">
                <i className="ri-question-line text-[#FFD700] text-2xl sm:text-2xl"></i>
                <span className="text-base sm:text-base text-[#FFD700] font-medium">常見疑慮</span>
              </div>
              <h2 className="text-3xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-4">
                您最關心的 5 個問題
              </h2>
              <p className="text-lg sm:text-lg md:text-lg text-gray-400 max-w-3xl mx-auto">
                快速了解高端代理的服務細節
              </p>
            </div>

            {/* 🔥 修改：只顯示前 5 個核心問題 */}
            <div className="space-y-4 sm:space-y-4 animate-fade-in">
              {displayedFAQs.map((faq, index) => (
                <div 
                  key={index}
                  className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden hover:border-[#FFD700]/50 transition-all duration-300 card-flip"
                  style={{ transformStyle: 'preserve-3d' }}
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full flex items-center justify-between p-5 sm:p-5 text-left hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-start gap-3 flex-1">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#FFD700] to-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-question-line text-lg text-black"></i>
                      </div>
                      <h3 className="text-base sm:text-base font-bold text-white pr-4">{faq.question}</h3>
                    </div>
                    <i className={`ri-arrow-${openFAQ === index ? 'up' : 'down'}-s-line text-2xl text-[#FFD700] flex-shrink-0 transition-transform duration-300`}></i>
                  </button>
                  {openFAQ === index && (
                    <div className="px-5 sm:px-5 pb-5 sm:pb-6 animate-fade-in">
                      <div className="pl-13">
                        {Array.isArray(faq.answer) ? (
                          <ul className="space-y-2">
                            {faq.answer.map((item, i) => (
                              <li key={i} className="text-sm sm:text-sm text-gray-400 leading-relaxed">
                                {item}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm sm:text-sm text-gray-400 leading-loose whitespace-pre-line">
                            {faq.answer}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* 🔥 新增：查看更多按钮 */}
            {!showAllFAQs && faqs.length > 5 && (
              <div className="text-center mt-8 sm:mt-8 animate-fade-in">
                <button
                  onClick={() => setShowAllFAQs(true)}
                  className="px-8 sm:px-8 py-4 sm:py-4 bg-gradient-to-r from-white/10 to-white/5 border border-white/20 text-white text-base sm:text-base font-bold rounded-full hover:from-white/20 hover:to-white/10 hover:border-[#FFD700]/50 transition-all duration-300 transform hover:scale-105 active:scale-95 cursor-pointer"
                >
                  <i className="ri-arrow-down-s-line mr-2"></i>
                  查看更多問題（還有 {faqs.length - 5} 個）
                </button>
              </div>
            )}

            {/* 🔥 新增：收起按钮 */}
            {showAllFAQs && (
              <div className="text-center mt-8 sm:mt-8 animate-fade-in">
                <button
                  onClick={() => {
                    setShowAllFAQs(false);
                    scrollToSection('faq');
                  }}
                  className="px-8 sm:px-8 py-4 sm:py-4 bg-gradient-to-r from-white/10 to-white/5 border border-white/20 text-white text-base sm:text-base font-bold rounded-full hover:from-white/20 hover:to-white/10 hover:border-[#FFD700]/50 transition-all duration-300 transform hover:scale-105 active:scale-95 cursor-pointer"
                >
                  <i className="ri-arrow-up-s-line mr-2"></i>
                  收起問題
                </button>
              </div>
            )}
          </div>
        </section>

        {/* 🔥 新增：12. 立即申請（第二次 CTA） */}
        <section className="py-16 sm:py-20 md:py-24 lg:py-32 px-4 sm:px-6 md:px-8 bg-gradient-to-b from-black via-[#0A0A0A] to-black relative overflow-hidden">
          {/* 背景裝飾 */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#FFD700]/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#D4AF37]/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          </div>

          <div className="max-w-4xl mx-auto relative z-10">
            <div className="bg-gradient-to-br from-[#FFD700]/20 via-[#D4AF37]/10 to-transparent backdrop-blur-sm rounded-3xl p-8 sm:p-10 md:p-12 border-2 border-[#FFD700]/50 shadow-2xl shadow-[#FFD700]/30 animate-fade-in">
              {/* 標題區 */}
              <div className="text-center mb-8 sm:mb-10">
                <div className="inline-flex items-center gap-2 mb-4 px-5 py-2.5 bg-gradient-to-r from-[#FFD700] to-[#D4AF37] rounded-full shadow-lg">
                  <i className="ri-vip-crown-fill text-2xl text-black animate-pulse"></i>
                  <span className="text-base font-bold text-black">最後機會</span>
                </div>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
                  還在猶豫嗎？
                </h2>
                <p className="text-lg sm:text-xl text-gray-300 mb-6">
                  本月僅剩 <span className="text-[#FFD700] font-bold text-2xl">{remainingSlots}</span> 個名額
                </p>
                <div className="inline-flex items-center gap-3 px-6 py-3 bg-red-500/20 rounded-full border border-red-500/50">
                  <i className="ri-alarm-warning-line text-red-400 text-2xl animate-pulse"></i>
                  <div className="text-left">
                    <div className="text-sm text-red-400 font-medium">限時優惠倒數</div>
                    <div className="text-xl font-bold text-white">
                      {countdown.hours} 小時 {countdown.minutes} 分鐘
                    </div>
                  </div>
                </div>
              </div>

              {/* 特色重點 */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-10">
                <div className="bg-black/30 backdrop-blur-sm rounded-xl p-5 border border-[#FFD700]/20 text-center">
                  <div className="w-14 h-14 mx-auto mb-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                    <i className="ri-time-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-base font-bold text-white mb-2">30 分鐘到帳</h3>
                  <p className="text-sm text-gray-400">快速出金保證</p>
                </div>
                <div className="bg-black/30 backdrop-blur-sm rounded-xl p-5 border border-[#FFD700]/20 text-center">
                  <div className="w-14 h-14 mx-auto mb-3 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center">
                    <i className="ri-shield-check-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-base font-bold text-white mb-2">看完即刪</h3>
                  <p className="text-sm text-gray-400">隱私保護第一</p>
                </div>
                <div className="bg-black/30 backdrop-blur-sm rounded-xl p-5 border border-[#FFD700]/20 text-center">
                  <div className="w-14 h-14 mx-auto mb-3 bg-gradient-to-br from-[#FFD700] to-[#D4AF37] rounded-full flex items-center justify-center">
                    <i className="ri-customer-service-2-line text-3xl text-black"></i>
                  </div>
                  <h3 className="text-base font-bold text-white mb-2">24H 客服</h3>
                  <p className="text-sm text-gray-400">專屬管家服務</p>
                </div>
              </div>

              {/* CTA 按鈕組 */}
              <div className="space-y-4">
                <button
                  onClick={() => scrollToSection('apply')}
                  className="w-full min-h-[64px] px-8 py-4 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-black text-xl font-bold rounded-xl hover:from-[#FFD700] hover:to-[#D4AF37] transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-2xl shadow-[#FFD700]/50 whitespace-nowrap"
                >
                  <span className="flex items-center justify-center gap-3">
                    <i className="ri-vip-crown-fill text-2xl"></i>
                    立即申請成為 VIP 會員
                  </span>
                </button>
                <a
                  href="https://lin.ee/EkFWw1qE"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    // 🔥 修正：添加 LINE 客服點擊追蹤
                    if (pixelLoaded) {
                      const contentKey = 'line_click_final_cta';
                      if (!trackedViewContent.has(contentKey)) {
                        console.log('🎯 LINE 客服點擊事件被觸發（最終 CTA）');
                        trackViewContent(
                          'LINE 客服 - 最終 CTA',
                          '聯絡方式',
                          {
                            contact_method: 'line',
                            location: 'final_cta',
                            action: 'click'
                          }
                        );
                        setTrackedViewContent(prev => new Set(prev).add(contentKey));
                      }
                    }
                  }}
                  className="w-full min-h-[56px] px-8 py-3 bg-white/10 border-2 border-white/20 text-white text-lg font-bold rounded-xl hover:bg-white/20 hover:border-white/30 transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-line-fill text-2xl text-[#06C755]"></i>
                  先用 LINE 諮詢
                </a>
              </div>

              {/* 底部提示 */}
              <div className="mt-8 pt-6 border-t border-white/10 text-center">
                <p className="text-sm text-gray-400 mb-3">
                  <i className="ri-information-line text-[#FFD700] mr-1"></i>
                  已有 <span className="text-[#FFD700] font-bold">{realApplicationData.monthlyTotal}</span> 人申請本月名額
                </p>
                <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                  <i className="ri-shield-check-line text-green-400"></i>
                  <span>資料嚴格保密</span>
                  <span>•</span>
                  <i className="ri-time-line text-blue-400"></i>
                  <span>24 小時內回覆</span>
                  <span>•</span>
                  <i className="ri-customer-service-2-line text-[#FFD700]"></i>
                  <span>專屬客服</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* 成功弹窗 - 修改版 - 🔥 提高 z-index */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-fadeIn overflow-y-auto">
          <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md rounded-2xl p-6 sm:p-8 max-w-md w-full border border-[#D4AF37]/50 shadow-2xl shadow-[#D4AF37]/20 animate-scaleIn my-8">
            <div className="text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 bg-gradient-to-br from-[#D4AF37] to-[#FFD700] rounded-full flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-4xl sm:text-5xl text-black"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#FFD700] mb-3 sm:mb-4">申請提交成功！</h3>
              
              {/* 新增：審核編號 */}
              <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-gradient-to-r from-[#D4AF37]/20 to-[#FFD700]/10 rounded-xl border border-[#D4AF37]/30">
                <div className="text-xs sm:text-sm text-gray-400 mb-2">您的審核編號</div>
                <div className="text-xl sm:text-2xl font-bold text-[#FFD700] tracking-wider mb-2">{applicationId}</div>
                <div className="text-[10px] sm:text-xs text-gray-400">請保存此編號，方便後續查詢</div>
              </div>

              <p className="text-sm sm:text-base text-gray-300 mb-4 sm:mb-6 leading-relaxed">
                感謝您的申請！我們的專屬客服會在 <strong className="text-[#FFD700]">24 小時內</strong>透過電話與您聯繫。<br />
                請保持電話暢通。
              </p>
              
              {/* 預計聯繫時間 */}
              <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-white/5 rounded-xl border border-white/10">
                <div className="flex items-center justify-center gap-2 text-xs sm:text-sm text-gray-300 mb-2">
                  <i className="ri-time-line text-[#FFD700]"></i>
                  <span>預計聯繫時間</span>
                </div>
                <div className="text-base sm:text-lg font-bold text-white">
                  {new Date(Date.now() + 24 * 60 * 60 * 1000).toLocaleDateString('zh-TW', { 
                    month: 'long', 
                    day: 'numeric',
                    weekday: 'short'
                  })} 下午 2-5 點
                </div>
              </div>

              {/* 後續聯繫方式 */}
              <div className="bg-white/5 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 text-left">
                <p className="text-xs sm:text-sm text-gray-300 mb-2 sm:mb-3">如有任何問題，歡迎聯繫我們：</p>
                <div className="space-y-2 text-xs sm:text-sm">
                  <div className="flex items-center gap-2 text-gray-300">
                    <i className="ri-line-fill text-green-400"></i>
                    <span>LINE ID：<strong className="text-[#FFD700]">@marsVIP</strong></span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <i className="ri-time-fill text-[#FFD700]"></i>
                    <span>服務時間：<strong className="text-[#FFD700]">24 小時全年無休</strong></span>
                  </div>
                </div>
              </div>

              <p className="text-[10px] sm:text-xs text-gray-400 mb-4 sm:mb-6">💡 如果 24 小時內未收到火星人聯繫，請主動透過 LINE 聯繫我們</p>

              {/* 新增：雙按鈕 */}
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://lin.ee/EkFWw1qE"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 py-3 sm:py-4 bg-gradient-to-r from-[#06C755] to-[#00B900] text-white text-base sm:text-lg font-bold rounded-xl hover:from-[#00B900] hover:to-[#06C755] transition-all duration-300 whitespace-nowrap text-center flex items-center justify-center gap-2"
                >
                  <i className="ri-line-fill"></i>
                  加 LINE 客服
                </a>
                <button
                  onClick={() => setShowSuccessModal(false)}
                  className="flex-1 py-3 sm:py-4 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-black text-base sm:text-lg font-bold rounded-xl hover:from-[#FFD700] hover:to-[#D4AF37] transition-all duration-300 whitespace-nowrap"
                >
                  確定
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 失敗彈窗 - 🔥 提高 z-index */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md rounded-2xl p-8 max-w-md w-full border border-red-500/50 shadow-2xl shadow-red-500/20 animate-scaleIn">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center">
                <i className="ri-close-circle-line text-5xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-red-400 mb-4">提交失敗</h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                {errorMessage || '提交失敗，請稍後再試'}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowErrorModal(false)}
                  className="flex-1 py-4 bg-white/5 border border-white/10 text-white text-lg font-bold rounded-xl hover:bg-white/10 transition-all duration-300 whitespace-nowrap"
                >
                  關閉
                </button>
                <a
                  href="https://lin.ee/EkFWw1qE"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 py-4 bg-gradient-to-r from-[#06C755] to-[#00B900] text-white text-lg font-bold rounded-xl hover:from-[#00B900] hover:to-[#06C755] transition-all duration-300 whitespace-nowrap text-center"
                >
                  聯絡客服
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 🎰 抽獎彈窗 - 改為抽紅包 */}
      {showLuckyDraw && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm animate-fade-in p-2 sm:p-4 overflow-hidden">
          <div className="relative bg-gradient-to-br from-red-900/95 via-red-800/95 to-red-900/95 rounded-xl sm:rounded-2xl p-3 sm:p-4 max-w-xl w-full mx-2 sm:mx-4 border-2 border-red-500/50 shadow-2xl animate-scale-in max-h-[96vh] overflow-y-auto">
            
            {/* 關閉按鈕 - 只在抽完後顯示 */}
            {hasDrawn && (
              <button
                onClick={resetLuckyDraw}
                className="absolute top-2 right-2 sm:top-3 sm:right-3 w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-white/60 hover:text-white transition-colors bg-black/30 rounded-full hover:bg-black/50 z-10"
              >
                <i className="ri-close-line text-lg sm:text-xl"></i>
              </button>
            )}

            {/* 標題區域 */}
            <div className="text-center mb-3 sm:mb-4">
              <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full mb-2 sm:mb-3 shadow-lg">
                <i className="ri-gift-fill text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-1 flex items-center justify-center gap-2">
                <span className="text-lg sm:text-xl">🧧</span>
                <span>抽取現金紅包池</span>
              </h3>
              <p className="text-xs sm:text-sm text-yellow-300/90">
                {hasDrawn ? '恭喜您抽中紅包池！' : '點擊任意紅包抽取紅包池金額！'}
              </p>
            </div>

            {/* 紅包網格 - 3x4 排列 */}
            <div className="grid grid-cols-4 gap-2 sm:gap-3 mb-3 sm:mb-4">
              {redEnvelopes.map((envelope, index) => (
                <button
                  key={index}
                  onClick={() => !hasDrawn && handleDrawEnvelope(index)}
                  disabled={hasDrawn}
                  className={`relative aspect-[3/4] rounded-lg sm:rounded-xl transition-all duration-300 ${
                    envelope.isOpened
                      ? 'bg-gradient-to-br from-yellow-400 to-orange-500 scale-105 shadow-xl'
                      : 'bg-gradient-to-br from-red-600 to-red-700 hover:scale-105 hover:shadow-lg'
                  } ${!hasDrawn ? 'cursor-pointer' : 'cursor-default'}`}
                >
                  {/* 紅包內容 */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center p-1 sm:p-2">
                    {envelope.isOpened ? (
                      <>
                        <div className="text-2xl sm:text-4xl font-bold text-white mb-0.5 sm:mb-1">
                          {envelope.amount.toLocaleString()}
                        </div>
                        <div className="text-[10px] sm:text-xs text-white/90 font-medium">
                          點數
                        </div>
                      </>
                    ) : (
                      <>
                        <i className="ri-gift-fill text-2xl sm:text-3xl text-yellow-400/80 mb-0.5 sm:mb-1"></i>
                        <div className="text-[10px] sm:text-xs text-yellow-300/70 font-medium">
                          試玩金
                        </div>
                      </>
                    )}
                  </div>

                  {/* 開啟動畫效果 */}
                  {envelope.isOpened && (
                    <div className="absolute inset-0 rounded-lg sm:rounded-xl overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-t from-yellow-500/20 to-transparent animate-pulse"></div>
                    </div>
                  )}
                </button>
              ))}
            </div>

            {/* 中獎結果顯示 */}
            {hasDrawn && drawnAmount && (
              <div className="bg-gradient-to-br from-yellow-600/30 to-orange-600/30 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4 mb-3 sm:mb-4 border border-yellow-500/30 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="text-xl sm:text-2xl">🎉🎊</span>
                  <p className="text-sm sm:text-base text-yellow-300 font-medium">
                    恭喜你！紅包池獲得
                  </p>
                </div>
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">
                  {drawnAmount.toLocaleString()}
                </div>
                <div className="text-sm sm:text-base text-yellow-300/90">
                  元紅包
                </div>
              </div>
            )}

            {/* 說明文字 */}
            {hasDrawn && (
              <div className="space-y-2 mb-3 sm:mb-4">
                <div className="bg-red-950/40 backdrop-blur-sm rounded-lg p-2 sm:p-3 border border-red-500/20">
                  <div className="flex items-start gap-2 mb-1.5 sm:mb-2">
                    <i className="ri-customer-service-2-fill text-yellow-400 text-base sm:text-lg mt-0.5 flex-shrink-0"></i>
                    <p className="text-xs sm:text-sm text-yellow-300/90 font-medium">
                      請等待客服與您溝通領取
                    </p>
                  </div>
                  <p className="text-[10px] sm:text-xs text-white/70 leading-relaxed pl-6">
                    客服將主動與您聯繫，說明紅包池使用方式與領取流程
                  </p>
                </div>

                <div className="space-y-1 text-[10px] sm:text-xs text-white/60 px-2">
                  <p className="flex items-start gap-1.5">
                    <i className="ri-checkbox-circle-fill text-yellow-400/60 mt-0.5 flex-shrink-0"></i>
                    <span>紅包池金額已為您保留，等待客服確認</span>
                  </p>
                  <p className="flex items-start gap-1.5">
                    <i className="ri-information-fill text-yellow-400/60 mt-0.5 flex-shrink-0"></i>
                    <span>紅包池是「之後能多拿的現金上限」</span>
                  </p>
                  <p className="flex items-start gap-1.5">
                    <i className="ri-vip-crown-fill text-yellow-400/60 mt-0.5 flex-shrink-0"></i>
                    <span>每次贏錢結帳時，贏多少就能從紅包池再多領同樣金額</span>
                  </p>
                </div>
              </div>
            )}

            {/* 按鈕區域 */}
            {hasDrawn && (
              <div className="flex flex-col gap-2">
                <a
                  href="https://line.me/ti/p/~@888tgbet"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-bold transition-all duration-300 hover:scale-105 shadow-lg flex items-center justify-center gap-2 text-sm sm:text-base whitespace-nowrap"
                >
                  <i className="ri-customer-service-2-fill text-lg sm:text-xl"></i>
                  <span>聯繫客服了解詳情</span>
                </a>
                <button
                  onClick={resetLuckyDraw}
                  className="w-full bg-white/10 hover:bg-white/20 text-white py-2 sm:py-2.5 rounded-lg sm:rounded-xl font-medium transition-all duration-300 text-xs sm:text-sm whitespace-nowrap"
                >
                  我知道了
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 🎊 慶祝動畫 */}
    </div>
  );
}
